import "../styles.css"
import "./admin.css"
import van from "vanjs-core"
import * as vanX from "vanjs-ext"
import { api_get, api_post, api_patch, api_delete, api_upload } from "../lib/api.client"
import { session, session_org_feature, session_org_action } from "../lib/session.client"
import { EMBED_TOOLS, EMBED_TOOL_LABELS, type EmbedTool } from "../lib/entitlements"
import { ORDIN_904_SPATIAL } from "../lib/ordin904"
import { validate_basemaps, type Basemap } from "../lib/basemaps"
import {
	validate_saved_filters,
	FILTER_OPS,
	FILTER_OP_SYMBOLS,
	type FilterOp,
	type FilterValue,
	type SavedFilter,
} from "../lib/filters"
import { validate_attribute_schema } from "../lib/attribute_schema"
import {
	validate_layer_access,
	minimal_layer_access,
	parse_layer_access,
	VIEWER_LEVELS,
	EDITOR_LEVELS,
	VIEWER_LEVEL_LABELS,
	EDITOR_LEVEL_LABELS,
	type ViewerLevel,
	type EditorLevel,
} from "../lib/layer_access"
import { INDICATOR_LABELS, type CodeChange, type KeyedDiff } from "../lib/rlu_diff"
import type { RluHistory, RluHistoryEntry, RluVersionDetail } from "../rlu_history"
import { author_label } from "../lib/mcp_agent"
import { validation_error, type Validation } from "../lib/validation"
import {
	parse_avize,
	AVIZE_ALL,
	EXTRAVILAN_ECONOMIC_KEY,
	EXTRAVILAN_TEHNIC_KEY,
	EXTRAVILAN_ECONOMIC_DEFAULT,
	EXTRAVILAN_TEHNIC_DEFAULT,
} from "../lib/rlu"
import { judet_name } from "../lib/emitent"
import { slug_key, slugify, VIRTUAL_LAYER_SLUGS } from "../lib/slug"
import { format_area } from "../lib/geodesy"
import {
	NIL_TOPOLOGY,
	TOPOLOGY_LABELS_RO,
	TOPOLOGY_MAX_FINDINGS,
	type TopologyFinding,
	type TopologyReport,
} from "../lib/topology"

const t = van.tags

// Rolurile de org care pot scrie (crea/importa hărți): owner, admin, editor.
const WRITE_ROLES = new Set(["owner", "admin", "editor"])

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface MapSummary {
	id: string
	slug: string
	name: string
	description?: string
	org_name?: string // numele primăriei (server: list_maps) — afișat read-only celor care nu o administrează
}

interface LayerSummary {
	id: string
	slug: string
	name: string
	geometry_type: string
	feature_count?: number
	source_srid?: number
	attribute_schema?: unknown
	style?: unknown
	style3d?: Record<string, unknown> // config extrudare 3D (F10-T1) — normalizat de server
	metadata?: unknown
	description?: string
	role?: string // rol urbanism: 'utr' | 'derogare' | "" (F5 — regim on-click + REQ-URB-008)
	ordin904_class?: string // maparea pe clasa Ordin 904/2023 (F8); "" = nemapat
	allowed_templates?: string[] // șabloane permise pe features-ul stratului (F13)
	consultare?: boolean // consultare publică PUG (F6-T6): banner + observații în portal
	consultare_pana_la?: string // YYYY-MM-DD; "" = fără termen
	icon_url?: string // iconița stratului punctual (decizia 1.4); "" = simbolul implicit
	saved_filters?: SavedFilter[] // filtre salvate ca straturi derivate (decizia 2.2)
	visible_default?: boolean // 18.09: stratul pornește aprins pe hartă (absent = da)
	color_index?: number // 18.09: poziția istorică din care se alege culoarea (nu cea afișată)
}

interface MapDetail extends MapSummary {
	org_id?: string | null
	config?: { description?: string; name?: string }
	metadata?: unknown
	attributes?: Record<string, unknown> // EAV-ul hărții (stema_url pt. branding public etc.)
	layers: LayerSummary[]
	extent?: [number, number, number, number] | null // bbox 4326 (get_map) — bbox_lock la embed-uri
	basemaps?: Basemap[] // fundaluri configurabile (decizia 1.3), normalizate de server
}

interface ImportLayerSummary {
	slug: string
	features: number
	added?: number
	updated?: number
	unchanged?: number
	deleted?: number
}
interface ImportSummary {
	map?: { id: string; slug: string }
	layers?: ImportLayerSummary[]
	skipped?: { layer: string; reason: string }[]
}

// ─────────────────────────────────────────────
// Global reactive state
// ─────────────────────────────────────────────

const store = vanX.reactive({
	maps: [] as MapSummary[],
	maps_loading: false,
	maps_error: "",

	selected_map: null as string | null,
	map_detail: null as MapDetail | null,
	map_detail_loading: false,
	map_detail_error: "",

	active_tab: "layers" as "layers" | "new_layer" | "import" | "regim_utr",

	edit_layer_slug: null as string | null,

	import_loading: false,
	import_result: null as ImportSummary | null,
	import_is_preview: false, // ultimul rezultat e o PREVIZUALIZARE (dry-run, nimic scris)
})

// ─────────────────────────────────────────────
// Toast system
// ─────────────────────────────────────────────

function show_toast(message: string, kind: "ok" | "err" | "info" = "info", duration = 4000) {
	const area = document.getElementById("toast-area")
	if (!area) {
		return
	}

	const kind_cls = {
		ok: "bg-emerald-50 dark:bg-emerald-950 border-l-4 border-emerald-500 text-emerald-800 dark:text-emerald-300",
		err: "bg-red-50 dark:bg-red-950 border-l-4 border-red-500 text-red-800",
		info: "bg-slate-50 dark:bg-slate-950 border-l-4 border-slate-400 text-slate-700 dark:text-slate-200",
	}[kind]

	const el = t.div(
		{
			class: `toast-slide flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium
        max-w-sm pointer-events-auto ${kind_cls}`,
		},
		message,
	)
	van.add(area, el)
	setTimeout(() => el.remove(), duration)
}

// ─────────────────────────────────────────────
// Data loading
// ─────────────────────────────────────────────

async function load_maps() {
	store.maps_loading = true
	store.maps_error = ""
	const result = await api_get<MapSummary[]>("/api/maps")
	if (result.ok) {
		store.maps = result.data
	} else {
		store.maps_error = result.err?.message ?? ""
		show_toast(`Eroare la încărcarea hărților: ${result.err?.message ?? ""}`, "err")
	}
	store.maps_loading = false
}

async function load_map_detail(map_slug: string) {
	store.map_detail_loading = true
	store.map_detail_error = ""
	store.map_detail = null
	const result = await api_get<MapDetail>(`/api/maps/${map_slug}`)
	if (result.ok) {
		store.map_detail = result.data
	} else {
		store.map_detail_error = result.err?.message ?? ""
		show_toast(`Eroare la încărcarea hărții: ${result.err?.message ?? ""}`, "err")
	}
	store.map_detail_loading = false
}

async function create_map(slug: string, name: string, description: string, org_id?: string) {
	const body: Record<string, string> = { slug, name }
	if (description) {
		body.description = description
	}
	if (org_id) {
		body.org = org_id
	}
	const result = await api_post<MapSummary>("/api/maps", body)
	if (result.ok) {
		show_toast(`Hartă "${name}" creată cu succes`, "ok")
		await load_maps()
		notify_maps_changed()
		return true
	} else {
		show_toast(`Eroare: ${result.err?.message ?? ""}`, "err")
		return false
	}
}

async function create_layer(
	map_slug: string,
	payload: {
		slug: string
		name: string
		geometry_type: string
		source_srid: number
		attribute_schema?: unknown
		style?: unknown
		description?: string
	},
) {
	const result = await api_post<LayerSummary>(`/api/maps/${map_slug}/layers`, payload)
	if (result.ok) {
		show_toast(`Strat "${payload.name}" creat cu succes`, "ok")
		await load_map_detail(map_slug)
		return true
	} else {
		show_toast(`Eroare: ${result.err?.message ?? ""}`, "err")
		return false
	}
}

async function patch_layer(map_slug: string, layer_slug: string, payload: Record<string, unknown>) {
	const result = await api_patch<LayerSummary>(
		`/api/maps/${map_slug}/layers/${layer_slug}`,
		payload,
	)
	if (result.ok) {
		show_toast("Strat actualizat", "ok")
		await load_map_detail(map_slug)
		return true
	} else {
		show_toast(`Eroare: ${result.err?.message ?? ""}`, "err")
		return false
	}
}

/**
 * ORDINEA DE DESENARE a straturilor (18.09) — mută un strat cu o poziție în lista hărții.
 *
 * `dir = +1` = „adu în față" (mai sus pe hartă = mai JOS în listă: panoul desenează de la primul
 * rând în sus, deci ultimul rând e stratul de deasupra). Ordinea trăiește ca `attributes.layer_order`
 * pe HARTĂ — o singură scriere per mutare, fără versiune nouă de strat, deci fără invalidare de cache.
 *
 * ⚠️ La succes se rescrie lista DIN STORE, nu se cheamă `load_map_detail`: acela pune
 * `map_detail = null`, deci tabelul trece prin spinner și butonul dispare de sub deget. Tabelul se
 * reconstruiește ORICUM (rândurile se mută), deci un panou de editare deschis rămâne deschis, dar se
 * reface din starea de pe server — ce era tastat și nesalvat în el se pierde. Cine are ceva nesalvat
 * salvează întâi; asta e scris și în scenariile de testare.
 */
async function move_layer_in_order(
	map_slug: string,
	layer_slug: string,
	dir: 1 | -1,
): Promise<void> {
	const detail = store.map_detail
	if (!detail) {
		return
	}
	const slugs = detail.layers.map((l) => l.slug)
	const i = slugs.indexOf(layer_slug)
	const j = i + dir
	if (i < 0 || j < 0 || j >= slugs.length) {
		return
	}
	const next = [...slugs]
	next[i] = next[j]
	next[j] = layer_slug
	const r = await api_patch(`/api/maps/${encodeURIComponent(map_slug)}`, {
		attributes: { layer_order: next },
	})
	if (!r.ok) {
		show_toast(`Eroare: ${r.err?.message ?? ""}`, "err", 7000)
		return
	}
	const target = store.map_detail
	if (target) {
		// ⚠️ COPII plate (JSON), nu referințele din listă: `vanX.replace` copiază câmp cu câmp ÎN
		// obiectele existente, pe poziții — cu referințe, rândul 0 primea câmpurile rândului 2, iar
		// rândul 1 le lua apoi de la „rândul 0", deja suprascris: toată lista devenea ultimul strat
		// (probat în scratch înainte de a scrie asta). LayerSummary e JSON de la API, deci copia e
		// fără pierderi.
		vanX.replace(target.layers, (items) => {
			const by_slug = new Map(
				items.map((l) => [l.slug, JSON.parse(JSON.stringify(l)) as LayerSummary]),
			)
			return next.map((sl) => by_slug.get(sl)).filter((l): l is LayerSummary => l !== undefined)
		})
		target.attributes = { ...target.attributes, layer_order: next }
	}
}

/** Șterge ordinea configurată — straturile revin la ordinea importului. */
async function reset_layer_order(map_slug: string): Promise<void> {
	const r = await api_patch(`/api/maps/${encodeURIComponent(map_slug)}`, {
		attributes: { layer_order: [] },
	})
	if (!r.ok) {
		show_toast(`Eroare: ${r.err?.message ?? ""}`, "err", 7000)
		return
	}
	show_toast("Ordinea a revenit la cea a importului", "ok")
	await load_map_detail(map_slug)
}

async function do_import(
	file: File,
	target_map?: string,
	org_id?: string,
	natural_key?: string,
	match_fid?: boolean,
	dry_run?: boolean,
	merge_schema?: boolean,
	source_srid?: string,
	new_map?: { name: string }, // „Hartă nouă": numele afișat + doar creare (ținta existentă → 409)
) {
	store.import_loading = true
	store.import_result = null
	const fd = new FormData()
	fd.append("file", file)
	if (target_map) {
		fd.append("map", target_map)
	}
	if (new_map) {
		fd.append("name", new_map.name)
		fd.append("create_only", "true")
	}
	if (org_id) {
		fd.append("org", org_id)
	}
	// Sistemul de coordonate al fișierului (3.2): opțional pe GPKG/SHP/GML/KML (suprascrie detecția),
	// OBLIGATORIU pe CSV/XLSX cu coloane x/y sau WKT — serverul sare stratul cu motiv altfel.
	if (source_srid) {
		fd.append("source_srid", source_srid)
	}
	if (match_fid) {
		fd.append("match_fid", "true")
	} else if (natural_key) {
		fd.append("natural_key", natural_key)
	}
	if (dry_run) {
		fd.append("dry_run", "true")
	}
	if (merge_schema) {
		fd.append("merge_schema", "true")
	}
	// api_upload întoarce network_error ca ApiErr — nu mai e nevoie de try/catch local
	const json = await api_upload<ImportSummary>("/api/import", fd)
	if (json.ok) {
		store.import_result = json.data
		store.import_is_preview = dry_run === true
		const layers = json.data.layers ?? []
		const skipped = json.data.skipped ?? []
		if (layers.length === 0 && skipped.length > 0) {
			// Layer respins (ex. cheie ne-unică) — la fel și la preview.
			show_toast(
				`${dry_run ? "Previzualizare oprită" : "Import oprit"}: ${skipped[0].reason}`,
				"err",
			)
		} else if (dry_run) {
			show_toast("Previzualizare — nimic nu s-a scris. Verifică planul, apoi Importă.", "info")
		} else if (skipped.length > 0) {
			show_toast(`Import parțial: ${skipped.length} layer(e) sărit(e) — vezi detaliile jos`, "err")
		} else {
			show_toast("Import realizat cu succes", "ok")
		}
		// Preview-ul nu schimbă nimic → nu reîncărcăm lista de hărți.
		if (!dry_run) {
			await load_maps()
			notify_maps_changed() // reîmprospătează selectorul de workspace din header
		}
	} else {
		show_toast(`Eroare import: ${json.err?.message ?? ""}`, "err")
	}
	store.import_loading = false
}

/** Semnal global „lista de hărți s-a schimbat" — header-ul își reîncarcă selectorul. */
function notify_maps_changed() {
	window.dispatchEvent(new Event("maps:changed"))
}

/** Org-urile în care utilizatorul poate crea/importa (editor+); super le vede pe toate. */
async function writable_orgs(): Promise<Array<{ id: string; name: string }>> {
	if (session.val.user?.platform_role === "super") {
		const r = await api_get<Array<{ id: string; name: string }>>("/api/admin/orgs")
		return r.ok ? r.data.map((o) => ({ id: o.id, name: o.name })) : []
	}
	return session.val.memberships
		.filter((m) => WRITE_ROLES.has(m.role))
		.map((m) => ({ id: m.org_id, name: m.org_name }))
}

/** Org-urile pe care utilizatorul le poate administra (owner/admin); super le vede pe toate. */
async function manageable_orgs(): Promise<Array<{ id: string; name: string }>> {
	if (session.val.user?.platform_role === "super") {
		const r = await api_get<Array<{ id: string; name: string }>>("/api/admin/orgs")
		return r.ok ? r.data.map((o) => ({ id: o.id, name: o.name })) : []
	}
	return session.val.memberships
		.filter((m) => m.role === "owner" || m.role === "admin")
		.map((m) => ({ id: m.org_id, name: m.org_name }))
}

// ─────────────────────────────────────────────
// Export în formate multiple (3.2 / 3.3 / 3.5 — plan/contracts/export_formats.md)
// ─────────────────────────────────────────────

const EXPORT_FORMATS_UI: ReadonlyArray<{ value: string; label: string; personal?: boolean }> = [
	{ value: "gpkg", label: "GeoPackage (.gpkg)" },
	{ value: "shp", label: "Shapefile (arhivă .zip)" },
	{ value: "csv", label: "CSV (coloana WKT + atribute)" },
	{ value: "xlsx", label: "Excel (.xlsx, o foaie per strat)" },
	{ value: "gml", label: "GML 3" },
	{ value: "kml", label: "KML (Google Earth, WGS84)" },
	// Doar personalul primăriei (cost de generare) — serverul refuză oricum (401/403).
	{ value: "mbtiles", label: "MBTiles (tile-uri vectoriale, zoom 8–15)", personal: true },
]
const EXPORT_SRIDS: ReadonlyArray<[string, string]> = [
	["3844", "Stereo 70 (EPSG:3844)"],
	["4326", "WGS84 lng/lat (EPSG:4326)"],
	["3857", "Web Mercator (EPSG:3857)"],
	["custom", "alt cod EPSG…"],
]
// Preferințele de export sunt PARTAJATE între bara hărții și butoanele per strat (același format,
// același sistem de coordonate) — un singur loc de ales, nu un select pe fiecare rând.
const export_format = van.state("gpkg")
const export_srid = van.state("3844")
const export_srid_custom = van.state("")
const export_attachments = van.state(false)

/**
 * Personalul hărții: super/support, membru al org-ului hărții (orice rol) sau, pe o hartă fără
 * org, membru al vreunei primării — oglinda gărzii `operator_access(read)` din contractul de export
 * (arhiva cu atașamente și MBTiles). Doar pentru a nu oferi opțiuni care ar da 401/403.
 */
function is_personal_for_map(): boolean {
	const s = session.val
	if (!s.authenticated) {
		return false
	}
	const pr = s.user?.platform_role
	if (pr === "super" || pr === "support") {
		return true
	}
	const org = store.map_detail?.org_id ?? null
	if (org == null) {
		return s.memberships.length > 0
	}
	return s.memberships.some((m) => m.org_id === org)
}

/** SRID-ul efectiv din controale ("" = implicit / ignorat de format). */
function export_srid_value(): string {
	if (export_format.val === "kml" || export_format.val === "mbtiles") {
		return "" // serverul îl ignoră cu avertisment — nu-l trimitem
	}
	return export_srid.val === "custom" ? export_srid_custom.val.trim() : export_srid.val
}

/** URL-ul de export pentru hartă (`layer_slug` gol) sau pentru un strat, din preferințele curente. */
function export_url(map_slug: string, layer_slug = ""): string {
	const base = layer_slug
		? `/api/maps/${encodeURIComponent(map_slug)}/layers/${encodeURIComponent(layer_slug)}/export`
		: `/api/maps/${encodeURIComponent(map_slug)}/export`
	const q = new URLSearchParams({ format: export_format.val })
	const srid = export_srid_value()
	if (srid) {
		q.set("srid", srid)
	}
	if (!layer_slug && export_attachments.val) {
		q.set("attachments", "1")
	}
	return `${base}?${q.toString()}`
}

/** Eticheta formatului (din EXPORT_FORMATS_UI) — fișierul livrat e `_shp.zip`/`_csv.zip`, nu `.shp`. */
function export_format_label(value: string): string {
	return EXPORT_FORMATS_UI.find((f) => f.value === value)?.label ?? value
}

/**
 * Descarcă un export prin fetch + blob (nu `<a href>`): așa citim `X-Export-Warnings` și arătăm
 * mesajele serverului la refuz (429 „prea multe exporturi", 400 plafon MBTiles, 401/403) în loc
 * de o pagină JSON goală. Fără retry automat (contractul cere ca utilizatorul să reîncerce).
 */
async function download_export(url: string): Promise<string[]> {
	let res: Response
	try {
		res = await fetch(url, { credentials: "include" })
	} catch (e) {
		show_toast(`Descărcarea a eșuat: ${String(e)}`, "err")
		return []
	}
	if (!res.ok) {
		let message = `Exportul a eșuat (${res.status}).`
		try {
			const body = (await res.json()) as { err?: { message?: string } }
			message = body?.err?.message || message
		} catch {
			/* corp ne-JSON — păstrăm mesajul generic */
		}
		show_toast(message, "err", 7000)
		return []
	}
	// Corpul poate pica DUPĂ headere (conexiune întreruptă la jumătatea arhivei) — `blob()` respinge.
	let blob: Blob
	try {
		blob = await res.blob()
	} catch (e) {
		show_toast(`Descărcarea s-a întrerupt: ${String(e)}`, "err")
		return []
	}
	const cd = res.headers.get("content-disposition") ?? ""
	const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)
	let filename = "export"
	try {
		filename = m ? decodeURIComponent(m[1]) : "export"
	} catch {
		filename = m ? m[1] : "export"
	}
	const a = document.createElement("a")
	a.href = URL.createObjectURL(blob)
	a.download = filename
	document.body.appendChild(a)
	a.click()
	a.remove()
	setTimeout(() => URL.revokeObjectURL(a.href), 60_000)
	const wh = res.headers.get("x-export-warnings")
	if (wh) {
		try {
			const parsed = JSON.parse(decodeURIComponent(wh)) as unknown
			if (Array.isArray(parsed)) {
				return parsed.map(String)
			}
		} catch {
			/* header malformat — fără avertismente */
		}
	}
	return []
}

// ─────────────────────────────────────────────
// Utils
// ─────────────────────────────────────────────

function parse_json_field(raw: string): { value: unknown; error: string } {
	if (!raw.trim()) {
		return { value: undefined, error: "" }
	}
	try {
		return { value: JSON.parse(raw), error: "" }
	} catch (e) {
		return { value: undefined, error: `JSON invalid: ${(e as Error).message}` }
	}
}

function json_pretty(v: unknown): string {
	if (v == null) {
		return ""
	}
	try {
		return JSON.stringify(v, null, 2)
	} catch {
		return String(v)
	}
}

/** Tipul unui atribut din `attribute_schema` — formatul nou `{type}` sau GDAL brut ("Integer64"). */
function schema_type_text(v: unknown): string {
	return String((typeof v === "object" && v !== null ? (v as { type?: string }).type : v) ?? "")
}
/** Atribut numeric? — același regex ca la coerciția din formularul de editare (map.client). */
function schema_is_numeric(v: unknown): boolean {
	return /int|real|float|double|decimal|number/i.test(schema_type_text(v))
}

// Interpretarea valorii unei clauze de filtru salvat (editorul din EditLayerPanel).
type ValueKind = "auto" | "text" | "number" | "bool" | "null"

/** Felul afișat în select pentru o valoare deja salvată. */
function clause_kind_of(v: FilterValue): ValueKind {
	if (v === null) {
		return "null"
	}
	if (typeof v === "number") {
		return "number"
	}
	if (typeof v === "boolean") {
		return "bool"
	}
	return "text"
}

/**
 * Valoarea unei clauze din controale — fail-closed pe număr: „număr" gol/ne-finit e o eroare
 * explicită (Number("") = 0 transforma `suprafata >` în `> 0`); `contains` e MEREU text (auto
 * făcea din „12" un număr și validarea cerea „un text nevid"); „auto" dă număr DOAR când textul
 * e exact reprezentarea numărului („007" și „1,5" rămân text); textul pleacă trim-uit.
 */
function clause_value(value: string, kind: ValueKind, op: string): Validation<FilterValue> {
	const v = value.trim()
	if (op === "contains") {
		return { ok: true, value: v }
	}
	switch (kind) {
		case "null":
			return { ok: true, value: null }
		case "number": {
			const n = v === "" ? Number.NaN : Number(v.replace(",", "."))
			if (!Number.isFinite(n)) {
				return validation_error(v === "" ? "lipsește numărul" : `„${v}" nu e un număr`)
			}
			return { ok: true, value: n }
		}
		case "bool":
			return { ok: true, value: /^(da|true|1|adevarat|adevărat)$/i.test(v) }
		case "text":
			return { ok: true, value: v }
		default:
			return { ok: true, value: v !== "" && String(Number(v)) === v ? Number(v) : v }
	}
}

// ─────────────────────────────────────────────
// Shared form helpers
// ─────────────────────────────────────────────

const INPUT_CLS =
	"w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 " +
	"placeholder:text-slate-400 focus:outline-none focus:border-cyan-600 focus:ring-2 " +
	"focus:ring-cyan-600/20 transition"

const TEXTAREA_CLS =
	"w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-xs text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 " +
	"placeholder:text-slate-400 focus:outline-none focus:border-cyan-600 focus:ring-2 " +
	"focus:ring-cyan-600/20 transition resize-y font-mono"

const SELECT_CLS =
	"w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 " +
	"focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/20 transition"

const LABEL_CLS =
	"block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1"

const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm " +
	"font-semibold hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed transition"

// max-md:py-2 — țintă touch mai înaltă doar pe telefon (desktopul rămâne identic)
const BTN_SM_PRIMARY =
	"inline-flex items-center gap-1 px-3 py-1.5 max-md:py-2 rounded-lg bg-cyan-700 text-white text-xs " +
	"font-semibold hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed transition"

const BTN_SM_SECONDARY =
	"inline-flex items-center gap-1 px-3 py-1.5 max-md:py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs " +
	"font-semibold hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition"

function Field(
	label: string,
	input: HTMLElement,
	err_state?: ReturnType<typeof van.state<string>>,
) {
	const children: (HTMLElement | ReturnType<typeof t.div>)[] = [
		t.label({ class: LABEL_CLS }, label),
		input,
	]
	if (err_state) {
		children.push(
			t.div(
				{
					class: "text-xs text-red-600 dark:text-red-400 mt-1",
					style: () => `display:${err_state.val ? "block" : "none"}`,
				},
				() => err_state.val,
			),
		)
	}
	return t.div({ class: "flex flex-col" }, ...children)
}

// ─────────────────────────────────────────────
// Geometry badge
// ─────────────────────────────────────────────

function GeomBadge(get_type: () => string) {
	const COLOR: Record<string, string> = {
		Point: "bg-sky-100 text-sky-700",
		LineString: "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300",
		Polygon: "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300",
		Geometry: "bg-violet-100 text-violet-700",
	}
	return t.span(
		{
			class: () => {
				const c =
					COLOR[get_type()] ?? "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
				return `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${c}`
			},
		},
		() => get_type(),
	)
}

// ─────────────────────────────────────────────
// Create Map Form
// ─────────────────────────────────────────────

function CreateMapForm() {
	const slug_err = van.state("")
	const name_err = van.state("")
	const submitting = van.state(false)

	const inp_slug = t.input({ type: "text", class: INPUT_CLS, placeholder: "ex: pug-baiculesti" })
	const inp_name = t.input({ type: "text", class: INPUT_CLS, placeholder: "ex: PUG Băiculești" })
	const inp_desc = t.input({ type: "text", class: INPUT_CLS, placeholder: "Descriere opțională" })
	const org_err = van.state("")
	const orgs = van.state<Array<{ id: string; name: string }>>([])
	const org_select = t.select({ class: INPUT_CLS })
	van.derive(() => {
		org_select.replaceChildren(
			t.option({ value: "" }, "— alege organizația —"),
			...orgs.val.map((o) => t.option({ value: o.id }, o.name)),
		)
	})
	// Reactiv la sesiune: la refresh, `writable_orgs` poate rula ÎNAINTE ca sesiunea să se încarce
	// (super are 0 membershipuri → ar cădea pe ramura goală). Re-încarcă când sesiunea se schimbă.
	van.derive(() => {
		void session.val
		writable_orgs().then((list) => {
			orgs.val = list
		})
	})

	async function on_submit(e: Event) {
		e.preventDefault()
		slug_err.val = ""
		name_err.val = ""
		org_err.val = ""

		const slug = inp_slug.value.trim()
		const name = inp_name.value.trim()
		const desc = inp_desc.value.trim()
		const org_id = org_select.value || undefined

		if (!slug) {
			slug_err.val = "Slug-ul este obligatoriu"
			return
		}
		if (!name) {
			name_err.val = "Numele este obligatoriu"
			return
		}
		if (!org_id) {
			org_err.val = "Alege o organizație"
			return
		}

		submitting.val = true
		const ok = await create_map(slug, name, desc, org_id)
		submitting.val = false
		if (ok) {
			inp_slug.value = ""
			inp_name.value = ""
			inp_desc.value = ""
		}
	}

	return t.form(
		{ onsubmit: on_submit, class: "flex flex-col gap-3" },
		Field("Slug", inp_slug, slug_err),
		Field("Nume", inp_name, name_err),
		Field("Organizație", org_select, org_err),
		Field("Descriere", inp_desc),
		t.button({ type: "submit", class: BTN_PRIMARY, disabled: () => submitting.val }, () =>
			submitting.val ? "Se creează..." : "Creare hartă",
		),
	)
}

// ─────────────────────────────────────────────
// Maps Sidebar
// ─────────────────────────────────────────────

function MapsSidebar() {
	return t.aside(
		{
			class:
				// Mobil: bandă full-width deasupra conținutului (shell-ul e flex-col sub lg), cu
				// max-h ca lista de hărți să nu împingă tot conținutul; desktop (lg+): coloana
				// istorică 288px, sticky sub header (h-12 ⇒ top-12 — off-by-16 istoric corectat).
				"w-full max-h-64 lg:max-h-none lg:w-72 shrink-0 bg-white dark:bg-slate-900 " +
				"border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-700 flex flex-col gap-5 p-4 " +
				"overflow-y-auto lg:h-[calc(100dvh-3rem)] lg:sticky lg:top-12",
		},

		// ── Map list card (flex-1: umple spațiul rămas; min-h: nu se colapsează pe ecran mic — atunci
		//    aside-ul derulează ca întreg, deci și lista și „Hartă nouă" rămân accesibile) ──
		t.div(
			{
				class:
					"rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col flex-1 min-h-[160px]",
			},
			t.div(
				{
					class:
						"flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-700 " +
						"rounded-t-xl shrink-0",
				},
				t.h2(
					{
						class: "text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400",
					},
					"Hărți",
				),
				t.span(
					{ style: () => `display:${store.maps_loading ? "inline-block" : "none"}` },
					t.span({ class: "spinner" }),
				),
			),
			t.div({ class: "p-2 overflow-y-auto min-h-0 flex-1" }, () => {
				const maps = store.maps
				if (maps.length === 0) {
					return t.div(
						{ class: "py-8 text-center text-sm text-slate-400 dark:text-slate-500" },
						store.maps_loading ? "Se încarcă..." : "Nicio hartă",
					)
				}
				return t.div(
					{ class: "flex flex-col gap-0.5" },
					...maps.map((m) =>
						t.button(
							{
								type: "button",
								class: () => {
									const base =
										"w-full text-left px-3 py-2.5 rounded-lg transition flex flex-col gap-0.5 "
									return store.selected_map === m.slug
										? base + "bg-cyan-50 dark:bg-cyan-950 border border-cyan-200"
										: base + "hover:bg-slate-50 dark:hover:bg-slate-800 border border-transparent"
								},
								onclick: () => {
									store.selected_map = m.slug
									store.active_tab = "layers"
									store.edit_layer_slug = null
									load_map_detail(m.slug)
								},
							},
							t.div(
								{
									class: () =>
										`text-sm font-semibold ${store.selected_map === m.slug ? "text-cyan-800 dark:text-cyan-300" : "text-slate-800 dark:text-slate-200"}`,
								},
								m.name,
							),
							t.div({ class: "text-xs font-mono text-slate-400 dark:text-slate-500" }, m.slug),
						),
					),
				)
			}),
		),

		// ── Create map card (shrink-0: înălțime naturală, mereu complet vizibil sub listă) ──
		t.div(
			{
				class: "rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0",
			},
			t.div(
				{
					class:
						"px-4 py-3 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-700",
				},
				t.h2(
					{
						class: "text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400",
					},
					"Hartă nouă",
				),
			),
			t.div({ class: "p-4" }, CreateMapForm()),
		),
	)
}

// ─────────────────────────────────────────────
// Edit Layer Panel (inline)
// ─────────────────────────────────────────────

// Șabloanele vizibile pe harta curentă (globale + ale org-ului ei), aduse O DATĂ per hartă:
// panoul de editare e instanțiat per RÂND de strat la fiecare reconstruire a tabelului (27 de
// straturi = 27 de cereri identice, la fiecare reordonare) — promisiunea e memorată pe slug.
let templates_memo: { key: string; list: Promise<{ slug: string; name: string }[]> } | null = null
function org_templates(map_slug: string): Promise<{ slug: string; name: string }[]> {
	if (templates_memo?.key === map_slug) {
		return templates_memo.list
	}
	const org = store.map_detail?.org_id ?? null
	const list = api_get<{ slug: string; name: string; org_id: string | null }[]>(
		"/api/templates",
	).then((r) =>
		r.ok
			? r.data
					.filter((x) => x.org_id === null || x.org_id === org)
					.map((x) => ({ slug: x.slug, name: x.name || x.slug }))
			: [],
	)
	templates_memo = { key: map_slug, list }
	return list
}

/** Text pliat pentru căutare: minuscule, fără diacritice („Fișă" găsește „fisa"). */
const fold_text = (v: string): string =>
	v
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")

function EditLayerPanel(get_layer: () => LayerSummary, get_map_slug: () => string) {
	const style_err = van.state("")
	const schema_err = van.state("")
	const meta_err = van.state("")
	const submitting = van.state(false)

	const inp_name = t.input({ type: "text", class: INPUT_CLS, placeholder: "Nume strat" })
	const ta_schema = t.textarea({
		class: TEXTAREA_CLS,
		placeholder: '{"cod":{"type":"string"}}',
		rows: "4",
	})
	const ta_style = t.textarea({
		class: TEXTAREA_CLS,
		placeholder: '{"fill-color":"#3a5bff"}',
		rows: "4",
	})
	const ta_meta = t.textarea({ class: TEXTAREA_CLS, placeholder: '{"sursa":"ANCPI"}', rows: "3" })
	// Rolul de URBANISM al stratului (F5): 'utr' = sursa regimului on-click (are și fallback pe
	// atributul cod_utr); 'derogare' = PUZ aprobat care PREVALEAZĂ asupra regimului (REQ-URB-008)
	// — derogarea NU are fallback, deci fără selectorul ăsta ar fi setabilă doar prin API.
	const sel_role = t.select(
		{ class: INPUT_CLS + " max-w-xs" },
		t.option({ value: "" }, "— fără rol —"),
		t.option({ value: "utr" }, "UTR — unitățile teritoriale de referință (sursa regimului)"),
		t.option({ value: "derogare" }, "Derogare — PUZ aprobat (prevalează asupra regimului)"),
		// F9-T5: fără opțiunea asta, deschiderea unui strat de colectare în admin ar cădea pe
		// „— fără rol —" și salvarea i-ar ȘTERGE tăcut rolul.
		t.option({ value: "colectare" }, "Colectare teren — strat de inventariere creat din teren"),
	) as HTMLSelectElement
	// Clasa Ordin 904/2023 (F8): maparea stratului pe nomenclatorul MDLPA — alimentează
	// validatorul de conformitate + exportul GPKG oficial (Stereo 70).
	const sel_904 = t.select(
		{ class: INPUT_CLS + " max-w-md" },
		t.option({ value: "" }, "— nemapat —"),
		...ORDIN_904_SPATIAL.map((c) => t.option({ value: c.name }, `${c.name} — ${c.label}`)),
	) as HTMLSelectElement
	// Șabloane permise pe features-ul stratului (F13): BIFE din lista șabloanelor primăriei
	// (globale + ale org-ului hărții) — înainte era text liber cu slug-uri, iar o greșeală de
	// tastare se salva tăcut și panoul hărții spunea doar „niciun act configurat". Nimic bifat =
	// predefinirea după rol (utr/derogare → certificat_urbanism), altfel niciun act. Peste
	// TEMPLATES_SEARCH_FROM șabloane lista primește căutare + derulare, cu cele bifate sus — o
	// primărie cu zeci de tipuri de acte nu derulează o coloană întreagă ca să vadă ce e activ.
	// ⚠️ Câmpul de căutare e construit O DATĂ (re-crearea lui la fiecare literă pierdea focusul);
	// selecția stă într-un Set, nu în DOM: o bifă ascunsă de filtru nu trebuie să se piardă.
	const TEMPLATES_SEARCH_FROM = 8
	let template_options: { slug: string; name: string }[] = []
	let templates_loaded = false
	let templates_selected = new Set<string>()
	let templates_filter = ""
	const templates_list = t.div({ class: "flex flex-col gap-1.5" })
	const templates_search = t.input({
		type: "search",
		class: INPUT_CLS + " max-w-xs",
		placeholder: "caută un șablon…",
		oninput: (e: Event) => {
			templates_filter = (e.target as HTMLInputElement).value
			render_templates_list()
		},
	}) as HTMLInputElement
	const templates_box = t.div(
		{ class: "flex flex-col gap-2" },
		templates_search,
		templates_list,
		t.p(
			{ class: "text-xs text-slate-400 dark:text-slate-500" },
			"nimic bifat = doar implicitul pe rol (UTR / derogare → Certificat de urbanism); un strat fără rol și fără bife nu oferă niciun act din panoul hărții",
		),
	)
	function render_templates_list(): void {
		const known = new Set(template_options.map((o) => o.slug))
		// Slug-uri deja salvate care nu mai corespund niciunui șablon (tastate greșit sau șablon
		// retras) rămân vizibile, bifate, ca să poată fi debifate — nu dispar tăcut.
		const all = [
			...template_options,
			...[...templates_selected]
				.filter((slug) => !known.has(slug))
				.map((slug) => ({ slug, name: `${slug} — șablon inexistent (debifează)` })),
		]
		const many = all.length > TEMPLATES_SEARCH_FROM
		templates_search.style.display = many ? "" : "none"
		templates_list.className = many
			? "flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1"
			: "flex flex-col gap-1.5"
		const q = fold_text(templates_filter.trim())
		const rows = all
			.filter((o) => q === "" || fold_text(`${o.name} ${o.slug}`).includes(q))
			.toSorted(
				(x, y) => Number(templates_selected.has(y.slug)) - Number(templates_selected.has(x.slug)),
			)
		templates_list.replaceChildren(
			...(templates_loaded
				? rows.length > 0
					? rows.map((o) =>
							t.label(
								{
									class:
										"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none",
								},
								t.input({
									type: "checkbox",
									class: "accent-cyan-700",
									value: o.slug,
									checked: templates_selected.has(o.slug),
									onchange: (e: Event) => {
										if ((e.target as HTMLInputElement).checked) {
											templates_selected.add(o.slug)
										} else {
											templates_selected.delete(o.slug)
										}
									},
								}),
								o.name,
							),
						)
					: [t.span({ class: "text-xs text-slate-400 italic" }, "niciun șablon nu se potrivește")]
				: [t.span({ class: "text-xs text-slate-400 italic" }, "Se încarcă șabloanele…")]),
		)
	}
	// Ordinea listei, nu a bifării — configurația salvată nu depinde de cum ai dat click.
	const checked_templates = (): string[] => {
		const known = template_options.map((o) => o.slug).filter((s) => templates_selected.has(s))
		const unknown = [...templates_selected].filter(
			(s) => !template_options.some((o) => o.slug === s),
		)
		return [...known, ...unknown]
	}
	void org_templates(get_map_slug()).then((list) => {
		template_options = list
		templates_loaded = true
		render_templates_list()
	})
	// Consultare publică PUG preliminar (F6-T6, REQ-PEX-008): bifat → stratul apare în portalul
	// public cu banner „în consultare" + formular de observații (dacă stratul e și PUBLICAT).
	const inp_consultare = t.input({ type: "checkbox", class: "accent-cyan-700" }) as HTMLInputElement
	const inp_consultare_term = t.input({
		type: "date",
		class: INPUT_CLS + " max-w-[180px]",
		title:
			"Termenul consultării (gol = fără termen); după această dată bannerul dispare din portal.",
	}) as HTMLInputElement
	const consultare_row = t.div(
		{ class: "flex items-center gap-3" },
		t.label(
			{
				class:
					"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none",
			},
			inp_consultare,
			"în consultare publică",
		),
		t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "până la"),
		inp_consultare_term,
	)
	// Starea IMPLICITĂ a stratului la deschiderea hărții (18.09): nebifat = stratul pornește stins
	// ȘI nu se încarcă deloc (nici cerere, nici tile-uri) — pe hărțile cu zeci de straturi e cel mai
	// direct câștig de viteză la pornire. Alegerea vizitatorului, în sesiunea lui, rămâne deasupra.
	const inp_visible_default = t.input({
		type: "checkbox",
		class: "accent-cyan-700",
	}) as HTMLInputElement
	const visible_default_row = t.label(
		{
			class:
				"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none",
		},
		inp_visible_default,
		"pornit implicit pe hartă (nebifat = harta se deschide mai repede, stratul se aprinde la cerere)",
	)
	// DREPTURI PE STRAT ȘI PE ATRIBUT pe rol (lib/layer_access): ce văd vizualizatorii, ce pot
	// editorii, plus atributele ascunse fiecărui rol — bife per cheie din `attribute_schema` (aceeași
	// idee ca atributele publice din Publicare). Se salvează în ACELAȘI „Salvează" al stratului, doar
	// dacă s-a schimbat, validat local cu aceeași funcție pură ca pe server. Blocul e VIZIBIL doar
	// administratorilor primăriei (owner/admin) și super — serverul refuză oricum restul cu 403.
	// ⚠️ Van: ambele liste de bife sunt montate STATIC și repopulate imperativ după fetch-ul lazy al
	// schemei; selecția stă în Set-uri, nu în DOM (o bifă re-randată nu-și pierde starea).
	const access_err = van.state("")
	const sel_access_viewer = t.select(
		{ class: INPUT_CLS + " max-w-xs" },
		...VIEWER_LEVELS.map((v) => t.option({ value: v }, VIEWER_LEVEL_LABELS[v])),
	) as HTMLSelectElement
	const sel_access_editor = t.select(
		{ class: INPUT_CLS + " max-w-xs" },
		...EDITOR_LEVELS.map((v) => t.option({ value: v }, EDITOR_LEVEL_LABELS[v])),
	) as HTMLSelectElement
	let access_loaded = false
	let access_initial = "null" // forma minimă stocată, ca JSON — diferențialul de la salvare
	let access_keys: string[] = []
	const hidden_viewer = new Set<string>()
	const hidden_editor = new Set<string>()
	const hidden_viewer_list = t.div({ class: "flex flex-col gap-1" })
	const hidden_editor_list = t.div({ class: "flex flex-col gap-1" })
	function render_hidden_list(target: HTMLElement, selected: Set<string>): void {
		target.replaceChildren(
			...(access_keys.length === 0
				? [
						t.span(
							{ class: "text-xs text-slate-400 italic" },
							access_loaded
								? "stratul n-are atribute declarate în schemă"
								: "Se încarcă atributele…",
						),
					]
				: access_keys.map((key) =>
						t.label(
							{
								class:
									"flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none",
							},
							t.input({
								type: "checkbox",
								class: "accent-cyan-700",
								checked: selected.has(key),
								onchange: (e: Event) => {
									if ((e.target as HTMLInputElement).checked) {
										selected.add(key)
									} else {
										selected.delete(key)
									}
								},
							}),
							key,
						),
					)),
		)
	}
	const access_block = t.div(
		{ class: "flex flex-col gap-2" },
		t.div(
			{ class: "flex flex-wrap items-center gap-3" },
			t.span({ class: "text-sm text-slate-700 dark:text-slate-200" }, "Vizualizatorii:"),
			sel_access_viewer,
			t.span({ class: "text-sm text-slate-700 dark:text-slate-200" }, "Editorii:"),
			sel_access_editor,
		),
		t.div(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Atribute ascunse vizualizatorilor (bifate = nu apar în hartă, tabel, căutare, export, acte):",
		),
		hidden_viewer_list,
		t.details(
			{ class: "text-xs" },
			t.summary(
				{ class: "cursor-pointer text-slate-500 dark:text-slate-400" },
				"Atribute ascunse editorilor (rar — editorul nu le vede și nu le poate modifica)",
			),
			t.div({ class: "mt-1" }, hidden_editor_list),
		),
		t.p(
			{ class: "text-xs text-slate-400 dark:text-slate-500" },
			"Proprietarii și administratorii primăriei văd și scriu mereu tot. Un strat „nu văd stratul” nu apare deloc rolului respectiv — nici pe hartă, nici în căutare, export sau regim; „doar citesc” lasă editorii să-l vadă, dar nu să-l modifice.",
		),
	)
	/** Poate contul curent configura drepturile? (owner/admin al primăriei hărții sau super.) */
	const can_configure_access = (): boolean => {
		const s = session.val
		if (s.user?.platform_role === "super") {
			return true
		}
		const org = store.map_detail?.org_id ?? ""
		const m = s.memberships.find((x) => x.org_id === org)
		return !!m && (m.role === "owner" || m.role === "admin")
	}
	// Extrudarea 3D a stratului (F10-T1): sursa înălțimii, în ordinea precedenței din lib/style3d
	// (atribut metri → atribut etaje ×3m → atribut regim „P+2" → join dinamic cu UTR-ul din RLU).
	const sel_3d = t.select(
		{ class: INPUT_CLS + " max-w-xs" },
		t.option({ value: "" }, "auto — doar straturile de clădiri"),
		t.option({ value: "on" }, "da — stratul se extrudează"),
		t.option({ value: "off" }, "nu — exclus din vederea 3D"),
	) as HTMLSelectElement
	const chk_regim_utr = t.input({ type: "checkbox", class: "accent-cyan-700" }) as HTMLInputElement
	// Sugestii pentru câmpurile de atribut: coloanele REALE ale stratului (attribute_schema) —
	// utilizatorul ALEGE dintr-o listă, nu ghicește numele exact al coloanei din CAD/OSM.
	const dl_3d_id = `s3d-attrs-${Math.random().toString(36).slice(2, 8)}`
	const dl_3d = t.datalist({ id: dl_3d_id })
	const inp_3d_h = t.input({
		type: "text",
		class: INPUT_CLS + " max-w-[160px]",
		placeholder: "alege coloana…",
		list: dl_3d_id,
		title:
			"NUMELE coloanei (nu o valoare!) cu înălțimea în metri, dacă datele stratului au una — ex. inaltime, H_MAX.",
	}) as HTMLInputElement
	const inp_3d_l = t.input({
		type: "text",
		class: INPUT_CLS + " max-w-[160px]",
		placeholder: "alege coloana…",
		list: dl_3d_id,
		title: "NUMELE coloanei cu numărul de etaje (fiecare etaj = 3m) — ex. etaje, nr_niveluri.",
	}) as HTMLInputElement
	const inp_3d_r = t.input({
		type: "text",
		class: INPUT_CLS + " max-w-[160px]",
		placeholder: "alege coloana…",
		list: dl_3d_id,
		title:
			"NUMELE coloanei text cu regimul de înălțime PER CLĂDIRE („P+2”, „D+P+M”…) — diferit de bifa de mai jos, care ia regimul ZONEI din regulament.",
	}) as HTMLInputElement
	const inp_3d_color = t.input({
		type: "text",
		class: INPUT_CLS + " max-w-[120px]",
		placeholder: "#0891b2",
		title: "Culoarea extrudării (gol = culoarea stratului).",
	}) as HTMLInputElement
	const style3d_rows = t.div(
		{ class: "flex flex-col gap-2" },
		sel_3d,
		dl_3d,
		t.div(
			{ class: "flex flex-wrap items-center gap-3 text-sm text-slate-700 dark:text-slate-200" },
			t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "înălțime (m)"),
			inp_3d_h,
			t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "etaje"),
			inp_3d_l,
			t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "regim"),
			inp_3d_r,
			t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "culoare"),
			inp_3d_color,
		),
		t.p(
			{ class: "text-[11px] text-slate-400 dark:text-slate-500 max-w-2xl" },
			"În câmpuri se pune NUMELE coloanei din datele stratului (apare lista la click), nu o valoare. " +
				"Sursele se încearcă în ordine, pentru fiecare clădire: înălțime → etaje → regim → bifa de mai jos. " +
				"Câmpurile goale caută automat numele uzuale (inaltime, etaje, regim_inaltime…).",
		),
		t.label(
			{
				class:
					"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none",
			},
			chk_regim_utr,
			"înălțimea din regimul UTR — ia regimul ZONEI (rh_max din regulament) pentru clădirile fără niciun atribut de mai sus",
		),
	)

	// populate synchronously
	const layer = get_layer()
	inp_name.value = layer.name
	ta_schema.value = json_pretty(layer.attribute_schema)
	ta_style.value = json_pretty(layer.style)
	ta_meta.value = json_pretty(layer.metadata)
	sel_role.value = layer.role ?? ""
	sel_904.value = layer.ordin904_class ?? ""
	templates_selected = new Set(layer.allowed_templates ?? [])
	templates_filter = ""
	templates_search.value = ""
	render_templates_list()
	inp_consultare.checked = layer.consultare === true
	inp_consultare_term.value = layer.consultare_pana_la ?? ""
	inp_visible_default.checked = layer.visible_default !== false
	// Sugestiile de coloane pentru 3D: cheile din attribute_schema, cele numerice primele
	// (înălțimea/etajele sunt numerice; regimul e text — lista e comună, tastarea filtrează).
	// ⚠️ map_detail NU transportă attribute_schema → fetch lazy din detaliul stratului
	// (același pattern ca load_keys din publicare); formularul rămâne funcțional și fără.
	// Schema REALĂ a stratului (din get_layer) = BAZA editorului de valori permise la salvare:
	// textarea-ul pornește GOL (LayerRef nu transportă attribute_schema), deci fără ea listele
	// n-ar fi ajuns niciodată pe server. `schema_initial` = amprenta, ca schema neschimbată să
	// nu plece în PATCH la fiecare salvare.
	let schema_current: Record<string, unknown> = {}
	let schema_initial = ""
	void (async () => {
		const r = await api_get<{
			config?: { attribute_schema?: Record<string, unknown>; access?: unknown }
		}>(`/api/maps/${encodeURIComponent(get_map_slug())}/layers/${encodeURIComponent(layer.slug)}`)
		const schema = ((r.ok ? r.data.config?.attribute_schema : null) ?? {}) as Record<
			string,
			unknown
		>
		schema_current = schema
		schema_initial = JSON.stringify(schema)
		// Drepturile pe strat/atribut: valoarea curentă (forma minimă) → controale; cheile = ale schemei.
		const access_cfg = parse_layer_access(r.ok ? r.data.config?.access : null)
		access_initial = JSON.stringify(access_cfg ? minimal_layer_access(access_cfg) : null)
		sel_access_viewer.value = access_cfg?.viewer ?? "read"
		sel_access_editor.value = access_cfg?.editor ?? "write"
		hidden_viewer.clear()
		hidden_editor.clear()
		for (const k of access_cfg?.hidden_attributes.viewer ?? []) {
			hidden_viewer.add(k)
		}
		for (const k of access_cfg?.hidden_attributes.editor ?? []) {
			hidden_editor.add(k)
		}
		// Cheile ASCUNSE care nu mai sunt în schemă rămân vizibile, bifate, ca să poată fi debifate.
		access_keys = [
			...Object.keys(schema),
			...[...hidden_viewer, ...hidden_editor].filter((k) => !(k in schema)),
		].filter((k, i, arr) => arr.indexOf(k) === i)
		access_loaded = true
		render_hidden_list(hidden_viewer_list, hidden_viewer)
		render_hidden_list(hidden_editor_list, hidden_editor)
		// Schema are DOUĂ formate istorice (vezi normalize_edit_schema din map.client):
		// {cheie: {type: "number"}} (nou) și {cheie: "Integer64"} (GDAL brut) — iar tipurile GDAL
		// vin cu majusculă (Integer64/Real). Clasificarea (`schema_is_numeric`) = același regex ca
		// la coerciția din formularul de editare.
		const attr_keys = Object.entries(schema).sort(
			(a, b) =>
				Number(schema_is_numeric(b[1])) - Number(schema_is_numeric(a[1])) ||
				a[0].localeCompare(b[0]),
		)
		dl_3d.replaceChildren(
			...attr_keys.map(([k, v]) =>
				t.option({ value: k, label: schema_is_numeric(v) ? `${k} — numeric` : `${k} — text` }),
			),
		)
		// Editorul de valori permise (1.6) se construiește din ACEEAȘI schemă (o singură cerere).
		build_values_rows(schema)
	})()
	const s3d_now = layer.style3d ?? {}
	sel_3d.value = s3d_now.enabled === true ? "on" : s3d_now.enabled === false ? "off" : ""
	chk_regim_utr.checked = s3d_now.regim_utr === true
	inp_3d_h.value = typeof s3d_now.height_attr === "string" ? s3d_now.height_attr : ""
	inp_3d_l.value = typeof s3d_now.levels_attr === "string" ? s3d_now.levels_attr : ""
	inp_3d_r.value = typeof s3d_now.regim_attr === "string" ? s3d_now.regim_attr : ""
	inp_3d_color.value = typeof s3d_now.color === "string" ? s3d_now.color : ""

	/** Config-ul 3D din controale + cheile negestionate de UI (opacity/base_attr, doar API) —
	 *  altfel salvarea din admin le-ar șterge tăcut. */
	function style3d_from_controls(): Record<string, unknown> {
		const out: Record<string, unknown> = {}
		for (const k of ["opacity", "base_attr"]) {
			if (s3d_now[k] !== undefined) {
				out[k] = s3d_now[k]
			}
		}
		if (sel_3d.value === "on") {
			out.enabled = true
		}
		if (sel_3d.value === "off") {
			out.enabled = false
		}
		if (chk_regim_utr.checked) {
			out.regim_utr = true
		}
		for (const [k, inp] of [
			["height_attr", inp_3d_h],
			["levels_attr", inp_3d_l],
			["regim_attr", inp_3d_r],
			["color", inp_3d_color],
		] as const) {
			const v = inp.value.trim()
			if (v) {
				out[k] = v
			}
		}
		return out
	}
	const style3d_initial = JSON.stringify(style3d_from_controls())

	// ── Iconița stratului punctual (decizia 1.4, plan/contracts/icoane.md) ──
	// Gestionată DOAR prin rutele dedicate (upload/ștergere) — PATCH-ul de stil o păstrează singur.
	const is_point_layer = /point/i.test(layer.geometry_type)
	const icon_url = van.state(layer.icon_url ?? "")
	van.derive(() => {
		icon_url.val = get_layer().icon_url ?? ""
	})
	const icon_busy = van.state(false)
	/**
	 * Actualizează `icon_url` PE LOC în store (vanX.reactive e adânc) — NU `load_map_detail`:
	 * acela pune `map_detail = null`, tabelul de straturi se re-randează și panoul de editare
	 * (cu filtrele/valorile/JSON-ul nesalvate) dispărea de sub utilizator.
	 */
	function set_layer_icon_url(url: string) {
		const l = store.map_detail?.layers.find((x) => x.slug === layer.slug)
		if (l) {
			l.icon_url = url
		}
	}
	const icon_img = t.img({
		alt: "iconița stratului",
		class: () =>
			"w-9 h-9 object-contain rounded border border-slate-200 dark:border-slate-700 bg-white " +
			(icon_url.val ? "" : "hidden"),
		src: () => icon_url.val || "",
	})
	const icon_file = t.input({
		type: "file",
		accept: "image/png,image/svg+xml",
		class:
			"text-xs file:mr-2 file:px-2 file:py-1 file:rounded-md file:border-0 file:text-xs " +
			"file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100 cursor-pointer",
	}) as HTMLInputElement
	async function upload_icon() {
		const f = icon_file.files?.[0]
		if (!f) {
			show_toast("Alege un fișier PNG sau SVG (max 64 KB).", "err")
			return
		}
		if (f.size > 64 * 1024) {
			show_toast(
				"Iconița depășește 64 KB — micșoreaz-o (un simbol de 64×64 px e sub 10 KB).",
				"err",
			)
			return
		}
		const fd = new FormData()
		fd.append("file", f)
		icon_busy.val = true
		const r = await api_upload<{ icon_url: string }>(
			`/api/maps/${encodeURIComponent(get_map_slug())}/layers/${encodeURIComponent(layer.slug)}/icon`,
			fd,
		)
		icon_busy.val = false
		if (r.ok) {
			icon_url.val = r.data.icon_url
			icon_file.value = ""
			show_toast("Iconiță încărcată — apare pe hartă și în legendă", "ok")
			set_layer_icon_url(r.data.icon_url)
		} else {
			// Mesajul serverului spune DE CE a fost respins (ex. SVG cu script) — îl arătăm ca atare.
			show_toast(r.err?.message ?? "Iconița a fost respinsă.", "err", 8000)
		}
	}
	async function clear_icon() {
		if (!confirm("Ștergi iconița stratului? Punctele revin la simbolul implicit.")) {
			return
		}
		icon_busy.val = true
		const r = await api_delete(
			`/api/maps/${encodeURIComponent(get_map_slug())}/layers/${encodeURIComponent(layer.slug)}/icon`,
		)
		icon_busy.val = false
		if (r.ok) {
			icon_url.val = ""
			show_toast("Iconiță ștearsă", "ok")
			set_layer_icon_url("")
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}
	const icon_row = t.div(
		{ class: "flex items-center gap-3 flex-wrap" },
		icon_img,
		t.span(
			{
				class: () => "text-xs text-slate-400 dark:text-slate-500 " + (icon_url.val ? "hidden" : ""),
			},
			"fără iconiță (simbolul implicit)",
		),
		icon_file,
		t.button(
			{
				type: "button",
				class: BTN_SM_SECONDARY,
				disabled: () => icon_busy.val,
				onclick: upload_icon,
			},
			() => (icon_busy.val ? "…" : "Încarcă iconița"),
		),
		t.button(
			{
				type: "button",
				class: () =>
					BTN_SM_SECONDARY + " text-red-600 dark:text-red-400 " + (icon_url.val ? "" : "hidden"),
				disabled: () => icon_busy.val,
				onclick: clear_icon,
			},
			"Șterge iconița",
		),
		t.span(
			{ class: "text-[11px] text-slate-400 dark:text-slate-500 basis-full" },
			"PNG sau SVG, max 64 KB, fundal transparent. SVG-urile cu script sau referințe externe sunt respinse.",
		),
	)

	// ── Valori permise per atribut (decizia 1.6, plan/contracts/liste_valori.md) ──
	// Rândurile se construiesc din schema REALĂ a stratului (fetch-ul lazy de mai sus); la salvare,
	// listele se ÎMBINĂ în schema din textarea (editorul e sursa pentru `values`), apoi totul trece
	// prin `validate_attribute_schema` — același cod ca pe server, mesaj înainte de PATCH.
	const values_box = t.div({ class: "flex flex-col gap-1.5" })
	const values_rows: Array<{ key: string; ta: HTMLTextAreaElement; numeric: boolean }> = []
	const values_count = van.state(0) // câte atribute AU listă (eticheta blocului)
	// Câte rânduri există + dacă schema a sosit — `values_rows.length` e un array simplu, citit
	// reactiv se evalua o singură dată la montare („Stratul nu are atribute" rămânea vizibil).
	const values_n = van.state(0)
	const values_loaded = van.state(false)
	const values_open = van.state(false)
	function build_values_rows(schema: Record<string, unknown>) {
		values_box.replaceChildren()
		values_rows.length = 0
		values_count.val = 0
		for (const [key, def] of Object.entries(schema)) {
			const current = Array.isArray((def as { values?: unknown[] })?.values)
				? ((def as { values: unknown[] }).values as unknown[])
				: []
			const numeric = schema_is_numeric(def)
			const ta = t.textarea({
				class:
					"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm outline-none focus:border-cyan-500 flex-1 min-h-[2.2rem]",
				rows: "1",
				placeholder: "o valoare pe rând (gol = câmp liber)",
			}) as HTMLTextAreaElement
			ta.value = current.map(String).join("\n")
			if (current.length > 0) {
				values_count.val++
			}
			values_rows.push({ key, ta, numeric })
			values_box.appendChild(
				t.div(
					{ class: "flex gap-2 items-start" },
					t.code(
						{
							class:
								"font-mono text-xs text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-1 rounded w-44 flex-shrink-0 truncate",
							title: `${key} — ${numeric ? "numeric" : "text"}`,
						},
						key,
					),
					ta,
				),
			)
		}
		values_n.val = values_rows.length
		values_loaded.val = true
	}
	/** Îmbină listele din editor în schema dată (mutare in-place); întoarce schema. */
	function apply_values(schema: Record<string, unknown>): Record<string, unknown> {
		for (const row of values_rows) {
			if (!(row.key in schema)) {
				continue // cheie scoasă din JSON între timp — nu o reînviem
			}
			const values = row.ta.value
				.split(/\r?\n/)
				.map((s) => s.trim())
				.filter((s) => s !== "")
				.map((s) => (row.numeric && /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : s))
			const def = schema[row.key]
			const is_obj = typeof def === "object" && def !== null
			if (values.length > 0) {
				// Forma veche `"String"` devine `{type:"String"}` DOAR când primește listă — altfel
				// o schemă neatinsă ar arăta „modificată" și ar pleca în PATCH la fiecare salvare.
				const base: Record<string, unknown> = is_obj
					? { ...(def as Record<string, unknown>) }
					: { type: String(def ?? "") }
				base.values = values
				schema[row.key] = base
			} else if (is_obj && "values" in (def as Record<string, unknown>)) {
				const base = { ...(def as Record<string, unknown>) }
				delete base.values
				schema[row.key] = base
			}
		}
		return schema
	}
	const values_block = t.div(
		{ class: "flex flex-col gap-2" },
		t.button(
			{
				type: "button",
				class:
					"self-start text-xs text-cyan-700 dark:text-cyan-300 font-semibold hover:underline cursor-pointer",
				onclick: () => {
					values_open.val = !values_open.val
				},
			},
			() =>
				`${values_open.val ? "▾" : "▸"} valori permise per atribut` +
				(values_count.val > 0 ? ` (${values_count.val} liste)` : ""),
		),
		t.div(
			{ class: () => "flex flex-col gap-2 " + (values_open.val ? "" : "hidden") },
			t.p(
				{ class: "text-[11px] text-slate-400 dark:text-slate-500 max-w-2xl" },
				"Pentru atributele cu listă, formularele de editare și de colectare afișează o alegere, iar serverul " +
					"refuză valorile din afara listei (valoarea goală rămâne permisă). Max 200 de valori, fără dubluri.",
			),
			values_box,
			t.span(
				{
					class: () =>
						"text-xs text-slate-400 dark:text-slate-500 " +
						(values_loaded.val && values_n.val === 0 ? "" : "hidden"),
				},
				"Stratul nu are atribute în schemă.",
			),
		),
	)

	// ── Filtre salvate ca straturi derivate (decizia 2.2, plan/contracts/filtre_salvate.md) ──
	// Editor structurat (nume, slug, condiții ȘI): fără SQL liber; validat local cu
	// `validate_saved_filters` (același cod ca serverul) înainte de a intra în PATCH.
	interface ClauseRow {
		el: HTMLElement
		attr: HTMLInputElement
		op: HTMLSelectElement
		value: HTMLInputElement
		kind: HTMLSelectElement
	}
	interface FilterRow {
		el: HTMLElement
		name: HTMLInputElement
		slug: HTMLInputElement
		slug_auto: boolean
		clauses: ClauseRow[]
		clauses_box: HTMLElement
	}
	const filter_rows: FilterRow[] = []
	const filters_box = t.div({ class: "flex flex-col gap-3" })
	const filters_count = van.state(0)
	const filters_open = van.state(false)
	const filters_err = van.state("")
	const F_IN =
		"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm outline-none focus:border-cyan-500"

	function add_clause(row: FilterRow, c?: { attr: string; op: FilterOp; value: FilterValue }) {
		const attr = t.input({
			class: F_IN + " w-40",
			placeholder: "atribut",
			list: dl_3d_id, // aceeași listă de coloane ca la 3D
			value: c?.attr ?? "",
		}) as HTMLInputElement
		const op = t.select(
			{ class: F_IN + " w-24" },
			...FILTER_OPS.map((o) => t.option({ value: o }, FILTER_OP_SYMBOLS[o])),
		) as HTMLSelectElement
		op.value = c?.op ?? "eq"
		const value = t.input({
			class: F_IN + " w-40",
			placeholder: "valoare",
			value: c && c.value !== null ? String(c.value) : "",
		}) as HTMLInputElement
		const kind = t.select(
			{ class: F_IN + " w-28", title: "Cum se interpretează valoarea" },
			t.option({ value: "auto" }, "auto"),
			t.option({ value: "text" }, "text"),
			t.option({ value: "number" }, "număr"),
			t.option({ value: "bool" }, "da / nu"),
			t.option({ value: "null" }, "gol"),
		) as HTMLSelectElement
		kind.value = c ? clause_kind_of(c.value) : "auto"
		const el = t.div(
			{ class: "flex gap-2 items-center flex-wrap pl-4" },
			attr,
			op,
			value,
			kind,
			t.button(
				{
					type: "button",
					class: "text-slate-400 dark:text-slate-500 hover:text-red-600 cursor-pointer text-sm",
					title: "Șterge condiția",
					onclick: () => {
						el.remove()
						const i = row.clauses.findIndex((x) => x.el === el)
						if (i >= 0) {
							row.clauses.splice(i, 1)
						}
					},
				},
				"✕",
			),
		) as HTMLElement
		row.clauses.push({ el, attr, op, value, kind })
		row.clauses_box.appendChild(el)
	}
	function add_filter(f?: SavedFilter) {
		const name = t.input({
			class: F_IN + " w-56",
			placeholder: "Nume (ex. Parcele peste 500 m²)",
			value: f?.name ?? "",
			maxlength: "80",
		}) as HTMLInputElement
		const slug = t.input({
			class: F_IN + " w-40 font-mono text-xs",
			placeholder: "identificator",
			value: f?.slug ?? "",
			maxlength: "40",
		}) as HTMLInputElement
		const clauses_box = t.div({ class: "flex flex-col gap-1.5" }) as HTMLElement
		const row: FilterRow = {
			el: t.div() as HTMLElement,
			name,
			slug,
			slug_auto: !f,
			clauses: [],
			clauses_box,
		}
		// Slug-ul se derivă din nume cât timp nu a fost editat manual (aceeași regulă ca la straturi).
		name.addEventListener("input", () => {
			if (row.slug_auto) {
				slug.value = slug_key(name.value).slice(0, 40)
			}
		})
		slug.addEventListener("input", () => {
			row.slug_auto = slug.value.trim() === ""
		})
		row.el = t.div(
			{
				class:
					"rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-2.5 flex flex-col gap-1.5",
			},
			t.div(
				{ class: "flex gap-2 items-center flex-wrap" },
				name,
				slug,
				t.button(
					{
						type: "button",
						class:
							"text-xs text-cyan-700 dark:text-cyan-300 font-semibold hover:underline cursor-pointer",
						onclick: () => add_clause(row),
					},
					"+ condiție",
				),
				t.button(
					{
						type: "button",
						class:
							"text-slate-400 dark:text-slate-500 hover:text-red-600 cursor-pointer text-sm ml-auto",
						title: "Șterge filtrul",
						onclick: () => {
							row.el.remove()
							const i = filter_rows.findIndex((x) => x === row)
							if (i >= 0) {
								filter_rows.splice(i, 1)
							}
							filters_count.val = filter_rows.length
						},
					},
					"✕",
				),
			),
			clauses_box,
		) as HTMLElement
		for (const c of f?.filter ?? []) {
			add_clause(row, c)
		}
		if (!f) {
			add_clause(row)
		}
		filter_rows.push(row)
		filters_box.appendChild(row.el)
		filters_count.val = filter_rows.length
	}
	/** Forma brută din editor (se validează cu lib/filters înainte de trimitere); eroarea are poziția. */
	function filters_from_rows(): Validation<unknown[]> {
		const out: unknown[] = []
		for (const [i, r] of filter_rows.entries()) {
			const filter: unknown[] = []
			for (const [j, c] of r.clauses.entries()) {
				const v = clause_value(c.value.value, c.kind.value as ValueKind, c.op.value)
				if (!v.ok) {
					return validation_error(`filtrul #${i + 1}, clauza #${j + 1}: ${v.error}`)
				}
				filter.push({ attr: c.attr.value.trim(), op: c.op.value, value: v.value })
			}
			out.push({ slug: r.slug.value.trim(), name: r.name.value.trim(), filter })
		}
		return { ok: true, value: out }
	}
	for (const f of layer.saved_filters ?? []) {
		add_filter(f)
	}
	const filters_initial = JSON.stringify(layer.saved_filters ?? [])
	const filters_block = t.div(
		{ class: "flex flex-col gap-2" },
		t.button(
			{
				type: "button",
				class:
					"self-start text-xs text-cyan-700 dark:text-cyan-300 font-semibold hover:underline cursor-pointer",
				onclick: () => {
					filters_open.val = !filters_open.val
				},
			},
			() =>
				`${filters_open.val ? "▾" : "▸"} filtre salvate (straturi derivate)` +
				(filters_count.val > 0 ? ` (${filters_count.val})` : ""),
		),
		t.div(
			{ class: () => "flex flex-col gap-2 " + (filters_open.val ? "" : "hidden") },
			t.p(
				{ class: "text-[11px] text-slate-400 dark:text-slate-500 max-w-2xl" },
				"Un filtru salvat apare pe hartă ca un strat derivat (ex. „Parcele peste 500 m²”). Condițiile se leagă prin ȘI. " +
					"Textul se compară exact („conține” nu ține cont de majuscule); „număr” compară numeric; „gol” = atribut lipsă. " +
					"Pentru date folosește formatul AAAA-LL-ZZ.",
			),
			filters_box,
			t.button(
				{ type: "button", class: BTN_SM_SECONDARY + " self-start", onclick: () => add_filter() },
				"+ Filtru nou",
			),
			t.div(
				{
					class: () =>
						"text-xs text-red-600 dark:text-red-400 " + (filters_err.val ? "" : "hidden"),
				},
				() => filters_err.val,
			),
		),
	)

	async function on_submit(e: Event) {
		e.preventDefault()
		style_err.val = schema_err.val = meta_err.val = filters_err.val = ""

		const parsed_schema = parse_json_field(ta_schema.value)
		if (parsed_schema.error) {
			schema_err.val = parsed_schema.error
			return
		}
		// Listele de valori (1.6) intră în schemă și totul se validează local, ca pe server.
		// Baza = JSON-ul din textarea dacă există; altfel schema REALĂ a stratului (textarea-ul
		// pornește gol) — altfel editorul de valori nu ajungea niciodată pe server. Se trimite
		// ÎNTREAGA schemă, și doar dacă s-a schimbat față de cea citită.
		const has_text_schema = parsed_schema.value !== undefined
		if (has_text_schema || values_rows.length > 0) {
			const base = has_text_schema ? parsed_schema.value : schema_current
			if (typeof base !== "object" || base === null || Array.isArray(base)) {
				schema_err.val = "attribute_schema trebuie să fie un obiect {cheie: tip | {type, values}}"
				return
			}
			const merged = apply_values({ ...(base as Record<string, unknown>) })
			const vs = validate_attribute_schema(merged)
			if (!vs.ok) {
				schema_err.val = vs.error
				values_open.val = true
				return
			}
			if (has_text_schema || JSON.stringify(merged) !== schema_initial) {
				parsed_schema.value = vs.value
			}
		}
		// Filtrele salvate (2.2) — doar dacă s-au schimbat; validate local înainte de PATCH.
		let saved_filters_payload: SavedFilter[] | null | undefined
		{
			const raw = filters_from_rows()
			if (!raw.ok) {
				filters_err.val = raw.error
				filters_open.val = true
				return
			}
			const vf = validate_saved_filters(raw.value)
			if (!vf.ok) {
				filters_err.val = vf.error
				filters_open.val = true
				return
			}
			if (JSON.stringify(vf.value) !== filters_initial) {
				saved_filters_payload = vf.value.length > 0 ? vf.value : null
			}
		}

		const parsed_style = parse_json_field(ta_style.value)
		if (parsed_style.error) {
			style_err.val = parsed_style.error
			return
		}

		const parsed_meta = parse_json_field(ta_meta.value)
		if (parsed_meta.error) {
			meta_err.val = parsed_meta.error
			return
		}

		const payload: Record<string, unknown> = {}
		const name_val = inp_name.value.trim()
		if (name_val) {
			payload.name = name_val
		}
		if (parsed_schema.value !== undefined) {
			payload.attribute_schema = parsed_schema.value
		}
		if (parsed_style.value !== undefined) {
			payload.style = parsed_style.value
		}
		if (parsed_meta.value !== undefined) {
			payload.metadata = parsed_meta.value
		}
		// Rolul se trimite doar dacă s-a SCHIMBAT (null = scoate rolul) — altfel nu atingem config-ul.
		const role_now = sel_role.value
		if (role_now !== (get_layer().role ?? "")) {
			payload.role = role_now || null
		}
		if (sel_904.value !== (get_layer().ordin904_class ?? "")) {
			payload.ordin904_class = sel_904.value || null
		}
		// Doar dacă lista de șabloane a apucat să se încarce: altfel „nimic bifat" ar fi golit
		// configurația stratului la o salvare făcută prea repede.
		if (templates_loaded) {
			const tpl = checked_templates()
			// comparație pe MULȚIME: ordinea salvată pe vremea textului liber poate diferi de a listei,
			// iar o salvare fără nicio schimbare nu trebuie să scrie o versiune nouă a stratului
			const cur = get_layer().allowed_templates ?? []
			if (tpl.toSorted().join(",") !== cur.toSorted().join(",")) {
				payload.allowed_templates = tpl.length > 0 ? tpl : null
			}
		}
		// Consultarea publică — la fel, doar diferențele (F6-T6).
		if (inp_consultare.checked !== (get_layer().consultare === true)) {
			payload.consultare = inp_consultare.checked
		}
		if (inp_consultare_term.value !== (get_layer().consultare_pana_la ?? "")) {
			payload.consultare_pana_la = inp_consultare_term.value || null
		}
		// Starea implicită — tot diferențial; bifat = se șterge cheia (revine la implicitul „aprins").
		if (inp_visible_default.checked !== (get_layer().visible_default !== false)) {
			payload.visible_default = inp_visible_default.checked ? null : false
		}
		// Drepturile pe strat/atribut — doar după ce valoarea curentă s-a încărcat (altfel „totul pe
		// implicit" ar șterge o configurație existentă la o salvare făcută prea repede), doar dacă s-au
		// schimbat, validate local cu aceeași funcție ca serverul.
		access_err.val = ""
		if (access_loaded && can_configure_access()) {
			const va = validate_layer_access({
				viewer: sel_access_viewer.value as ViewerLevel,
				editor: sel_access_editor.value as EditorLevel,
				hidden_attributes: { viewer: [...hidden_viewer], editor: [...hidden_editor] },
			})
			if (!va.ok) {
				access_err.val = va.error
				return
			}
			const next = va.value ? minimal_layer_access(va.value) : null
			if (JSON.stringify(next) !== access_initial) {
				payload.access = next
			}
		}
		// Stilul 3D — doar dacă s-a schimbat ceva în controale; gol = null (scoate config-ul).
		const s3d_next = style3d_from_controls()
		if (JSON.stringify(s3d_next) !== style3d_initial) {
			payload.style3d = Object.keys(s3d_next).length > 0 ? s3d_next : null
		}
		if (saved_filters_payload !== undefined) {
			payload.saved_filters = saved_filters_payload
		}

		submitting.val = true
		const ok = await patch_layer(get_map_slug(), get_layer().slug, payload)
		submitting.val = false
		if (ok) {
			store.edit_layer_slug = null
		}
	}

	return t.div(
		{
			class:
				"bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-700 px-5 py-4",
		},
		t.h3(
			{ class: "text-xs font-bold uppercase tracking-wider text-cyan-700 dark:text-cyan-300 mb-4" },
			() => `Editare strat: ${get_layer().slug}`,
		),
		t.form(
			{ onsubmit: on_submit, class: "flex flex-col gap-3" },
			Field("Nume", inp_name),
			Field("Rol urbanism (regim on-click / derogări PUZ)", sel_role),
			Field("Clasă Ordin 904/2023 (conformitate MDLPA)", sel_904),
			Field("Acte permise pe acest strat (butoanele „Generează” din panoul hărții)", templates_box),
			Field("Vizibilitate la deschiderea hărții", visible_default_row),
			// Drepturi pe strat/atribut pe rol — doar pentru cine le poate scrie (owner/admin/super).
			t.div(
				{ style: () => (can_configure_access() ? "" : "display:none") },
				Field(
					"Drepturi pe rol (vizualizatori / editori) și atribute ascunse",
					access_block,
					access_err,
				),
			),
			// Gating pe contract (pex_consultare / model_3d): secțiunile negate de contractul
			// org-ului nu se oferă în editor (serverul rămâne poarta reală).
			t.div(
				{
					style: () =>
						session_org_feature(store.map_detail?.org_id ?? "", "pex_consultare")
							? ""
							: "display:none",
				},
				Field("Consultare publică PUG (banner + observații în portal)", consultare_row),
			),
			t.div(
				{
					style: () =>
						session_org_feature(store.map_detail?.org_id ?? "", "model_3d") ? "" : "display:none",
				},
				Field("Extrudare 3D — sursa înălțimii", style3d_rows),
			),
			// Iconiță (1.4) — doar straturile punctuale (Point / MultiPoint).
			t.div(
				{ class: is_point_layer ? "" : "hidden" },
				Field("Iconița punctelor (PNG/SVG)", icon_row),
			),
			Field("Attribute Schema (JSON)", ta_schema, schema_err),
			values_block,
			filters_block,
			Field("Stil (JSON)", ta_style, style_err),
			Field("Metadate (JSON)", ta_meta, meta_err),
			t.div(
				{ class: "flex items-center gap-2 pt-1" },
				t.button({ type: "submit", class: BTN_SM_PRIMARY, disabled: () => submitting.val }, () =>
					submitting.val ? "Se salvează..." : "Salvează",
				),
				t.button(
					{
						type: "button",
						class: BTN_SM_SECONDARY,
						onclick: () => {
							store.edit_layer_slug = null
						},
					},
					"Anulează",
				),
			),
		),
	)
}

// ─────────────────────────────────────────────
// Layers Tab
// ─────────────────────────────────────────────

/**
 * Rând de (re)asignare a hărții la o organizație (owner/admin pe org-ul țintă, sau super).
 * Cine nu poate administra nicio primărie (editor/viewer) vede DOAR numele primăriei curente, ca
 * text: înainte, selectul gol arăta „— neasignată —" pe o hartă asignată și „Asignează" dădea
 * eroare (testare 08.09). Primăria curentă apare în select chiar dacă nu e în lista celor
 * administrabile (opțiune dezactivată) — selectul nu mai minte despre starea reală.
 */
function MapOrgRow(get_map_slug: () => string) {
	const orgs = van.state<Array<{ id: string; name: string }>>([])
	const orgs_loaded = van.state(false)
	manageable_orgs().then((l) => {
		orgs.val = l
		orgs_loaded.val = true
	})
	const sel = t.select({ class: INPUT_CLS + " max-w-xs" })
	const saving = van.state(false)
	const current_org_name = () => {
		const d = store.map_detail
		if (!d?.org_id) {
			return ""
		}
		return (
			orgs.val.find((o) => o.id === d.org_id)?.name ||
			store.maps.find((m) => m.slug === d.slug)?.org_name ||
			"primăria hărții"
		)
	}
	const can_manage = () => orgs_loaded.val && orgs.val.length > 0
	// Citirile (store.map_detail.org_id + orgs.val) stau în derive — fără leak în scope părinte.
	van.derive(() => {
		const current = store.map_detail?.org_id ?? ""
		const known = orgs.val.some((o) => o.id === current)
		sel.replaceChildren(
			t.option({ value: "" }, "— neasignată —"),
			...orgs.val.map((o) => t.option({ value: o.id }, o.name)),
			...(current && !known
				? [
						t.option(
							{ value: current, disabled: true },
							`${current_org_name()} (nu o administrezi)`,
						),
					]
				: []),
		)
		sel.value = current
	})
	async function save() {
		saving.val = true
		const r = await api_patch(`/api/maps/${get_map_slug()}`, { org_id: sel.value || null })
		saving.val = false
		if (r.ok) {
			show_toast("Organizație actualizată", "ok")
			await load_map_detail(get_map_slug())
			notify_maps_changed()
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}
	return t.div(
		{ class: "flex items-center gap-3 px-1" },
		t.span(
			{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide" },
			"Organizație:",
		),
		// Text simplu pentru cine nu administrează nicio primărie (montat static, comutat prin class).
		t.span(
			{ class: () => (can_manage() ? "hidden" : "text-sm text-slate-700 dark:text-slate-200") },
			() => (orgs_loaded.val ? current_org_name() || "— neasignată —" : "…"),
		),
		t.div(
			{ class: () => (can_manage() ? "flex items-center gap-3" : "hidden") },
			sel,
			t.button({ class: BTN_SM_SECONDARY, onclick: save, disabled: () => saving.val }, () =>
				saving.val ? "Se salvează..." : "Asignează",
			),
		),
	)
}

/** Branding public (F6-T1): URL-ul stemei primăriei — EAV `attributes.stema_url` pe hartă.
 *  Apare în antetul /embed în locul brandului platformei. Gol = fără stemă. */
function MapBrandingRow(get_map_slug: () => string) {
	const inp = t.input({
		type: "url",
		class: INPUT_CLS + " max-w-md",
		placeholder: "https://primaria.ro/stema.png (gol = brandul platformei)",
	}) as HTMLInputElement
	const saving = van.state(false)
	van.derive(() => {
		const attrs = store.map_detail?.attributes as Record<string, unknown> | undefined
		inp.value = typeof attrs?.stema_url === "string" ? attrs.stema_url : ""
	})
	async function save() {
		saving.val = true
		const r = await api_patch(`/api/maps/${get_map_slug()}`, {
			attributes: { stema_url: inp.value.trim() },
		})
		saving.val = false
		if (r.ok) {
			show_toast("Stema actualizată — apare în harta publică (/embed)", "ok")
			await load_map_detail(get_map_slug())
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}
	return t.div(
		{ class: "flex items-center gap-3 px-1" },
		t.span(
			{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide" },
			"Stema primăriei (public):",
		),
		inp,
		t.button({ class: BTN_SM_SECONDARY, onclick: save, disabled: () => saving.val }, () =>
			saving.val ? "Se salvează..." : "Salvează",
		),
	)
}

/**
 * Datele de EMITENT ale hărții (antetul și semnăturile actelor): EAV pe hartă cu EXACT cheile
 * câmpurilor comune din șabloanele oficiale (COMMON_HEADER/COMMON_FOOTER, scripts/register_templates.ts)
 * — `emitent_judet`, `emitent_autoritate`, `conducator`, `secretar`, `arhitect_sef`. Se moștenesc în
 * contextul de autofill cu precedența cea mai mică, deci un act generat le ia de aici fără ca
 * operatorul să le retasteze. Până la 09.09 se puteau scrie doar prin API / seed_map_meta.ts.
 * Scriere prin PATCH-ul generic de atribute (per cheie; gol = șters din act, rămâne de completat).
 */
const ISSUER_FIELDS: ReadonlyArray<{
	key: string
	label: string
	placeholder: string
	hint?: string
}> = [
	{
		key: "emitent_judet",
		label: "Județul",
		// DOAR numele: șablonul oficial scrie singur „Județul {emitent_judet}" — cu „Județul Vaslui"
		// aici, actul ieșea „Județul Județul Vaslui". Prefixul tastat din obișnuință se scoate la salvare
		// (și pe server, lib/emitent).
		placeholder: "Vaslui",
		hint: "doar numele județului — actul scrie singur „Județul”",
	},
	{
		key: "emitent_autoritate",
		label: "Autoritatea emitentă",
		placeholder: "PRIMĂRIA COMUNEI ȘTEFAN CEL MARE",
	},
	{ key: "conducator", label: "Conducătorul (funcție, nume)", placeholder: "Primar Ion Popescu" },
	{ key: "secretar", label: "Secretarul general", placeholder: "Secretar general Maria Ionescu" },
	{
		key: "arhitect_sef",
		label: "Arhitect-șef",
		placeholder: "Arhitect-șef Andrei Pop (gol dacă nu există)",
	},
]
function MapIssuerSection(get_map_slug: () => string) {
	const inputs = new Map<string, HTMLInputElement>()
	const saving = van.state(false)
	const rows = ISSUER_FIELDS.map((f) => {
		const inp = t.input({
			type: "text",
			class: INPUT_CLS + " w-full",
			placeholder: f.placeholder,
			maxlength: "200",
		}) as HTMLInputElement
		inputs.set(f.key, inp)
		return t.label(
			{ class: "flex flex-col gap-1" },
			t.span({ class: "text-[11px] text-slate-500 dark:text-slate-400" }, f.label),
			inp,
			f.hint
				? t.span({ class: "text-[10px] text-slate-400 dark:text-slate-500" }, f.hint)
				: t.span({ style: "display:none" }),
		)
	})
	// Precompletare din EAV-ul hărții; inputurile rămân NEcontrolate (tastarea nu atinge store-ul).
	van.derive(() => {
		const attrs = store.map_detail?.attributes as Record<string, unknown> | undefined
		for (const f of ISSUER_FIELDS) {
			const v = attrs?.[f.key]
			inputs.get(f.key)!.value = typeof v === "string" ? v : ""
		}
	})
	async function save() {
		const attributes: Record<string, string> = {}
		for (const f of ISSUER_FIELDS) {
			attributes[f.key] = inputs.get(f.key)!.value.trim()
		}
		// Aceeași regulă ca pe server: câmpul arată imediat ce se stochează („Județul Vaslui" → „Vaslui").
		attributes.emitent_judet = judet_name(attributes.emitent_judet ?? "")
		inputs.get("emitent_judet")!.value = attributes.emitent_judet
		saving.val = true
		const r = await api_patch(`/api/maps/${get_map_slug()}`, { attributes })
		saving.val = false
		if (r.ok) {
			show_toast(
				"Datele de emitent salvate — intră automat în antetul și semnăturile actelor",
				"ok",
			)
			await load_map_detail(get_map_slug())
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}
	return t.div(
		{ class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4" },
		t.div(
			{
				class:
					"text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1",
			},
			"Date de emitent (antetul și semnăturile actelor)",
		),
		t.p(
			{ class: "text-[11px] text-slate-500 dark:text-slate-400 mb-3" },
			"Se completează automat în certificatele de urbanism, autorizații și fișe generate pe această hartă; operatorul le poate schimba în formularul actului.",
		),
		t.div({ class: "grid gap-2 sm:grid-cols-2" }, ...rows),
		t.div(
			{ class: "mt-3" },
			t.button({ class: BTN_SM_SECONDARY, onclick: save, disabled: () => saving.val }, () =>
				saving.val ? "Se salvează..." : "Salvează datele de emitent",
			),
		),
	)
}

// UI conform rolului (IT-PORTAL-19): datele+funcțiile se populează după drepturile de acces.
const RANK: Record<string, number> = { owner: 3, admin: 2, editor: 1, viewer: 0 }
/**
 * Poate userul curent SCRIE pe harta selectată? `super` → da; hartă neasignată + logat → da (ca
 * `decide()` în backend); altfel `editor+` pe org-ul hărții. Reactiv (session + map_detail.org_id) →
 * ascunde acțiunile de scriere pentru `viewer`/anonim, lăsând vizualizarea liberă.
 */
function can_edit_map(): boolean {
	const s = session.val
	if (!s.authenticated) {
		return false
	}
	if (s.user?.platform_role === "super") {
		return true
	}
	const org = store.map_detail?.org_id ?? null
	if (org == null) {
		return true // hartă neasignată → orice user autentificat
	}
	const m = s.memberships.find((x) => x.org_id === org)
	return m ? (RANK[m.role] ?? -1) >= RANK.editor : false
}

/** Rând link read-only + buton de copiere (link public / snippet iFrame). */
function LinkRow(label: string, value: string) {
	const inp = t.input({
		class: INPUT_CLS + " text-xs font-mono",
		readonly: true,
		value,
	}) as HTMLInputElement
	return t.div(
		{ class: "flex flex-col gap-1" },
		t.span({ class: "text-xs font-semibold text-slate-500 dark:text-slate-400" }, label),
		t.div(
			{ class: "flex gap-2" },
			inp,
			t.button(
				{
					class: BTN_SM_SECONDARY,
					onclick: async () => {
						inp.select()
						try {
							await navigator.clipboard.writeText(value)
							show_toast("Copiat ✓", "ok")
						} catch {
							show_toast("Copiază manual (Ctrl+C)", "info")
						}
					},
				},
				"Copiază",
			),
		),
	)
}

/**
 * Secțiune PUBLICARE (portal public, IT-PORTAL-29): alege straturile expuse public, generează linkul
 * unic (`/embed?k=<token>`), oferă snippet iFrame + depublicare. Documentele/istoricul NU se expun.
 */

// ── Conformitate Ordin 904/2023 (F8) — raportul validatorului + exportul oficial ──
function ConformitySection(get_map_slug: () => string) {
	interface Problem {
		severity: string
		message: string
		count?: number
	}
	interface ClassReport {
		class: string
		label: string
		required: boolean
		layer_slug: string
		status: string
		problems: Problem[]
	}
	interface Report {
		ok_for_export: boolean
		classes: ClassReport[]
		unmapped_layers: Array<{ slug: string; suggestion: string; certain: boolean }>
	}
	const report = van.state<Report | null>(null)
	const loading = van.state(false)
	const applying = van.state(false)

	async function load() {
		loading.val = true
		const r = await api_get<Report>(`/api/maps/${encodeURIComponent(get_map_slug())}/conformitate`)
		report.val = r.ok ? r.data : null
		loading.val = false
	}

	/**
	 * Mapările SIGURE (potrivire pe numele oficial al clasei, `certain`) care se pot aplica automat:
	 * o clasă cu doi candidați siguri ar deveni „dublură" (blocant) — se lasă omului.
	 */
	function applicable_suggestions(r: Report): Array<{ slug: string; suggestion: string }> {
		const certain = r.unmapped_layers.filter((u) => u.certain && u.suggestion)
		const count = new Map<string, number>()
		for (const u of certain) {
			count.set(u.suggestion, (count.get(u.suggestion) ?? 0) + 1)
		}
		const already = new Set(r.classes.filter((c) => c.layer_slug).map((c) => c.class))
		return certain.filter((u) => count.get(u.suggestion) === 1 && !already.has(u.suggestion))
	}

	async function apply_suggestions() {
		const r = report.val
		if (!r || applying.val) {
			return
		}
		const list = applicable_suggestions(r)
		if (list.length === 0) {
			return
		}
		const ok = confirm(
			`Setez clasa Ordin 904 pe ${list.length} strat(uri), după numele oficial:\n\n` +
				list.map((u) => `${u.slug} → ${u.suggestion}`).join("\n") +
				"\n\nPoți schimba oricând maparea din formularul stratului.",
		)
		if (!ok) {
			return
		}
		applying.val = true
		let done = 0
		let failed = ""
		for (const u of list) {
			const res = await api_patch<LayerSummary>(
				`/api/maps/${encodeURIComponent(get_map_slug())}/layers/${encodeURIComponent(u.slug)}`,
				{ ordin904_class: u.suggestion },
			)
			if (res.ok) {
				done++
			} else {
				failed = res.err?.message ?? "eroare"
				break
			}
		}
		applying.val = false
		if (failed) {
			show_toast(`Mapate ${done}/${list.length}; oprit la eroare: ${failed}`, "err")
		} else {
			show_toast(`Clasa Ordin 904 setată pe ${done} strat(uri)`, "ok")
		}
		await load_map_detail(get_map_slug())
		await load()
	}

	const body = t.div()
	van.derive(() => {
		const r = report.val
		if (loading.val) {
			body.replaceChildren(
				t.p({ class: "text-sm text-slate-400 dark:text-slate-500" }, "Se validează..."),
			)
			return
		}
		if (!r) {
			body.replaceChildren(
				t.button({ class: BTN_SM_SECONDARY, onclick: load }, "Rulează validarea"),
			)
			return
		}
		const rows = r.classes.map((c) => {
			const has_blocker = c.problems.some((p) => p.severity === "blocant") || c.status === "lipsa"
			const color = has_blocker
				? "text-red-600 dark:text-red-400"
				: c.problems.length > 0
					? "text-amber-600 dark:text-amber-400"
					: "text-emerald-600 dark:text-emerald-400"
			return t.div(
				{ class: "text-xs py-0.5" },
				t.span({ class: color + " font-bold mr-1.5" }, "●"),
				t.span({ class: "font-mono font-semibold" }, c.class),
				t.span(
					{ class: "text-slate-400 dark:text-slate-500" },
					c.layer_slug ? ` ← ${c.layer_slug}` : " (nemapat)",
				),
				...c.problems.map((p) =>
					t.div(
						{
							class:
								"pl-5 text-[11px] " +
								(p.severity === "blocant"
									? "text-red-600 dark:text-red-400"
									: "text-amber-600 dark:text-amber-400"),
						},
						`${p.severity === "blocant" ? "✕" : "⚠"} ${p.message}${p.count ? ` (${p.count})` : ""}`,
					),
				),
			)
		})
		const applicable = applicable_suggestions(r)
		const applicable_slugs = new Set(applicable.map((u) => u.slug))
		// Indiciile rămase pentru om: euristici (nesigure) + potriviri sigure dar ambigue (2 candidați).
		const sugg = r.unmapped_layers.filter((u) => u.suggestion && !applicable_slugs.has(u.slug))
		body.replaceChildren(
			t.div(
				{ class: "flex items-center gap-3 mb-2 flex-wrap" },
				r.ok_for_export
					? t.span(
							{ class: "text-sm font-bold text-emerald-600 dark:text-emerald-400" },
							"✓ Exportabil conform Ordin 904",
						)
					: t.span(
							{ class: "text-sm font-bold text-red-600 dark:text-red-400" },
							"✕ Probleme blocante — exportul e refuzat",
						),
				t.button({ class: BTN_SM_SECONDARY, onclick: load }, "Re-validează"),
				r.ok_for_export
					? t.a(
							{
								class: BTN_SM_PRIMARY,
								href: `/api/maps/${encodeURIComponent(get_map_slug())}/export/904`,
							},
							"Descarcă GPKG 904 (Stereo 70)",
						)
					: t.span({ style: "display:none" }),
			),
			t.div({ class: "flex flex-col" }, ...rows),
			applicable.length
				? t.div(
						{ class: "mt-2 flex items-center gap-2 flex-wrap text-[11px]" },
						t.button(
							{
								class: BTN_SM_PRIMARY,
								disabled: () => applying.val,
								onclick: apply_suggestions,
							},
							() =>
								applying.val
									? "Se aplică…"
									: `Aplică ${applicable.length} mapări după numele oficial`,
						),
						t.span(
							{ class: "text-slate-500 dark:text-slate-400" },
							applicable.map((u) => `${u.slug} → ${u.suggestion}`).join("; "),
						),
					)
				: t.span({ style: "display:none" }),
			sugg.length
				? t.div(
						{ class: "mt-2 text-[11px] text-slate-500 dark:text-slate-400" },
						"Sugestii de mapare (de confirmat în formularul stratului): " +
							sugg.map((u) => `${u.slug} → ${u.suggestion}`).join("; "),
					)
				: t.span({ style: "display:none" }),
		)
	})

	return t.div(
		{
			class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4",
			// Faza 2: pe contract (validator_904, ON implicit în TOATE tier-urile — off doar prin
			// override explicit); serverul rămâne poarta reală (403 pe rute).
			style: () =>
				session_org_feature(store.map_detail?.org_id ?? "", "validator_904") ? "" : "display:none",
		},
		t.div(
			{
				class:
					"text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2",
			},
			"Conformitate Ordin 904/2023 (livrare MDLPA)",
		),
		body,
	)
}

// ── Verificare topologică pe strat (decizia 4.5) — raportul topologic per strat (server: conformity.ts) ──

/** Celulă CSV: ghilimele doar când e nevoie (separator, ghilimele, linie nouă). */
function csv_cell(v: string | number): string {
	const s = String(v)
	return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Descarcă un CSV generat în browser (BOM + separator „;" — Excel cu setări RO îl deschide direct). */
function download_csv(filename: string, headers: string[], rows: Array<Array<string | number>>) {
	const csv = "\uFEFF" + [headers, ...rows].map((r) => r.map(csv_cell).join(";")).join("\r\n")
	const a = document.createElement("a")
	a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }))
	a.download = filename
	document.body.appendChild(a)
	a.click()
	a.remove()
	setTimeout(() => URL.revokeObjectURL(a.href), 60_000)
}

/** Număr pentru celula CSV: virgulă zecimală, fără separator de mii (Excel RO îl citește ca număr). */
function csv_num(n: number | undefined): string {
	return n === undefined
		? ""
		: n.toLocaleString("ro-RO", { maximumFractionDigits: 6, useGrouping: false })
}

function plural_ro(n: number, one: string, many: string): string {
	return `${n} ${n === 1 ? one : many}`
}

/**
 * Erori/avertismente pentru verdict. `summary` are TOTALURILE reale per fel; severitatea unei
 * suprapuneri (sliver = avertisment) se știe doar pe constatările LISTATE → suprapunerile nelistate
 * (peste plafon) se numără ca erori — aceeași prudență ca `report_clean` pe server, deci verdictul
 * (`clean`) și numărătoarea nu se contrazic: clean ⇔ 0 erori.
 */
function topology_counts(r: TopologyReport): { errors: number; warnings: number } {
	const slivers = r.findings.filter(
		(f) => f.kind === "overlap" && f.severity === "avertisment",
	).length
	return {
		errors: r.summary.invalid + r.summary.duplicate + Math.max(0, r.summary.overlap - slivers),
		warnings: r.summary.gap + slivers,
	}
}

/** Aria unei constatări: sub 10 m² cu 2 zecimale (sliver-ele sunt sub 1 m² — „0 m²" ar ascunde
 *  exact ce trebuie văzut), altfel scara urbanistică din geodesy (m² / ha / km²). */
function fmt_topology_area(m2: number | undefined): string {
	if (m2 === undefined) {
		return "—"
	}
	if (m2 < 10) {
		return `${m2.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²`
	}
	return format_area(m2)
}

/**
 * Un strat la alegere → `GET /api/maps/:map/layers/:slug/topologie` → verdict (`clean`), note
 * (obiecte analizate, limita folosită la goluri, analizele nerulate, plafonul) + tabelul
 * constatărilor cu deep-link-uri pe hartă și export CSV. Client-only peste raport. Sub același gate
 * de contract ca validatorul 904: secțiunea se ascunde DOAR când flag-ul e ȘTIUT oprit — serverul
 * rămâne poarta reală (403 cu mesajul de contract → toast).
 */
function TopologySection(get_map_slug: () => string) {
	const report = van.state<TopologyReport>(NIL_TOPOLOGY)
	const loaded = van.state(false)
	const loading = van.state(false)
	const layer_count = van.state(0)

	function reset() {
		loaded.val = false
		report.val = NIL_TOPOLOGY
	}

	// Selectul de straturi = DOM necontrolat (focus-safe); opțiunile se re-populează din
	// store.map_detail.layers. Straturile VIRTUALE (acte/solicitari/zonificare_actuala) n-au
	// geometrii proprii de analizat — nu apar. Schimbarea stratului aruncă raportul vechi.
	const sel = t.select({ class: SELECT_CLS + " sm:max-w-xs", onchange: reset }) as HTMLSelectElement
	let last = ""
	van.derive(() => {
		const slug = store.map_detail?.slug ?? ""
		const layers = (store.map_detail?.layers ?? []).filter(
			(l) => !VIRTUAL_LAYER_SLUGS.includes(l.slug),
		)
		const prev = sel.value
		sel.replaceChildren(
			...layers.map((l) => t.option({ value: l.slug }, l.name ? `${l.name} (${l.slug})` : l.slug)),
		)
		layer_count.val = layers.length
		if (slug && slug !== last) {
			last = slug
			reset()
		} else if (layers.some((l) => l.slug === prev)) {
			sel.value = prev // re-încărcarea aceleiași hărți (editare de strat) nu schimbă alegerea
		} else if (prev) {
			reset() // stratul ales a dispărut → raportul lui nu mai are obiect
		}
	})

	async function run() {
		const map = get_map_slug()
		const layer = sel.value
		if (!map || !layer) {
			show_toast("Alege un strat.", "err")
			return
		}
		loading.val = true
		const r = await api_get<TopologyReport>(
			`/api/maps/${encodeURIComponent(map)}/layers/${encodeURIComponent(layer)}/topologie`,
		)
		loading.val = false
		if (map !== get_map_slug() || layer !== sel.value) {
			return // între timp s-a schimbat harta/stratul — raportul nu mai e al selecției curente
		}
		if (!r.ok) {
			show_toast(r.err?.message ?? "Verificarea topologică a eșuat.", "err", 7000)
			return
		}
		report.val = r.data
		loaded.val = true
	}

	// Blocul de rezultat: montat STATIC, comutat prin class; conținutul se umple din derive.
	const verdict = t.div({ class: "text-sm font-bold" })
	const notes = t.div({
		class: "mt-1 flex flex-col gap-0.5 text-[11px] text-slate-500 dark:text-slate-400",
	})
	const tbody = t.tbody()
	const TH =
		"text-left px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400"
	const TD = "px-3 py-1.5 align-top text-xs"
	const LINK = "text-cyan-700 dark:text-cyan-300 hover:underline whitespace-nowrap"

	function objects_cell(f: TopologyFinding, map: string): HTMLElement {
		if (f.feature_ids.length > 0) {
			return t.span(
				{ class: "flex flex-wrap gap-x-2" },
				...f.feature_ids.map((id, i) =>
					t.a(
						{
							class: LINK,
							target: "_blank",
							title: id,
							href: `#/map?map=${encodeURIComponent(map)}&feature=${encodeURIComponent(id)}`,
						},
						`obiect ${i + 1}`,
					),
				),
			)
		}
		if (f.location) {
			return t.a(
				{
					class: LINK,
					target: "_blank",
					href: `#/map?map=${encodeURIComponent(map)}&lng=${f.location[0]}&lat=${f.location[1]}`,
				},
				"vezi pe hartă",
			)
		}
		return t.span({ class: "text-slate-400" }, "—")
	}

	van.derive(() => {
		const r = report.val
		const { errors, warnings } = topology_counts(r)
		const warn_txt = plural_ro(warnings, "avertisment", "avertismente")
		verdict.className =
			"text-sm font-bold " +
			(r.clean ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")
		verdict.textContent = r.clean
			? `✅ Fără erori topologice pe ce s-a analizat${warnings > 0 ? ` · ${warn_txt}` : ""}`
			: `❌ ${plural_ro(errors, "eroare", "erori")}, ${warn_txt}`
		notes.replaceChildren(
			t.div(
				`${plural_ro(r.features, "obiect cu geometrie analizat", "obiecte cu geometrie analizate")}` +
					(r.geometry_type ? ` · tip declarat: ${r.geometry_type}` : ""),
			),
			t.div(
				r.boundary_layer
					? `Goluri față de: ${r.boundary_layer}`
					: "Goluri: harta nu are strat de limită administrativă",
			),
			...r.skipped.map((s) =>
				t.div(
					{ class: "text-amber-600 dark:text-amber-400" },
					`Analiză nerulată: ${TOPOLOGY_LABELS_RO[s.kind]} — ${s.reason}`,
				),
			),
			t.div(
				{ class: r.truncated ? "" : "hidden" },
				`Sunt afișate primele ${r.findings.length} din ${r.summary.total} constatări (plafon ${TOPOLOGY_MAX_FINDINGS}); totalurile de mai sus sunt cele reale.`,
			),
		)
		tbody.replaceChildren(
			...r.findings.map((f) =>
				t.tr(
					{ class: "border-b border-slate-100 dark:border-slate-800" },
					t.td({ class: TD }, TOPOLOGY_LABELS_RO[f.kind]),
					t.td(
						{ class: TD },
						t.span(
							{
								class:
									"inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold " +
									(f.severity === "eroare"
										? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
										: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"),
							},
							f.severity,
						),
					),
					t.td({ class: TD + " text-right whitespace-nowrap" }, fmt_topology_area(f.area_m2)),
					t.td({ class: TD }, objects_cell(f, r.map_slug)),
					t.td({ class: TD + " text-slate-500 dark:text-slate-400" }, f.reason || "—"),
				),
			),
		)
	})

	const CSV_HEADERS = [
		"Fel",
		"Severitate",
		"Arie (m²)",
		"Obiect 1",
		"Obiect 2",
		"Longitudine",
		"Latitudine",
		"Motiv",
	]
	function csv() {
		const r = report.val
		download_csv(
			`topologie_${r.layer_slug || "strat"}.csv`,
			CSV_HEADERS,
			r.findings.map((f) => [
				TOPOLOGY_LABELS_RO[f.kind],
				f.severity,
				csv_num(f.area_m2),
				f.feature_ids[0] ?? "",
				f.feature_ids[1] ?? "",
				csv_num(f.location?.[0]),
				csv_num(f.location?.[1]),
				f.reason ?? "",
			]),
		)
	}

	const result = t.div(
		{ class: () => (loaded.val ? "mt-3" : "hidden") },
		verdict,
		notes,
		t.p(
			{
				class: () =>
					report.val.findings.length === 0
						? "mt-2 text-xs text-slate-400 dark:text-slate-500"
						: "hidden",
			},
			"Nicio constatare de listat.",
		),
		t.div(
			{ class: () => (report.val.findings.length > 0 ? "mt-2" : "hidden") },
			t.div(
				{
					class:
						"overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900",
				},
				t.table(
					{ class: "w-full text-sm" },
					t.thead(
						t.tr(
							{
								class:
									"bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-700",
							},
							t.th({ class: TH }, "Fel"),
							t.th({ class: TH }, "Severitate"),
							t.th({ class: TH + " text-right" }, "Arie"),
							t.th({ class: TH }, "Obiecte"),
							t.th({ class: TH }, "Motiv"),
						),
					),
					tbody,
				),
			),
			t.div(
				{ class: "mt-2" },
				t.button({ class: BTN_SM_SECONDARY, onclick: csv }, "⬇ Descarcă CSV"),
			),
		),
	)

	return t.div(
		{
			class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4",
			// Aceeași poartă ca la conformitate (validator_904 — unealtă de calitate a livrării);
			// serverul rămâne poarta reală (403 pe rută).
			style: () =>
				session_org_feature(store.map_detail?.org_id ?? "", "validator_904") ? "" : "display:none",
		},
		t.div(
			{
				class:
					"text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1",
			},
			"Verificare topologică pe strat",
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400 mb-2" },
			"Caută suprapuneri între poligoanele aceluiași strat, goluri față de limita administrativă, " +
				"geometrii nevalide și obiecte duplicate. Nu modifică nimic în date.",
		),
		t.div(
			{ class: "flex flex-col sm:flex-row sm:items-center gap-2" },
			sel,
			t.button(
				{
					class: BTN_SM_SECONDARY + " shrink-0",
					onclick: run,
					disabled: () => loading.val || layer_count.val === 0,
				},
				() =>
					loading.val
						? "Se analizează… (poate dura zeci de secunde pe straturi mari)"
						: "Verifică topologia",
			),
		),
		result,
	)
}

// ─────────────────────────────────────────────
// „Trimite pe email" (F7-T4 / REQ-URB-022): linkul public al hărții sau al unui embed, trimis de
// operator către un destinatar, prin providerul de mail al instanței. DOM necontrolat (focus-safe).
// ─────────────────────────────────────────────
function ShareMailRow(get_map_slug: () => string, embed_id: string | null) {
	const open = van.state(false)
	const busy = van.state(false)
	const to_input = t.input({
		class: INPUT_CLS + " text-sm",
		type: "email",
		placeholder: "destinatar@exemplu.ro",
	}) as HTMLInputElement
	const msg_input = t.textarea({
		class: INPUT_CLS + " text-sm",
		rows: "2",
		maxlength: "1000",
		placeholder: "Mesaj (opțional)",
	}) as HTMLTextAreaElement

	async function send() {
		const to = to_input.value.trim()
		if (!to) {
			show_toast("Scrie adresa destinatarului.", "err")
			return
		}
		busy.val = true
		const r = await api_post(`/api/maps/${get_map_slug()}/publish/share`, {
			to,
			embed_id,
			message: msg_input.value.trim() || null,
		})
		busy.val = false
		if (r.ok) {
			show_toast("Email trimis", "ok")
			to_input.value = ""
			msg_input.value = ""
			open.val = false
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.div(
		{ class: "flex flex-col gap-2" },
		t.button(
			{
				type: "button",
				class: BTN_SM_SECONDARY + " self-start",
				onclick: () => (open.val = !open.val),
			},
			() => (open.val ? "Ascunde" : "✉ Trimite pe email"),
		),
		t.div(
			{ class: () => (open.val ? "flex flex-col gap-2 pl-1" : "hidden") },
			to_input,
			msg_input,
			t.p(
				{ class: "text-[11px] text-slate-400 dark:text-slate-500" },
				() =>
					`Destinatarul primește linkul și îți va putea răspunde la ${session.val.user?.email ?? "adresa ta"}.`,
			),
			t.div(
				{ class: "flex gap-2" },
				t.button(
					{
						type: "button",
						class: BTN_SM_PRIMARY,
						disabled: () => busy.val,
						onclick: () => void send(),
					},
					() => (busy.val ? "Se trimite…" : "Trimite"),
				),
			),
		),
	)
}

// Semnal partajat publicare→embed-uri: PublishSection și EmbedsSection trăiesc în același tab
// (niciodată re-montat), deci EmbedsSection nu ar afla altfel că harta tocmai a fost (de)publicată.
const publish_version = van.state(0)
// Harta selectată e PUBLICATĂ, dar portalul extern nu e în contractul primăriei (O-C-10): linkul e
// mort. Învelișul secțiunii de publicare o ascunde când modulul e oprit — mai puțin în starea asta,
// ca primăria să VADĂ că are o hartă publicată cu link inactiv și să o poată depublica.
const publish_dead_link = van.state(false)

function PublishSection(get_map_slug: () => string) {
	const published = van.state(false)
	const token = van.state("")
	const mail_on = van.state(false) // instanța trimite emailuri? (butonul „Trimite pe email")
	// Portalul extern e în contractul primăriei? (O-C-10.) Publicată + portal oprit = link MORT: rutele
	// publice răspund „hartă inexistentă", deci secțiunea o spune, nu afișează un link ca și cum ar merge.
	const portal_on = van.state(true)
	const sel = van.state<string[]>([]) // slug-urile straturilor alese pentru public
	const busy = van.state(false)
	// F10: allow-list de atribute per strat public. Slug ABSENT = toate atributele publice (compat retro);
	// slug prezent = doar cheile listate. `keys_cache` = cheile disponibile per strat (din attribute_schema,
	// încărcate lazy). `expanded` = stratul cu panoul de atribute deschis.
	const attrs = van.state<Record<string, string[]>>({})
	const keys_cache = van.state<Record<string, string[]>>({})
	const expanded = van.state("")

	async function refresh(slug: string) {
		if (!slug) {
			return
		}
		const r = await api_get<{
			published: boolean
			token: string
			public_layers: string[]
			public_attributes: Record<string, string[]>
			mail?: boolean
			portal_enabled?: boolean
		}>(`/api/maps/${encodeURIComponent(slug)}/publish`)
		if (r.ok) {
			published.val = r.data.published
			token.val = r.data.token
			mail_on.val = r.data.mail === true
			portal_on.val = r.data.portal_enabled !== false
			publish_dead_link.val = r.data.published && r.data.portal_enabled === false
			sel.val = r.data.public_layers
			attrs.val = r.data.public_attributes ?? {}
		}
	}

	/** Încarcă lazy cheile de atribute ale unui strat. get_layer întoarce schema în `config`. */
	async function load_keys(slug: string) {
		if (keys_cache.val[slug]) {
			return
		}
		const r = await api_get<{ config?: { attribute_schema?: Record<string, unknown> } }>(
			`/api/maps/${encodeURIComponent(get_map_slug())}/layers/${encodeURIComponent(slug)}`,
		)
		const schema = (r.ok ? r.data.config?.attribute_schema : null) ?? {}
		keys_cache.val = { ...keys_cache.val, [slug]: Object.keys(schema) }
	}

	// Un atribut e public dacă stratul n-are restricție (toate publice) SAU cheia e în allow-list.
	const is_key_public = (slug: string, key: string) =>
		attrs.val[slug] === undefined ? true : attrs.val[slug].includes(key)

	function toggle_key(slug: string, key: string, on: boolean) {
		const all = keys_cache.val[slug] ?? []
		const current = new Set(attrs.val[slug] ?? all) // fără restricție → pornim de la toate
		if (on) {
			current.add(key)
		} else {
			current.delete(key)
		}
		attrs.val = { ...attrs.val, [slug]: all.filter((k) => current.has(k)) } // păstrează ordinea
	}
	// Reîncarcă starea de publicare când se schimbă harta selectată.
	let last = ""
	van.derive(() => {
		const slug = store.map_detail?.slug ?? ""
		if (slug && slug !== last) {
			last = slug
			publish_dead_link.val = false // starea era a hărții anterioare; `refresh` o pune pe cea nouă
			void refresh(slug)
		}
	})

	function toggle(slug: string, on: boolean) {
		const s = new Set(sel.val)
		if (on) {
			s.add(slug)
		} else {
			s.delete(slug)
		}
		sel.val = [...s]
	}
	/** Selectează / deselectează TOATE straturile hărții (checkbox-urile se actualizează reactiv). */
	function set_all(on: boolean) {
		sel.val = on ? (store.map_detail?.layers ?? []).map((l) => l.slug) : []
	}

	async function publish() {
		busy.val = true
		// Trimitem allow-list-ul DOAR pentru straturile publice (serverul oricum îl ignoră pe restul).
		const chosen = new Set(sel.val)
		const attributes: Record<string, string[]> = {}
		for (const [slug, keys] of Object.entries(attrs.val)) {
			if (chosen.has(slug)) {
				attributes[slug] = keys
			}
		}
		const r = await api_post<{ token: string }>(`/api/maps/${get_map_slug()}/publish`, {
			layers: sel.val,
			attributes,
		})
		busy.val = false
		if (r.ok) {
			published.val = true
			token.val = r.data.token
			portal_on.val = true // publicarea trece doar cu portalul în contract (gate_publish)
			publish_version.val++ // EmbedsSection reîncarcă starea (formularul se deblochează)
			show_toast("Hartă publicată", "ok")
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}
	async function unpublish() {
		busy.val = true
		const r = await api_delete(`/api/maps/${get_map_slug()}/publish`)
		busy.val = false
		if (r.ok) {
			published.val = false
			token.val = ""
			sel.val = []
			publish_dead_link.val = false
			publish_version.val++
			show_toast("Hartă depublicată", "ok")
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	/** Publicată, dar portalul nu e în contract: linkul e mort; rămân starea, explicația și depublicarea. */
	const dead_link = () => published.val && !portal_on.val
	const share_url = () => `${location.origin}/embed?k=${token.val}`
	const iframe_snippet = () =>
		`<iframe src="${share_url()}" width="100%" height="500" style="border:0" loading="lazy"></iframe>`
	// Adresa de PORNIRE a serviciului OGC API – Features: exact ce cere QGIS la „Add OGC API -
	// Features Layer", de unde clientul descoperă singur colecțiile prin linkurile documentului.
	// Serviciul exista și era testat, dar adresa nu apărea nicăieri: utilizatorul trebuia s-o compună
	// după modelul din ghidul „Integrare & API", deci la o demonstrație funcția părea nelivrată.
	// ⚠️ Documentul de pornire își generează propriile linkuri pe `PUBLIC_ORIGIN` (ogc.ts `base_of`),
	// care poate să difere de originea din bara browserului: adresa copiată merge oricum, iar QGIS
	// urmează mai departe originea configurată. Nu e nimic de aliniat.
	const ogc_url = () => `${location.origin}/ogc?k=${token.val}`

	// Panoul de atribute publice al unui strat (F10): checkbox per cheie, încărcate lazy.
	function attr_panel(slug: string) {
		const keys = keys_cache.val[slug]
		if (keys === undefined) {
			return t.div(
				{ class: "pl-7 text-xs text-slate-400 dark:text-slate-500" },
				"Se încarcă atributele…",
			)
		}
		if (keys.length === 0) {
			return t.div(
				{ class: "pl-7 text-xs text-slate-400 dark:text-slate-500" },
				"Stratul n-are atribute.",
			)
		}
		const restricted = attrs.val[slug] !== undefined
		return t.div(
			{
				class: "pl-7 flex flex-col gap-1 border-l border-slate-200 dark:border-slate-700 ml-2 mt-1",
			},
			t.p(
				{ class: "text-[11px] text-slate-400 dark:text-slate-500" },
				restricted
					? "Doar atributele bifate sunt vizibile public."
					: "Toate atributele sunt publice. Dezbifează pentru a restrânge.",
			),
			...keys.map((key) => {
				const kcb = t.input({
					type: "checkbox",
					onchange: (e: Event) => toggle_key(slug, key, (e.target as HTMLInputElement).checked),
				}) as HTMLInputElement
				kcb.checked = is_key_public(slug, key)
				return t.label(
					{
						class:
							"flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer",
					},
					kcb,
					key,
				)
			}),
		)
	}

	// Checkbox-uri per strat (reactiv la lista de straturi + selecția curentă + atribute F10).
	const layers_box = t.div({ class: "flex flex-col gap-1 pl-1" })
	van.derive(() => {
		const layers = store.map_detail?.layers ?? []
		const chosen = new Set(sel.val)
		// deps reactive pentru panoul de atribute:
		void attrs.val
		void keys_cache.val
		const open = expanded.val
		layers_box.replaceChildren(
			...layers.map((l) => {
				const cb = t.input({
					type: "checkbox",
					onchange: (e: Event) => toggle(l.slug, (e.target as HTMLInputElement).checked),
				}) as HTMLInputElement
				cb.checked = chosen.has(l.slug)
				const row = t.label(
					{
						class:
							"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer",
					},
					cb,
					l.name || l.slug,
				)
				if (!chosen.has(l.slug)) {
					return t.div(row)
				}
				// Strat public → buton de extindere + panou atribute (F10).
				const is_open = open === l.slug
				const restricted = attrs.val[l.slug] !== undefined
				const summary = restricted
					? `atribute: ${attrs.val[l.slug].length} publice`
					: "atribute: toate publice"
				const attr_toggle = t.button(
					{
						type: "button",
						class:
							"text-[11px] text-cyan-700 dark:text-cyan-300 hover:underline cursor-pointer text-left",
						onclick: () => {
							expanded.val = is_open ? "" : l.slug
							if (!is_open) {
								void load_keys(l.slug)
							}
						},
					},
					`${is_open ? "▾" : "▸"} ${summary}`,
				)
				return t.div(
					{ class: "flex flex-col" },
					row,
					t.div({ class: "pl-7" }, attr_toggle),
					is_open ? attr_panel(l.slug) : t.span({ style: "display:none" }),
				)
			}),
		)
	})

	return t.div(
		{ class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-3" },
		t.div(
			{ class: "flex items-center justify-between" },
			t.span(
				{
					class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
				},
				"Portal public (fără cont)",
			),
			() =>
				published.val
					? portal_on.val
						? t.span(
								{ class: "text-xs font-semibold text-emerald-600 dark:text-emerald-400" },
								"● Publicată",
							)
						: t.span(
								{ class: "text-xs font-semibold text-amber-600 dark:text-amber-400" },
								"● Publicată, link inactiv",
							)
					: t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "nepublicată"),
		),
		() =>
			dead_link()
				? t.div(
						{
							class:
								"rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200",
						},
						"Harta e publicată, dar portalul public nu este inclus în contractul primăriei: linkul public și codul de inserare nu se mai deschid (vizitatorii primesc „hartă inexistentă”). O poți depublica de aici.",
					)
				: t.span({ style: "display:none" }),
		// Alegerea straturilor + (re)publicarea: doar cu portalul în contract (altfel POST-ul e refuzat).
		t.div(
			{ class: "flex flex-col gap-3", style: () => (dead_link() ? "display:none" : "") },
			t.p(
				{ class: "text-xs text-slate-500 dark:text-slate-400" },
				"Alege straturile expuse public. Solicitările și istoricul nu se publică niciodată; documentele — doar cele marcate explicit „public” din pagina Documente.",
			),
			() => {
				const n = store.map_detail?.layers?.length ?? 0
				return n > 0
					? t.div(
							{ class: "flex gap-3 text-xs" },
							t.button(
								{
									type: "button",
									class:
										"text-cyan-700 dark:text-cyan-300 font-semibold hover:underline cursor-pointer",
									onclick: () => set_all(true),
								},
								"Selectează toate",
							),
							t.button(
								{
									type: "button",
									class: "text-slate-500 dark:text-slate-400 hover:underline cursor-pointer",
									onclick: () => set_all(false),
								},
								"Deselectează toate",
							),
						)
					: ""
			},
			layers_box,
		),
		t.div(
			{ class: "flex gap-2 items-center" },
			t.button(
				{
					class: BTN_SM_PRIMARY,
					// comutat prin style (nu prin clasa `hidden`, care pierde în fața lui `inline-flex` din BTN_SM)
					style: () => (dead_link() ? "display:none" : ""),
					disabled: () => busy.val,
					onclick: publish,
				},
				() => (published.val ? "Actualizează publicarea" : "Publică harta"),
			),
			() =>
				published.val
					? t.button(
							{ class: BTN_SM_SECONDARY, disabled: () => busy.val, onclick: unpublish },
							"Depublică",
						)
					: t.span({ style: "display:none" }),
		),
		() =>
			published.val && portal_on.val
				? t.div(
						{ class: "flex flex-col gap-2 pt-1" },
						LinkRow("Link public", share_url()),
						LinkRow("Cod de inserare (iFrame)", iframe_snippet()),
						// Gardat pe `interop_ogc` (dreptul contractual al interoperabilității): fără el,
						// `/ogc` răspunde 404 indistinct, deci am oferi o adresă care „nu există".
						// `portal_extern` nu se mai verifică — rândul stă oricum în blocul de după publicare.
						session_org_feature(store.map_detail?.org_id ?? "", "interop_ogc")
							? t.div(
									{ class: "flex flex-col gap-1" },
									LinkRow("OGC API – Features (QGIS)", ogc_url()),
									t.p(
										{ class: "text-xs text-slate-400 dark:text-slate-500" },
										"În QGIS: Layer → Add Layer → „Add OGC API - Features Layer” → lipești adresa. ",
										"Straturile publicate apar în listă.",
									),
								)
							: t.span({ style: "display:none" }),
						mail_on.val ? ShareMailRow(get_map_slug, null) : t.span({ style: "display:none" }),
					)
				: t.span({ style: "display:none" }),
	)
}

// ─────────────────────────────────────────────
// Catalog de embed-uri (F7-T3, REQ-API-002/003/006/016): iFrame-uri NELIMITATE per hartă, fiecare
// cu token propriu, submulțime de straturi (dintre cele DEJA publice), instrumente și expirare,
// revocabile individual. Completează publicarea de mai sus — tokenul de hartă rămâne „embed-ul
// implicit" cu tot conținutul public.
// ─────────────────────────────────────────────
interface EmbedRowView {
	id: string
	name: string
	token: string
	layers: string[]
	tools: Record<string, boolean>
	bbox_lock: [number, number, number, number] | null
	expires_at: string
	created_at: string
}

function EmbedsSection(get_map_slug: () => string) {
	const embeds = van.state<EmbedRowView[]>([])
	const published = van.state(false)
	const mail_on = van.state(false)
	const pub_layers = van.state<string[]>([]) // straturile DEJA publice — oferta formularului
	const busy = van.state(false)
	const form_open = van.state(false)
	const expanded = van.state("") // embed-ul cu link/snippet desfășurate

	// Formularul e DOM necontrolat (focus-safe, ca LegislatieSection): stările NU se citesc reactiv
	// în corpul lui. Selecțiile trăiesc în seturi de modul, bifele sunt input-uri simple.
	const name_input = t.input({
		class: INPUT_CLS,
		placeholder: "ex. Site primărie — pagina PUG",
	}) as HTMLInputElement
	const date_input = t.input({ class: INPUT_CLS + " text-xs", type: "date" }) as HTMLInputElement
	const bbox_cb = t.input({ type: "checkbox" }) as HTMLInputElement
	const layer_sel = new Set<string>()
	const tool_off = new Set<string>()

	async function refresh(slug: string) {
		if (!slug) {
			return
		}
		const pub = await api_get<{ published: boolean; public_layers: string[]; mail?: boolean }>(
			`/api/maps/${encodeURIComponent(slug)}/publish`,
		)
		if (pub.ok) {
			published.val = pub.data.published
			pub_layers.val = pub.data.public_layers
			mail_on.val = pub.data.mail === true
		}
		const r = await api_get<EmbedRowView[]>(`/api/maps/${encodeURIComponent(slug)}/embeds`)
		if (r.ok) {
			embeds.val = r.data
		}
	}
	/** Golește complet starea formularului (bifele-s seturi de MODUL — ar supraviețui altfel
	 *  schimbării hărții și ar planta slug-uri străine în config-ul următorului embed). */
	function reset_form() {
		layer_sel.clear()
		tool_off.clear()
		name_input.value = ""
		date_input.value = ""
		bbox_cb.checked = false
	}
	let last = ""
	van.derive(() => {
		const slug = store.map_detail?.slug ?? ""
		void publish_version.val // dep reactiv: (de)publicarea din PublishSection re-încarcă starea
		if (slug && slug !== last) {
			last = slug
			embeds.val = []
			form_open.val = false
			reset_form()
			void refresh(slug)
		} else if (slug) {
			void refresh(slug)
		}
	})

	async function create() {
		if (layer_sel.size === 0) {
			show_toast("Alege cel puțin un strat.", "err")
			return
		}
		const extent = store.map_detail?.extent ?? null
		if (bbox_cb.checked && !extent) {
			// Alegerea explicită a operatorului nu se aruncă tăcut (harta n-are extent calculabil).
			show_toast("Harta nu are un extent calculabil — debifează blocarea la extent.", "err")
			return
		}
		busy.val = true
		const tools: Record<string, boolean> = {}
		for (const tool of tool_off) {
			tools[tool] = false
		}
		const r = await api_post(`/api/maps/${get_map_slug()}/embeds`, {
			name: name_input.value,
			config: {
				layers: [...layer_sel],
				...(Object.keys(tools).length ? { tools } : {}),
				bbox_lock: bbox_cb.checked && extent ? extent : null,
			},
			...(date_input.value ? { expires_at: `${date_input.value}T23:59:59` } : {}),
		})
		busy.val = false
		if (r.ok) {
			show_toast("Embed creat", "ok")
			reset_form()
			form_open.val = false
			void refresh(get_map_slug())
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	async function revoke(e: EmbedRowView) {
		if (!confirm(`Revoci embed-ul „${e.name}"? Linkul moare imediat, definitiv.`)) {
			return
		}
		const r = await api_delete(`/api/maps/${get_map_slug()}/embeds/${e.id}`)
		if (r.ok) {
			show_toast("Embed revocat", "ok")
			void refresh(get_map_slug())
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	const embed_url = (tok: string) => `${location.origin}/embed?k=${tok}`
	const embed_snippet = (tok: string) =>
		`<iframe src="${embed_url(tok)}" width="100%" height="500" style="border:0" loading="lazy"></iframe>`
	const tools_summary = (tools: Record<string, boolean>) => {
		const off = EMBED_TOOLS.filter((x) => tools[x] === false)
		return off.length === 0
			? "toate instrumentele"
			: `fără: ${off.map((x) => EMBED_TOOL_LABELS[x as EmbedTool].toLowerCase()).join(", ")}`
	}

	// Lista embed-urilor (reactiv; link/snippet desfășurate per rând prin `expanded`).
	const list_box = t.div({ class: "flex flex-col gap-2" })
	van.derive(() => {
		const items = embeds.val
		const open = expanded.val
		list_box.replaceChildren(
			...items.map((e) => {
				const is_open = open === e.id
				return t.div(
					{
						class:
							"rounded-lg border border-slate-200 dark:border-slate-700 p-2 flex flex-col gap-1",
					},
					t.div(
						{ class: "flex items-center justify-between gap-2" },
						t.button(
							{
								type: "button",
								class:
									"text-sm font-semibold text-slate-700 dark:text-slate-200 hover:underline cursor-pointer text-left",
								onclick: () => (expanded.val = is_open ? "" : e.id),
							},
							`${is_open ? "▾" : "▸"} ${e.name || "(fără nume)"}`,
						),
						t.button(
							{ type: "button", class: BTN_SM_SECONDARY, onclick: () => void revoke(e) },
							"Revocă",
						),
					),
					t.div(
						{ class: "text-[11px] text-slate-500 dark:text-slate-400" },
						`${e.layers.length} straturi · ${tools_summary(e.tools)}` +
							(e.expires_at ? ` · expiră ${e.expires_at.slice(0, 10)}` : "") +
							(e.bbox_lock ? " · blocat la extent" : ""),
					),
					is_open
						? t.div(
								{ class: "flex flex-col gap-2 pt-1" },
								LinkRow("Link embed", embed_url(e.token)),
								LinkRow("Cod de inserare (iFrame)", embed_snippet(e.token)),
								mail_on.val ? ShareMailRow(get_map_slug, e.id) : t.span({ style: "display:none" }),
							)
						: t.span({ style: "display:none" }),
				)
			}),
		)
	})

	// Bifele de straturi ale formularului — DOAR dintre straturile publice (intersecția reală).
	const form_layers_box = t.div({ class: "flex flex-col gap-1 pl-1" })
	const layers_tick = van.state(0) // bump → bifele se re-sincronizează cu layer_sel (select/deselect all)
	function set_all_layers(on: boolean) {
		layer_sel.clear()
		if (on) {
			for (const slug of pub_layers.val) {
				layer_sel.add(slug)
			}
		}
		layers_tick.val++
	}
	van.derive(() => {
		const layers = pub_layers.val
		void layers_tick.val
		if (!form_open.val) {
			form_layers_box.replaceChildren()
			return
		}
		form_layers_box.replaceChildren(
			...layers.map((slug) => {
				const cb = t.input({
					type: "checkbox",
					onchange: (e: Event) => {
						if ((e.target as HTMLInputElement).checked) {
							layer_sel.add(slug)
						} else {
							layer_sel.delete(slug)
						}
					},
				}) as HTMLInputElement
				cb.checked = layer_sel.has(slug)
				return t.label(
					{
						class:
							"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer",
					},
					cb,
					slug,
				)
			}),
		)
	})

	// Bifele de instrumente — RE-construite la fiecare deschidere a formularului, sincronizate cu
	// `tool_off` (bifele statice rămâneau vizual debifate după un create, deși modelul era golit —
	// următorul embed ar fi ieșit tăcut cu toate instrumentele pornite).
	const form_tools_box = t.div({ class: "flex flex-col gap-1 pl-1" })
	van.derive(() => {
		if (!form_open.val) {
			form_tools_box.replaceChildren()
			return
		}
		form_tools_box.replaceChildren(
			...EMBED_TOOLS.map((tool) => {
				const cb = t.input({
					type: "checkbox",
					onchange: (e: Event) => {
						if ((e.target as HTMLInputElement).checked) {
							tool_off.delete(tool)
						} else {
							tool_off.add(tool)
						}
					},
				}) as HTMLInputElement
				cb.checked = !tool_off.has(tool)
				return t.label(
					{
						class:
							"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer",
					},
					cb,
					EMBED_TOOL_LABELS[tool as EmbedTool],
				)
			}),
		)
	})

	const form = t.div(
		{ class: "flex flex-col gap-2 rounded-lg border border-slate-200 dark:border-slate-700 p-3" },
		t.label(
			{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400" },
			"Nume (unde va fi înglobat)",
		),
		name_input,
		t.span(
			{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400" },
			"Straturi expuse",
		),
		t.p(
			{ class: "text-[11px] text-slate-400 dark:text-slate-500" },
			"Doar dintre straturile deja publice; straturile sunt referențiate prin identificator.",
		),
		t.div(
			{ class: "flex gap-3 text-xs" },
			t.button(
				{
					type: "button",
					class: "text-cyan-700 dark:text-cyan-300 font-semibold hover:underline cursor-pointer",
					onclick: () => set_all_layers(true),
				},
				"Selectează toate",
			),
			t.button(
				{
					type: "button",
					class: "text-slate-500 dark:text-slate-400 hover:underline cursor-pointer",
					onclick: () => set_all_layers(false),
				},
				"Deselectează toate",
			),
		),
		form_layers_box,
		t.span({ class: "text-xs font-semibold text-slate-500 dark:text-slate-400" }, "Instrumente"),
		form_tools_box,
		t.label(
			{
				class: "flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer",
			},
			bbox_cb,
			"Blochează navigarea la extentul hărții",
		),
		t.label(
			{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400" },
			"Expiră la (opțional)",
		),
		date_input,
		t.div(
			{ class: "flex gap-2" },
			t.button(
				{ class: BTN_SM_PRIMARY, disabled: () => busy.val, onclick: () => void create() },
				"Creează embed",
			),
			t.button({ class: BTN_SM_SECONDARY, onclick: () => (form_open.val = false) }, "Renunță"),
		),
	)

	return t.div(
		{ class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-3" },
		t.span(
			{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide" },
			"Embed-uri configurabile (iFrame)",
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Oricâte iFrame-uri, fiecare cu straturile și instrumentele lui, cu link propriu revocabil individual.",
		),
		() =>
			published.val
				? t.span({ style: "display:none" })
				: t.p(
						{ class: "text-xs text-amber-600 dark:text-amber-400" },
						"Publică întâi harta — embed-urile expun doar straturi deja publice.",
					),
		list_box,
		() =>
			published.val
				? form_open.val
					? form
					: t.button(
							{ class: BTN_SM_SECONDARY + " self-start", onclick: () => (form_open.val = true) },
							"+ Embed nou",
						)
				: t.span({ style: "display:none" }),
	)
}

// ─────────────────────────────────────────────
// „API-uri definibile" (F7-T4, REQ-API-004): linkuri către sisteme EXTERNE cu placeholders
// {lng}/{lat}/{attr:cheie}, completate din elementul selectat pe hartă. Pe hartă (EAV
// `attributes.api_links`, scris prin PATCH-ul generic — clona nomenclatorului legislativ).
// Butoanele apar în panoul elementului (intern + embed); doar http(s), fail-closed în client.
// ─────────────────────────────────────────────
function ApiLinksSection(get_map_slug: () => string) {
	const busy = van.state(false)
	const count = van.state(0) // doar pentru titlu — rândurile sunt DOM necontrolat (focus-safe)
	const IN_CLS =
		"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none focus:border-cyan-500"
	const rows_box = t.div({ class: "flex flex-col gap-2" })
	const row_refs: Array<{
		el: HTMLElement
		label: HTMLInputElement
		url_template: HTMLInputElement
		layers: HTMLInputElement
	}> = []

	function add_row(v: { label?: string; url_template?: string; layers?: string[] } = {}) {
		const label = t.input({
			class: IN_CLS + " w-44",
			placeholder: "Etichetă (ex. Extras ANCPI)",
			value: v.label ?? "",
		}) as HTMLInputElement
		const url_template = t.input({
			class: IN_CLS + " flex-1 min-w-52 font-mono text-xs",
			placeholder: "https://sistem.extern.ro/cauta?x={lng}&y={lat}&nr={attr:nr_cadastral}",
			value: v.url_template ?? "",
		}) as HTMLInputElement
		const layers = t.input({
			class: IN_CLS + " w-44",
			placeholder: "straturi (opțional, cu virgulă)",
			value: (v.layers ?? []).join(", "),
		}) as HTMLInputElement
		const el = t.div(
			{ class: "flex gap-2 items-center flex-wrap" },
			label,
			url_template,
			layers,
			t.button(
				{
					type: "button",
					class: "text-slate-400 dark:text-slate-500 hover:text-red-600 cursor-pointer text-sm",
					title: "Șterge linkul din listă",
					onclick: () => {
						el.remove()
						const i = row_refs.findIndex((r) => r.el === el)
						if (i >= 0) {
							row_refs.splice(i, 1)
						}
						count.val = row_refs.length
					},
				},
				"✕",
			),
		) as HTMLElement
		row_refs.push({ el, label, url_template, layers })
		rows_box.appendChild(el)
		count.val = row_refs.length
	}

	function set_rows(v: unknown) {
		rows_box.replaceChildren()
		row_refs.length = 0
		const list = Array.isArray(v) ? v : []
		for (const item of list) {
			const o = (item ?? {}) as { label?: unknown; url_template?: unknown; layers?: unknown }
			add_row({
				label: String(o.label ?? ""),
				url_template: String(o.url_template ?? ""),
				layers: Array.isArray(o.layers) ? o.layers.map(String) : [],
			})
		}
		count.val = row_refs.length
	}

	let last = ""
	async function refresh(slug: string) {
		const r = await api_get<{ attributes?: Record<string, unknown> }>(
			`/api/maps/${encodeURIComponent(slug)}`,
		)
		if (slug !== last) {
			return // răspuns întârziat pentru altă hartă — altfel save() ar scrie linkurile ei aici
		}
		set_rows(r.ok ? r.data.attributes?.api_links : [])
	}
	van.derive(() => {
		const slug = store.map_detail?.slug ?? ""
		if (slug && slug !== last) {
			last = slug
			void refresh(slug)
		}
	})

	async function save() {
		if (busy.val) {
			return
		}
		const api_links = row_refs
			.map((r) => ({
				label: r.label.value.trim(),
				url_template: r.url_template.value.trim(),
				layers: r.layers.value
					.split(",")
					.map((s) => s.trim())
					.filter((s) => s !== ""),
			}))
			.filter((l) => l.label || l.url_template)
		// Parserul (parse_api_links) cere AMBELE câmpuri — un rând pe jumătate completat s-ar salva
		// „cu succes" dar n-ar apărea nicăieri, fără explicație. Refuz explicit, nu ignorare tăcută.
		const half = api_links.find((l) => !l.label || !l.url_template)
		if (half) {
			show_toast("Fiecare link are nevoie și de etichetă, și de URL-șablon.", "err")
			return
		}
		// Gardă de bun-simț la scriere (fail-closed-ul REAL e la randare, în client + expandare):
		const bad = api_links.find((l) => !/^https?:\/\//i.test(l.url_template))
		if (bad) {
			show_toast(`URL-șablonul „${bad.label}" trebuie să înceapă cu http(s)://`, "err")
			return
		}
		busy.val = true
		const r = await api_patch(`/api/maps/${encodeURIComponent(get_map_slug())}`, {
			attributes: { api_links },
		})
		busy.val = false
		if (r.ok) {
			show_toast("Linkuri API salvate", "ok")
			set_rows(api_links)
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.div(
		{ class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-3" },
		t.div(
			{ class: "flex items-center justify-between" },
			t.span(
				{
					class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
				},
				"Interogare sisteme externe (API-uri definibile)",
			),
			() =>
				count.val > 0
					? t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, `${count.val} linkuri`)
					: t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "gol"),
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Butoane în panoul elementului selectat, către sisteme externe. Placeholders: " +
				"{lng}, {lat}, {attr:cheie} — completate din element. Doar adrese http(s).",
		),
		rows_box,
		t.div(
			{ class: "flex gap-2 items-center" },
			t.button(
				{ type: "button", class: BTN_SM_SECONDARY, onclick: () => add_row() },
				"+ Adaugă link",
			),
			t.button(
				{ type: "button", class: BTN_SM_PRIMARY, disabled: () => busy.val, onclick: save },
				"Salvează",
			),
		),
	)
}

// ─────────────────────────────────────────────
// Nomenclator legislativ (F5-T6, REQ-URB-004): legile/HG-urile care modifică regimurile
// aprobate prin RLU. Listă de referințe pe hartă (`attributes.legislatie`, EAV — fără tabel
// nou, versionată prin update_map). Afișată în panoul de regim din hartă + în CU (context).
// ─────────────────────────────────────────────
/**
 * Semnal către istoricul regulamentului (RegimUtrTab) după o salvare din ALTĂ secțiune a tabului:
 * nomenclatorul legislativ scrie EAV fără versiune, iar lista deja încărcată nu avea de unde să afle
 * (testare 21.09 — actul adăugat nu apărea până la reîncărcarea paginii). Stare reactivă, nu un
 * callback global: nu poate rămâne agățată de o instanță veche a tabului.
 */
const rlu_history_tick = van.state(0)
// Generația tabului montat: derive-ul de mai jos e înregistrat pe un state de MODUL și nu se
// dezînregistrează la demontare (Van nu ține DOM pentru el) — fără gardă, fiecare re-montare a
// consolei adăuga încă un ascultător care cerea istoricul într-un DOM detașat.
let regim_tab_generation = 0

// ─────────────────────────────────────────────
// Textele de EXTRAVILAN din acte (O1, 01.10): când punctul/parcela nu cade în niciun UTR, rubricile
// „regim economic” și „regim tehnic” ale certificatului primesc un text — cel implicit sau cel scris
// aici (atribute de hartă `extravilan_regim_economic` / `extravilan_regim_tehnic`, prin PATCH-ul
// generic). Gol = textul implicit (o cheie EAV nu se poate șterge prin REST).
// ─────────────────────────────────────────────
function ExtravilanSection(get_map_slug: () => string) {
	const busy = van.state(false)
	// Textul implicit e afișat ca exemplu (placeholder): câmpul gol = implicitul.
	const field = (key: string, label: string, fallback: string) => ({
		key,
		label,
		input: t.textarea({
			class: TEXTAREA_CLS.replace(" font-mono", "") + " w-full",
			rows: "3",
			maxlength: "2000",
			placeholder: fallback,
		}) as HTMLTextAreaElement,
	})
	const fields = [
		field(EXTRAVILAN_ECONOMIC_KEY, "Regimul economic (rubrica 2)", EXTRAVILAN_ECONOMIC_DEFAULT),
		field(EXTRAVILAN_TEHNIC_KEY, "Regimul tehnic (rubrica 3)", EXTRAVILAN_TEHNIC_DEFAULT),
	]
	// Precompletare din EAV-ul hărții; câmpurile rămân NEcontrolate (tastarea nu atinge store-ul).
	van.derive(() => {
		const attrs = store.map_detail?.attributes as Record<string, unknown> | undefined
		for (const f of fields) {
			const v = attrs?.[f.key]
			f.input.value = typeof v === "string" ? v : ""
		}
	})
	async function save() {
		if (busy.val) {
			return
		}
		const attributes: Record<string, string> = {}
		for (const f of fields) {
			attributes[f.key] = f.input.value.trim()
		}
		busy.val = true
		const r = await api_patch(`/api/maps/${encodeURIComponent(get_map_slug())}`, { attributes })
		busy.val = false
		if (r.ok) {
			show_toast("Textele de extravilan salvate", "ok")
			await load_map_detail(get_map_slug())
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}
	return t.div(
		{ class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-3" },
		t.span(
			{
				class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
			},
			"Extravilan — textele din act",
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Când terenul nu se află în niciun UTR, certificatul de urbanism primește aceste texte la regimul economic și la cel tehnic. " +
				"Lasă câmpul gol pentru textul implicit (cel afișat cu gri).",
		),
		...fields.map((f) =>
			t.label(
				{ class: "flex flex-col gap-1" },
				t.span({ class: "text-[11px] text-slate-500 dark:text-slate-400" }, f.label),
				f.input,
			),
		),
		t.div(
			t.button(
				{ type: "button", class: BTN_SM_PRIMARY, disabled: () => busy.val, onclick: save },
				"Salvează textele",
			),
		),
	)
}

function LegislatieSection(get_map_slug: () => string) {
	const busy = van.state(false)
	const count = van.state(0) // doar pentru titlu — rândurile sunt DOM necontrolat (focus-safe)
	const IN_CLS =
		"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none focus:border-cyan-500"
	const rows_box = t.div({ class: "flex flex-col gap-2" })
	const row_refs: Array<{
		el: HTMLElement
		act: HTMLInputElement
		descriere: HTMLInputElement
		url: HTMLInputElement
	}> = []

	function add_row(v: { act?: string; descriere?: string; url?: string } = {}) {
		const act = t.input({
			class: IN_CLS + " w-44",
			placeholder: "Act (ex. Legea 350/2001)",
			value: v.act ?? "",
		}) as HTMLInputElement
		const descriere = t.input({
			class: IN_CLS + " flex-1 min-w-40",
			placeholder: "Descriere (ce modifică)",
			value: v.descriere ?? "",
		}) as HTMLInputElement
		const url = t.input({
			class: IN_CLS + " w-52",
			placeholder: "https://legislatie.just.ro/…",
			value: v.url ?? "",
		}) as HTMLInputElement
		const el = t.div(
			{ class: "flex gap-2 items-center flex-wrap" },
			act,
			descriere,
			url,
			t.button(
				{
					type: "button",
					class: "text-slate-400 dark:text-slate-500 hover:text-red-600 cursor-pointer text-sm",
					title: "Șterge actul din listă",
					onclick: () => {
						el.remove()
						const i = row_refs.findIndex((r) => r.el === el)
						if (i >= 0) {
							row_refs.splice(i, 1)
						}
						count.val = row_refs.length
					},
				},
				"✕",
			),
		) as HTMLElement
		row_refs.push({ el, act, descriere, url })
		rows_box.appendChild(el)
		count.val = row_refs.length
	}

	function set_rows(v: unknown) {
		rows_box.replaceChildren()
		row_refs.length = 0
		const list = Array.isArray(v) ? v : []
		for (const item of list) {
			const o = (item ?? {}) as { act?: unknown; descriere?: unknown; url?: unknown }
			add_row({
				act: String(o.act ?? ""),
				descriere: String(o.descriere ?? ""),
				url: String(o.url ?? ""),
			})
		}
		count.val = row_refs.length
	}

	let last = ""
	async function refresh(slug: string) {
		const r = await api_get<{ attributes?: Record<string, unknown> }>(
			`/api/maps/${encodeURIComponent(slug)}`,
		)
		if (slug !== last) {
			return // răspuns întârziat pentru altă hartă (aceeași gardă ca ApiLinksSection)
		}
		set_rows(r.ok ? r.data.attributes?.legislatie : [])
	}

	// Reîncarcă lista când se schimbă harta selectată (pattern-ul PublishSection).
	van.derive(() => {
		const slug = store.map_detail?.slug ?? ""
		if (slug && slug !== last) {
			last = slug
			void refresh(slug)
		}
	})

	async function save() {
		if (busy.val) {
			return
		}
		// Rândurile complet goale se aruncă tăcut (nu blocăm salvarea pe un rând uitat gol).
		const legislatie = row_refs
			.map((r) => ({
				act: r.act.value.trim(),
				descriere: r.descriere.value.trim(),
				url: r.url.value.trim(),
			}))
			.filter((l) => l.act || l.descriere)
		busy.val = true
		const r = await api_patch(`/api/maps/${encodeURIComponent(get_map_slug())}`, {
			attributes: { legislatie },
		})
		busy.val = false
		if (r.ok) {
			show_toast("Nomenclator legislativ salvat", "ok")
			rlu_history_tick.val++
			set_rows(legislatie)
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	return t.div(
		{ class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-3" },
		t.div(
			{ class: "flex items-center justify-between" },
			t.span(
				{
					class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
				},
				"Nomenclator legislativ",
			),
			() =>
				count.val > 0
					? t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, `${count.val} acte`)
					: t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "gol"),
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Legile și ordonanțele care pot modifica regimurile aprobate prin RLU. " +
				"Apar în panoul de regim din hartă și în contextul certificatelor (temei_legislativ).",
		),
		rows_box,
		t.div(
			{ class: "flex gap-2 items-center" },
			t.button(
				{ type: "button", class: BTN_SM_SECONDARY, onclick: () => add_row() },
				"+ Adaugă act normativ",
			),
			t.button(
				{ type: "button", class: BTN_SM_PRIMARY, disabled: () => busy.val, onclick: save },
				"Salvează",
			),
		),
	)
}

// ─────────────────────────────────────────────
// Fundaluri de hartă configurabile (decizia 1.3 — plan/contracts/basemaps.md): listă de
// {name, type xyz|wms, url, layers, attribution} pe hartă (EAV `attributes.basemaps`), validată
// LOCAL cu același cod ca serverul (lib/basemaps) înainte de PATCH; clona LegislatieSection
// (DOM necontrolat, focus-safe). Fără chei de acces în URL — ajung în viewerul public.
// ─────────────────────────────────────────────
function BasemapsSection(get_map_slug: () => string) {
	const busy = van.state(false)
	const count = van.state(0)
	const IN_CLS =
		"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none focus:border-cyan-500"
	const rows_box = t.div({ class: "flex flex-col gap-2" })
	const row_refs: Array<{
		el: HTMLElement
		name: HTMLInputElement
		type: HTMLSelectElement
		url: HTMLInputElement
		layers: HTMLInputElement
		attribution: HTMLInputElement
		orig: Partial<Basemap> // obiectul citit — cheile negestionate de UI (format/tile_size/max_zoom)
	}> = []

	function add_row(v: Partial<Basemap> = {}) {
		const name = t.input({
			class: IN_CLS + " w-40",
			placeholder: "Nume (ex. Ortofoto ANCPI)",
			value: v.name ?? "",
			maxlength: "60",
		}) as HTMLInputElement
		const type = t.select(
			{ class: IN_CLS + " w-24" },
			t.option({ value: "xyz" }, "XYZ"),
			t.option({ value: "wms" }, "WMS"),
		) as HTMLSelectElement
		type.value = v.type ?? "xyz"
		const url = t.input({
			class: IN_CLS + " flex-1 min-w-64",
			placeholder: "https://…/{z}/{x}/{y}.png  sau  https://…/wms (fără parametri)",
			value: v.url ?? "",
		}) as HTMLInputElement
		const layers = t.input({
			class: IN_CLS + " w-44",
			placeholder: "straturi WMS (LAYERS=)",
			title: "Doar pentru WMS: numele stratului/straturilor din serviciu, separate prin virgulă",
			value: v.layers ?? "",
		}) as HTMLInputElement
		const attribution = t.input({
			class: IN_CLS + " w-44",
			placeholder: "Atribuire (ex. © ANCPI)",
			value: v.attribution ?? "",
			maxlength: "200",
		}) as HTMLInputElement
		// Câmpul de straturi are sens doar la WMS — îl (de)activăm la schimbarea tipului.
		const sync_type = () => {
			layers.disabled = type.value !== "wms"
			layers.classList.toggle("opacity-40", type.value !== "wms")
		}
		type.addEventListener("change", sync_type)
		sync_type()
		const el = t.div(
			{ class: "flex gap-2 items-center flex-wrap" },
			name,
			type,
			url,
			layers,
			attribution,
			t.button(
				{
					type: "button",
					class: "text-slate-400 dark:text-slate-500 hover:text-red-600 cursor-pointer text-sm",
					title: "Șterge fundalul din listă",
					onclick: () => {
						el.remove()
						const i = row_refs.findIndex((r) => r.el === el)
						if (i >= 0) {
							row_refs.splice(i, 1)
						}
						count.val = row_refs.length
					},
				},
				"✕",
			),
		) as HTMLElement
		row_refs.push({ el, name, type, url, layers, attribution, orig: v })
		rows_box.appendChild(el)
		count.val = row_refs.length
	}

	function set_rows(list: Basemap[] | undefined) {
		rows_box.replaceChildren()
		row_refs.length = 0
		for (const b of list ?? []) {
			add_row(b)
		}
		count.val = row_refs.length
	}

	// Lista normalizată vine în detaliul hărții (`basemaps`) — reîncărcată la schimbarea hărții.
	let last = ""
	van.derive(() => {
		const d = store.map_detail
		const slug = d?.slug ?? ""
		if (slug && slug !== last) {
			last = slug
			set_rows(d?.basemaps)
		}
	})

	async function save() {
		if (busy.val) {
			return
		}
		// Peste obiectul original: format/tile_size/max_zoom (doar prin API) supraviețuiesc salvării —
		// altfel `validate_basemaps` le readucea la implicit (image/png, 256, 19); regula de la style3d.
		const raw = row_refs.map((r) =>
			Object.assign({}, r.orig, {
				name: r.name.value.trim(),
				type: r.type.value,
				url: r.url.value.trim(),
				layers: r.layers.value.trim(),
				attribution: r.attribution.value.trim(),
			}),
		)
		// Un rând gol NU se aruncă tăcut („Fundaluri salvate" fără să se fi salvat nimic — testare
		// 08.09): utilizatorul l-a adăugat, deci ori îl completează, ori îl șterge explicit.
		const empty_at = raw.findIndex((b) => !b.name && !b.url)
		if (empty_at >= 0) {
			show_toast(
				`Fundaluri: rândul #${empty_at + 1} e gol — completează numele și adresa sau șterge-l (✕).`,
				"err",
				7000,
			)
			return
		}
		// Aceeași validare ca pe server (lib/basemaps) — mesajul spune care rând și de ce.
		const v = validate_basemaps(raw)
		if (!v.ok) {
			show_toast(`Fundaluri: ${v.error}.`, "err", 7000)
			return
		}
		busy.val = true
		const r = await api_patch(`/api/maps/${encodeURIComponent(get_map_slug())}`, {
			attributes: { basemaps: v.value },
		})
		busy.val = false
		if (r.ok) {
			show_toast(
				v.value.length > 0
					? "Fundaluri salvate — apar pe hartă și în portalul public"
					: "Fundaluri șterse — harta revine la perechea implicită Hartă/Satelit",
				"ok",
			)
			await load_map_detail(get_map_slug())
			set_rows(store.map_detail?.basemaps)
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err", 7000)
		}
	}

	return t.div(
		{ class: "rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-3" },
		t.div(
			{ class: "flex items-center justify-between" },
			t.span(
				{
					class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
				},
				"Fundaluri de hartă",
			),
			() =>
				count.val > 0
					? t.span(
							{ class: "text-xs text-slate-400 dark:text-slate-500" },
							`${count.val} fundaluri`,
						)
					: t.span(
							{ class: "text-xs text-slate-400 dark:text-slate-500" },
							"implicit: Hartă / Satelit",
						),
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Servicii de imagini (ortofotoplan ANCPI prin WMS, alte servicii XYZ/WMS) care înlocuiesc butoanele fixe " +
				"Hartă/Satelit, pe harta internă și în portalul public. XYZ: adresă cu {z}/{x}/{y}; WMS: adresa de bază " +
				"(fără parametri) + straturile. Doar http(s), fără chei de acces în adresă — ajung în browserul oricui.",
		),
		rows_box,
		t.div(
			{ class: "flex gap-2 items-center" },
			t.button(
				{ type: "button", class: BTN_SM_SECONDARY, onclick: () => add_row() },
				"+ Adaugă fundal",
			),
			t.button(
				{ type: "button", class: BTN_SM_PRIMARY, disabled: () => busy.val, onclick: save },
				"Salvează",
			),
		),
	)
}

// ─────────────────────────────────────────────
// ── Istoricul regulamentului: helperi puri de afișare (folosiți de RegimUtrTab) ──
const hist_when = (iso: string): string => (iso ? new Date(iso).toLocaleString("ro-RO") : "—")
/**
 * Autorul unui rând de istoric; fără autor (seed, script, import) se arată SURSA, nu o celulă goală.
 * Agentul MCP al primăriei apare ca „Agent AI”, nu cu adresa lui tehnică (`author_label`, lib/mcp_agent).
 */
const hist_who = (e: { author: string | null; source: string | null }): string =>
	author_label(e.author) || (e.source === "import" ? "import" : e.source ? `(${e.source})` : "—")
function hist_fmt_v(v: unknown): string {
	if (v === undefined || v === null || v === "") {
		return "—"
	}
	if (typeof v === "number") {
		return v.toLocaleString("ro-RO")
	}
	return typeof v === "string" ? v : JSON.stringify(v)
}
function hist_keyed_text(d: KeyedDiff, label: string): string[] {
	const out: string[] = []
	if (d.added.length > 0) {
		out.push(`+${d.added.length} ${label}`)
	}
	if (d.changed.length > 0) {
		out.push(`~${d.changed.length} ${label}`)
	}
	if (d.removed.length > 0) {
		out.push(`−${d.removed.length} ${label}`)
	}
	return out
}
/** Textul unui rând din listă: ce s-a schimbat, pe scurt. */
function hist_entry_text(e: RluHistoryEntry): string {
	if (e.kind === "legislatie") {
		const parts: string[] = []
		if (e.legislatie.added.length > 0) {
			parts.push("adăugat: " + e.legislatie.added.join(", "))
		}
		if (e.legislatie.changed.length > 0) {
			parts.push("modificat: " + e.legislatie.changed.join(", "))
		}
		if (e.legislatie.removed.length > 0) {
			parts.push("retras: " + e.legislatie.removed.join(", "))
		}
		return "Legislație — " + (parts.join("; ") || "nicio schimbare")
	}
	const s = e.summary
	const parts = [
		...hist_keyed_text(s.utr, "UTR"),
		...hist_keyed_text(s.fise, "fișe"),
		...hist_keyed_text(s.avize, "avize"),
		...(s.other_rlu.length > 0 ? ["alte chei RLU: " + s.other_rlu.join(", ")] : []),
	]
	if (parts.length === 0) {
		if (e.change_type === "create") {
			return "Harta a fost creată"
		}
		if (s.other_meta.length > 0) {
			return "Fără schimbări în regulament (altele: " + s.other_meta.join(", ") + ")"
		}
		return e.change_type === "update_config"
			? "Fără schimbări în regulament (nume/descriere)"
			: "Fără schimbări în regulament"
	}
	return (
		parts.join(" · ") + (s.other_meta.length > 0 ? " · altele: " + s.other_meta.join(", ") : "")
	)
}
/** O schimbare pe un cod (UTR/aviz): rubricile înainte → după. */
function hist_code_change_text(c: CodeChange, labels: Record<string, string> = {}): string {
	const lab = (k: string) => labels?.[k] ?? k
	if (c.kind === "added") {
		return "nou: " + c.fields.map((f) => `${lab(f.key)} = ${hist_fmt_v(f.after)}`).join("; ")
	}
	if (c.kind === "removed") {
		return "șters"
	}
	return c.fields
		.map((f) => `${lab(f.key)}: ${hist_fmt_v(f.before)} → ${hist_fmt_v(f.after)}`)
		.join("; ")
}

// Tab „Regim UTR" (F5-T7, REQ-URB-005): tabel editabil peste `metadata.rlu.utr_indicatori`
// (UTR / denumire / POT / CUT / Rh) + fișele RLU (`metadata.rlu_fise[cod]`, etichetă → text),
// scris prin `update_map` (versionat — istoricul regulamentului se păstrează în versions).
// Importul din xlsx (seed_map_meta.ts) rămâne pentru seed-ul inițial; aici e întreținerea.
// Rândurile = UNIUNEA codurilor din indicatori ∪ fișe (în datele reale există fișe fără rând
// de indicatori — In1/In2 la Ștefan cel Mare — și invers; ambele trebuie vizibile/editabile).
// DOM necontrolat (focus-safe); fișa unui UTR se construiește LAZY la prima expandare
// (28 fișe × ~23 câmpuri ar însemna mii de textarea-uri construite degeaba).
// ─────────────────────────────────────────────
function RegimUtrTab(get_map_slug: () => string) {
	const busy = van.state(false)
	const count = van.state(0)
	const IN_CLS =
		"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm outline-none focus:border-cyan-500"

	interface FisaField {
		key: HTMLInputElement
		val: HTMLTextAreaElement
		el: HTMLElement
	}
	interface UtrRow {
		el: HTMLElement
		cod: HTMLInputElement
		denumire: HTMLInputElement
		pot: HTMLInputElement
		cut: HTMLInputElement
		rh: HTMLInputElement
		orig_ind: Record<string, unknown> // rândul original de indicatori (păstrează cheile needitabile: cod_szf…)
		fisa_data: Record<string, unknown> // fișa încărcată (sursa pt. build lazy)
		fisa_fields: FisaField[] | null // null = DOM-ul fișei nu e construit încă
		fisa_box: HTMLElement
	}
	const rows: UtrRow[] = []
	const rows_box = t.div({ class: "flex flex-col gap-1.5" })
	let meta_rlu: Record<string, unknown> = {} // rlu-ul original (păstrează zone_protectie etc.)

	// ── Avize necesare (decizia 4.8, plan/contracts/avize.md): `metadata.rlu.avize` ──
	// Rând per cod de zonă (UTR / SZF / „*” = toate zonele) + avizele, unul pe rând; se salvează
	// odată cu regimul (același PATCH, `rlu` întreg).
	interface AvizRow {
		el: HTMLElement
		cod: HTMLInputElement
		avize: HTMLTextAreaElement
	}
	const aviz_rows: AvizRow[] = []
	const aviz_box = t.div({ class: "flex flex-col gap-1.5" })
	const aviz_count = van.state(0)
	const dl_cod_id = `avize-coduri-${Math.random().toString(36).slice(2, 8)}`
	const dl_cod = t.datalist({ id: dl_cod_id })
	function add_aviz_row(v: { cod?: string; avize?: string[] } = {}) {
		const cod = t.input({
			class: IN_CLS + " w-28 font-semibold",
			placeholder: "Cod sau *",
			title: "Codul zonei (UTR/SZF) sau * = avize generale, valabile în orice zonă (și extravilan)",
			list: dl_cod_id,
			value: v.cod ?? "",
		}) as HTMLInputElement
		const avize = t.textarea(
			{
				class: IN_CLS + " flex-1 min-h-[2.4rem]",
				placeholder: "un aviz pe rând (ex. Aviz DSP)",
				rows: "2",
			},
			(v.avize ?? []).join("\n"),
		) as HTMLTextAreaElement
		const el = t.div(
			{ class: "flex gap-2 items-start" },
			cod,
			avize,
			t.button(
				{
					type: "button",
					class:
						"text-slate-400 dark:text-slate-500 hover:text-red-600 cursor-pointer text-sm mt-1",
					title: "Șterge rândul",
					onclick: () => {
						el.remove()
						const i = aviz_rows.findIndex((r) => r.el === el)
						if (i >= 0) {
							aviz_rows.splice(i, 1)
						}
						aviz_count.val = aviz_rows.length
					},
				},
				"✕",
			),
		) as HTMLElement
		aviz_rows.push({ el, cod, avize })
		aviz_box.appendChild(el)
		aviz_count.val = aviz_rows.length
	}
	function set_aviz_rows(raw: unknown) {
		aviz_box.replaceChildren()
		aviz_rows.length = 0
		for (const e of parse_avize(raw)) {
			add_aviz_row({ cod: e.cod, avize: e.avize })
		}
		aviz_count.val = aviz_rows.length
	}
	/** Forma trimisă la server: `[{cod, avize:[…]}]` (parsată defensiv de lib/rlu la citire). */
	function avize_from_rows(): Array<{ cod: string; avize: string[] }> {
		const out: Array<{ cod: string; avize: string[] }> = []
		for (const r of aviz_rows) {
			const cod = r.cod.value.trim()
			const list = r.avize.value
				.split(/\r?\n|;/)
				.map((s) => s.trim())
				.filter((s, i, arr) => s !== "" && arr.indexOf(s) === i)
			if (cod && list.length > 0) {
				out.push({ cod, avize: list })
			}
		}
		return out
	}
	/** Sugestiile de coduri = codurile din tabelul de indicatori/fișe + „*”. */
	function refresh_cod_suggestions() {
		const codes = new Set<string>([AVIZE_ALL])
		for (const r of rows) {
			const c = r.cod.value.trim()
			if (c) {
				codes.add(c)
			}
		}
		dl_cod.replaceChildren(
			...[...codes].map((c) =>
				t.option({ value: c, label: c === AVIZE_ALL ? "* — toate zonele" : c }),
			),
		)
	}

	const obj = (x: unknown): Record<string, unknown> =>
		x !== null && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : {}
	const str = (x: unknown): string => (x == null ? "" : String(x))
	/** „0.35"/„0,35" rămân numere; textul normativ liber rămâne text. */
	const numify = (s: string): unknown => {
		const v = s.trim()
		if (v === "") {
			return ""
		}
		const norm = v.replace(",", ".")
		return /^-?\d+(\.\d+)?$/.test(norm) ? Number(norm) : v
	}

	function add_fisa_field(row: UtrRow, key = "", val = "") {
		const key_in = t.input({
			class: IN_CLS + " w-64 flex-shrink-0",
			placeholder: "Câmp (ex. Utilizări admise)",
			value: key,
		}) as HTMLInputElement
		const val_ta = t.textarea(
			{ class: IN_CLS + " flex-1 min-h-[2.4rem]", placeholder: "Text din regulament…" },
			val,
		) as HTMLTextAreaElement
		const el = t.div(
			{ class: "flex gap-2 items-start" },
			key_in,
			val_ta,
			t.button(
				{
					type: "button",
					class:
						"text-slate-400 dark:text-slate-500 hover:text-red-600 cursor-pointer text-sm mt-1",
					title: "Șterge câmpul din fișă",
					onclick: () => {
						el.remove()
						const list = row.fisa_fields
						if (list) {
							const i = list.findIndex((f) => f.el === el)
							if (i >= 0) {
								list.splice(i, 1)
							}
						}
					},
				},
				"✕",
			),
		) as HTMLElement
		row.fisa_fields?.push({ key: key_in, val: val_ta, el })
		return el
	}

	/** Construiește DOM-ul fișei la prima expandare (lazy). */
	function build_fisa(row: UtrRow) {
		if (row.fisa_fields !== null) {
			return
		}
		row.fisa_fields = []
		const fields_box = t.div({ class: "flex flex-col gap-1.5" })
		for (const [k, v] of Object.entries(row.fisa_data)) {
			fields_box.appendChild(add_fisa_field(row, k, str(v)))
		}
		row.fisa_box.appendChild(
			t.div(
				{
					class:
						"rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-3 flex flex-col gap-2",
				},
				fields_box,
				t.button(
					{
						type: "button",
						class:
							"self-start text-xs text-cyan-700 dark:text-cyan-300 font-semibold hover:underline cursor-pointer",
						onclick: () => {
							const el = add_fisa_field(row)
							fields_box.appendChild(el)
						},
					},
					"+ câmp în fișă",
				),
			),
		)
	}

	function add_row(v: {
		cod?: string
		ind?: Record<string, unknown>
		fisa?: Record<string, unknown>
	}) {
		const ind = v.ind ?? {}
		const cod = t.input({
			class: IN_CLS + " w-20 font-semibold",
			placeholder: "Cod",
			value: v.cod ?? "",
		}) as HTMLInputElement
		const denumire = t.input({
			class: IN_CLS + " flex-1 min-w-36",
			placeholder: "Denumirea zonei",
			value: str(ind.denumire),
		}) as HTMLInputElement
		const pot = t.input({
			class: IN_CLS + " w-24",
			placeholder: "POT max",
			title: "POT maxim — număr (0.35) sau text normativ",
			value: str(ind.pot_max),
		}) as HTMLInputElement
		const cut = t.input({
			class: IN_CLS + " w-24",
			placeholder: "CUT max",
			value: str(ind.cut_max),
		}) as HTMLInputElement
		const rh = t.input({
			class: IN_CLS + " w-24",
			placeholder: "Rh max",
			title: "Regim de înălțime maxim (ex. P+1E+M)",
			value: str(ind.rh_max),
		}) as HTMLInputElement
		const fisa_box = t.div({ class: "hidden mt-1.5" }) as HTMLElement
		const fisa_n = Object.keys(v.fisa ?? {}).length

		const row: UtrRow = {
			el: t.div() as HTMLElement, // înlocuit imediat mai jos
			cod,
			denumire,
			pot,
			cut,
			rh,
			orig_ind: ind,
			fisa_data: v.fisa ?? {},
			fisa_fields: null,
			fisa_box,
		}
		const expand_btn = t.button(
			{
				type: "button",
				class:
					"text-xs text-cyan-700 dark:text-cyan-300 font-semibold hover:underline cursor-pointer flex-shrink-0",
				onclick: () => {
					build_fisa(row)
					fisa_box.classList.toggle("hidden")
					expand_btn.textContent = fisa_box.classList.contains("hidden")
						? `▸ fișă (${row.fisa_fields?.length ?? fisa_n})`
						: "▾ fișă"
				},
			},
			`▸ fișă (${fisa_n})`,
		) as HTMLElement
		row.el = t.div(
			{ class: "border-b border-slate-100 dark:border-slate-800 pb-1.5" },
			t.div(
				{ class: "flex gap-2 items-center flex-wrap" },
				cod,
				denumire,
				pot,
				cut,
				rh,
				expand_btn,
				t.button(
					{
						type: "button",
						class: "text-slate-400 dark:text-slate-500 hover:text-red-600 cursor-pointer text-sm",
						title: "Șterge UTR-ul (indicatori + fișă)",
						onclick: () => {
							row.el.remove()
							const i = rows.findIndex((r) => r === row)
							if (i >= 0) {
								rows.splice(i, 1)
							}
							count.val = rows.length
						},
					},
					"✕",
				),
			),
			fisa_box,
		) as HTMLElement
		rows.push(row)
		rows_box.appendChild(row.el)
		count.val = rows.length
	}

	function set_rows(metadata: unknown) {
		rows_box.replaceChildren()
		rows.length = 0
		const meta = obj(metadata)
		meta_rlu = obj(meta.rlu)
		const indicatori = Array.isArray(meta_rlu.utr_indicatori)
			? (meta_rlu.utr_indicatori as unknown[]).map(obj)
			: []
		const fise = obj(meta.rlu_fise)
		// Uniunea codurilor: întâi ordinea indicatorilor, apoi fișele fără indicatori.
		const by_cod = new Map<
			string,
			{ ind?: Record<string, unknown>; fisa?: Record<string, unknown> }
		>()
		for (const ind of indicatori) {
			const cod = str(ind.utr).trim()
			if (cod) {
				by_cod.set(cod, { ind })
			}
		}
		for (const [cod, fisa] of Object.entries(fise)) {
			const e = by_cod.get(cod)
			if (e) {
				e.fisa = obj(fisa)
			} else {
				by_cod.set(cod, { fisa: obj(fisa) })
			}
		}
		for (const [cod, e] of by_cod) {
			add_row({ cod, ind: e.ind, fisa: e.fisa })
		}
		count.val = rows.length
		set_aviz_rows(meta_rlu.avize)
		refresh_cod_suggestions()
	}

	// Reîncarcă la schimbarea hărții — metadata e deja în store.map_detail (fără fetch în plus).
	let last = ""

	// ── Motivul modificării → `versions.message` (istoricul de mai jos îl afișează) ──
	const motiv = t.input({
		type: "text",
		class: IN_CLS + " flex-1 min-w-[16rem]",
		placeholder: "Motivul modificării (opțional — ex. HCL 12/2026)",
		maxlength: "500",
	}) as HTMLInputElement

	// ── Istoricul regulamentului (REQ-URB-028/056): arhiva există din prima zi (fiecare salvare =
	// versiune a hărții), aici e CONSULTAREA ei. Lista vine doar cu antete + rezumat; detaliul unei
	// versiuni (rubrici înainte/după + indicatorii de atunci) se cere la click. DOM imperativ,
	// ca restul tabului. ──
	const hist_open = van.state(false)
	const hist_show_all = van.state(false)
	const hist_status = van.state("")
	let hist_entries: RluHistoryEntry[] = []
	let hist_loaded = false
	const my_generation = ++regim_tab_generation
	const hist_rows = t.div({
		class: "flex flex-col divide-y divide-slate-100 dark:divide-slate-800",
	})
	const hist_detail = t.div({ class: "flex flex-col gap-2" })

	function render_hist() {
		hist_rows.replaceChildren()
		const visible = hist_entries.filter((e) => hist_show_all.val || e.touches_rlu)
		if (visible.length === 0) {
			hist_rows.appendChild(
				t.div(
					{ class: "text-xs text-slate-400 dark:text-slate-500 py-2" },
					hist_entries.length === 0
						? "Niciun istoric încă."
						: "Nicio modificare a regulamentului; bifează „arată și restul” pentru celelalte versiuni.",
				),
			)
			return
		}
		for (const e of visible) {
			const dim = !e.touches_rlu
			hist_rows.appendChild(
				t.div(
					{
						class:
							"grid grid-cols-1 sm:grid-cols-[9.5rem_11rem_1fr_auto] gap-x-3 gap-y-0.5 py-1.5 text-xs " +
							(dim ? "text-slate-400 dark:text-slate-500" : "text-slate-700 dark:text-slate-200"),
					},
					t.div({ class: "whitespace-nowrap" }, hist_when(e.at)),
					t.div({ class: "truncate", title: hist_who(e) }, hist_who(e)),
					t.div(
						t.div(hist_entry_text(e)),
						e.message
							? t.div({ class: "italic text-slate-500 dark:text-slate-400" }, "„" + e.message + "”")
							: t.span({ style: "display:none" }),
					),
					e.kind === "regulament"
						? t.button(
								{
									type: "button",
									class:
										"underline text-cyan-700 dark:text-cyan-300 hover:text-cyan-900 whitespace-nowrap",
									onclick: () => void load_detail(e.version),
								},
								`v${e.version} · detalii`,
							)
						: t.span({ style: "display:none" }),
				),
			)
		}
	}

	van.derive(() => {
		// `.val` citit ÎNTÂI = dependența (contorul e mereu ≥ 0); un tab demontat (altă generație) nu mai face nimic
		const tick = rlu_history_tick.val
		if (my_generation === regim_tab_generation && tick >= 0 && hist_loaded) {
			void load_hist()
		}
	})
	async function load_hist() {
		if (!get_map_slug()) {
			hist_status.val = "selectează o hartă"
			return
		}
		hist_status.val = "Se încarcă…"
		const r = await api_get<RluHistory>(
			`/api/maps/${encodeURIComponent(get_map_slug())}/regulament/istoric`,
		)
		if (!r.ok) {
			hist_status.val = `Eroare: ${r.err?.message ?? ""}`
			return
		}
		hist_entries = r.data.entries
		hist_loaded = true
		hist_status.val = `${r.data.total} intrări`
		render_hist()
	}

	async function load_detail(version: number) {
		hist_detail.replaceChildren(
			t.div({ class: "text-xs text-slate-400" }, `Se încarcă versiunea ${version}…`),
		)
		const r = await api_get<RluVersionDetail>(
			`/api/maps/${encodeURIComponent(get_map_slug())}/regulament/istoric/${version}`,
		)
		if (!r.ok) {
			hist_detail.replaceChildren(
				t.div({ class: "text-xs text-red-600" }, `Eroare: ${r.err?.message ?? ""}`),
			)
			return
		}
		const d = r.data
		const lines: HTMLElement[] = []
		const li = (txt: string) => t.li({ class: "text-xs text-slate-700 dark:text-slate-200" }, txt)
		for (const c of d.diff.utr) {
			lines.push(li(`UTR ${c.cod} — ${hist_code_change_text(c, INDICATOR_LABELS)}`))
		}
		for (const c of d.diff.fise) {
			lines.push(
				li(
					`Fișa ${c.cod} — ` +
						(c.kind === "added"
							? "fișă nouă"
							: c.kind === "removed"
								? "fișă ștearsă"
								: "rubrici modificate: " + c.fields.map((f) => f.key).join(", ")),
				),
			)
		}
		for (const c of d.diff.avize) {
			lines.push(li(`Avize ${c.cod} — ${hist_code_change_text(c)}`))
		}
		if (d.diff.summary.other_rlu.length > 0) {
			lines.push(li("Alte chei RLU modificate: " + d.diff.summary.other_rlu.join(", ")))
		}
		if (d.diff.summary.other_meta.length > 0) {
			lines.push(li("Alte metadate modificate: " + d.diff.summary.other_meta.join(", ")))
		}
		if (lines.length === 0) {
			lines.push(li("Nicio diferență în regulament față de versiunea precedentă."))
		}
		const cell = (txt: string, cls = "") => t.td({ class: "px-2 py-1 align-top " + cls }, txt)
		const table = t.table(
			{ class: "w-full text-xs border-collapse" },
			t.thead(
				t.tr(
					{
						class:
							"text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700",
					},
					...["Cod UTR", "Denumire", "POT max", "CUT max", "Rh max"].map((h) =>
						t.th({ class: "px-2 py-1 font-semibold" }, h),
					),
				),
			),
			t.tbody(
				...d.utr_indicatori.map((row) =>
					t.tr(
						{ class: "border-b border-slate-100 dark:border-slate-800" },
						cell(hist_fmt_v(row.utr), "font-mono"),
						cell(hist_fmt_v(row.denumire)),
						cell(hist_fmt_v(row.pot_max)),
						cell(hist_fmt_v(row.cut_max)),
						cell(hist_fmt_v(row.rh_max)),
					),
				),
			),
		)
		hist_detail.replaceChildren(
			t.div(
				{ class: "flex items-center justify-between gap-2" },
				t.span(
					{ class: "text-xs font-semibold text-slate-600 dark:text-slate-300" },
					`Versiunea ${d.version}` +
						(d.previous_version > 0 ? ` (față de v${d.previous_version})` : " (prima)") +
						` · ${hist_when(d.at)} · ${hist_who(d)}` +
						(d.message ? ` · „${d.message}”` : ""),
				),
				t.button(
					{
						type: "button",
						class: "text-xs underline text-slate-500",
						onclick: () => hist_detail.replaceChildren(),
					},
					"închide",
				),
			),
			t.ul({ class: "list-disc pl-5 flex flex-col gap-0.5" }, ...lines),
			t.div(
				{ class: "text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1" },
				`Indicatorii din această versiune (${d.utr_indicatori.length} UTR-uri, ${Object.keys(d.rlu_fise).length} fișe) — doar citire`,
			),
			t.div({ class: "overflow-x-auto" }, table),
		)
	}

	const hist_section = t.div(
		{
			class:
				"rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-3 flex flex-col gap-2",
		},
		t.div(
			{ class: "flex items-center justify-between gap-2" },
			t.span(
				{
					class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
				},
				"Istoricul regulamentului",
			),
			t.div(
				{ class: "flex items-center gap-3" },
				() => t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, hist_status.val),
				t.button(
					{
						type: "button",
						class: BTN_SM_SECONDARY,
						onclick: () => {
							hist_open.val = !hist_open.val
							// La FIECARE deschidere, nu doar la prima: între timp s-a putut salva ceva.
							if (hist_open.val) {
								void load_hist()
							}
						},
					},
					() => (hist_open.val ? "Ascunde" : "Arată istoricul"),
				),
			),
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Fiecare salvare a regimului este o versiune arhivată a hărții (indicatori, fișe, avize), iar nomenclatorul legislativ are propriul istoric. " +
				"Aici vezi cine, când și ce a schimbat; „detalii” arată rubricile înainte/după și indicatorii de la acea dată.",
		),
		t.div(
			{ style: () => (hist_open.val ? "" : "display:none"), class: "flex flex-col gap-2" },
			t.label(
				{
					class:
						"flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none",
				},
				t.input({
					type: "checkbox",
					class: "accent-cyan-700",
					onchange: (ev: Event) => {
						hist_show_all.val = (ev.target as HTMLInputElement).checked
						render_hist()
					},
				}),
				"arată și versiunile care nu ating regulamentul (redenumiri, bilanțuri)",
			),
			hist_rows,
			hist_detail,
		),
	)

	van.derive(() => {
		const d = store.map_detail
		const slug = d?.slug ?? ""
		if (slug && slug !== last) {
			last = slug
			set_rows(d?.metadata)
			// Istoricul și motivul erau ale hărții ANTERIOARE: fără reset, lista ar fi rămas pe ecran
			// etichetată ca a hărții noi, iar un motiv tastat ar fi ajuns pe versiunea altei hărți.
			motiv.value = ""
			hist_entries = []
			hist_loaded = false
			hist_status.val = ""
			hist_rows.replaceChildren()
			hist_detail.replaceChildren()
			if (hist_open.val) {
				void load_hist()
			}
		}
	})

	async function save() {
		if (busy.val) {
			return
		}
		const utr_indicatori: Array<Record<string, unknown>> = []
		const rlu_fise: Record<string, Record<string, unknown>> = {}
		for (const r of rows) {
			const cod = r.cod.value.trim()
			if (!cod) {
				continue // rând fără cod = incomplet, se aruncă tăcut
			}
			const denumire = r.denumire.value.trim()
			const pot = r.pot.value.trim()
			const cut = r.cut.value.trim()
			const rh = r.rh.value.trim()
			if (denumire || pot || cut || rh) {
				// Cheile needitabile din rândul original (cod_szf, denumire_szf…) se PĂSTREAZĂ.
				utr_indicatori.push({
					...r.orig_ind,
					utr: cod,
					denumire,
					pot_max: numify(pot),
					cut_max: numify(cut),
					rh_max: rh,
				})
			}
			// Fișa: needitată (DOM neconstruit) → se păstrează ca atare; editată → din inputs.
			if (r.fisa_fields === null) {
				if (Object.keys(r.fisa_data).length > 0) {
					rlu_fise[cod] = obj(r.fisa_data)
				}
			} else {
				const fisa: Record<string, unknown> = {}
				for (const f of r.fisa_fields) {
					const k = f.key.value.trim()
					const val = f.val.value.trim()
					if (k && val) {
						fisa[k] = val
					}
				}
				if (Object.keys(fisa).length > 0) {
					rlu_fise[cod] = fisa
				}
			}
		}
		busy.val = true
		// update_map face merge SHALLOW pe metadata → trimitem `rlu` ÎNTREG (cu zone_protectie
		// etc. păstrate din original, + avizele — 4.8) și `rlu_fise` întreg; restul rămâne neatins.
		const avize = avize_from_rows()
		const message = motiv.value.trim()
		const r = await api_patch(`/api/maps/${encodeURIComponent(get_map_slug())}`, {
			metadata: { rlu: { ...meta_rlu, utr_indicatori, avize }, rlu_fise },
			...(message ? { message } : {}),
		})
		busy.val = false
		if (r.ok) {
			meta_rlu = { ...meta_rlu, utr_indicatori, avize }
			motiv.value = ""
			show_toast("Regimul UTR salvat", "ok")
			if (hist_loaded) {
				void load_hist()
			}
		} else {
			show_toast(`Eroare: ${r.err?.message ?? ""}`, "err")
		}
	}

	/** Blocul „Avize necesare” — sub tabelul de indicatori, salvat cu același buton. */
	const avize_block = t.div(
		{
			class:
				"rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-3 flex flex-col gap-2",
		},
		t.div(
			{ class: "flex items-center justify-between" },
			t.span(
				{
					class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
				},
				"Avize necesare pe zone",
			),
			() =>
				t.span(
					{ class: "text-xs text-slate-400 dark:text-slate-500" },
					aviz_count.val > 0 ? `${aviz_count.val} rânduri` : "gol",
				),
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Avizele și acordurile cerute într-o zonă (cod UTR/SZF din tabelul de mai sus) sau în toate zonele (rândul „*”). " +
				"Apar în panoul de regim și se completează automat în certificatul de urbanism (rubrica „avize necesare”). " +
				"Pentru o subzonă (ex. In1) se folosesc și avizele codului de bază (In).",
		),
		dl_cod,
		aviz_box,
		t.button(
			{
				type: "button",
				class: BTN_SM_SECONDARY + " self-start",
				onclick: () => {
					refresh_cod_suggestions()
					add_aviz_row()
				},
			},
			"+ Adaugă rând de avize",
		),
	)

	return t.div(
		{
			class:
				"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex flex-col gap-3",
		},
		t.div(
			{ class: "flex items-center justify-between" },
			t.span(
				{
					class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
				},
				"Regim UTR (indicatori + fișe RLU)",
			),
			() => t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, `${count.val} UTR-uri`),
		),
		t.p(
			{ class: "text-xs text-slate-500 dark:text-slate-400" },
			"Indicatorii urbanistici (POT/CUT/Rh) și fișele RLU per UTR — sursa panoului de regim din hartă " +
				"și a autofill-ului din certificate. POT/CUT acceptă număr sau text normativ. " +
				"Derogările (PUZ aprobat) NU se introduc aici: strat cu rol „derogare” pe hartă (REQ-URB-008).",
		),
		t.div(
			{
				class:
					// hidden sm:flex — pe telefon rândurile de date se împachetează (flex-wrap), deci
					// header-ul pe o singură linie de ~480px s-ar desincroniza și ar lăți pagina.
					"hidden sm:flex gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 px-1",
			},
			t.span({ class: "w-20" }, "UTR"),
			t.span({ class: "flex-1 min-w-36" }, "Denumire"),
			t.span({ class: "w-24" }, "POT max"),
			t.span({ class: "w-24" }, "CUT max"),
			t.span({ class: "w-24" }, "Rh max"),
		),
		rows_box,
		t.div(
			{ class: "flex gap-2 items-center" },
			t.button(
				{ type: "button", class: BTN_SM_SECONDARY, onclick: () => add_row({}) },
				"+ Adaugă UTR",
			),
		),
		avize_block,
		t.div(
			{ class: "flex flex-wrap gap-2 items-center" },
			t.button(
				{ type: "button", class: BTN_SM_PRIMARY, disabled: () => busy.val, onclick: save },
				"Salvează regimul",
			),
			motiv,
			t.span(
				{ class: "text-xs text-slate-400 dark:text-slate-500" },
				"salvează indicatorii, fișele și avizele împreună; motivul rămâne în istoric",
			),
		),
		hist_section,
	)
}

/**
 * Bara de export a hărții (3.2/3.3/3.5): format + sistem de coordonate + „cu atașamente", descărcare
 * prin fetch (mesaje de refuz + avertismente vizibile). Preferințele (format/SRID) sunt partajate cu
 * butoanele de export per strat din tabel.
 */
function ExportBar(get_map_slug: () => string) {
	const busy = van.state(false)
	const warnings = van.state<string[]>([])
	const sel_format = t.select({
		class: INPUT_CLS + " max-w-xs",
		onchange: (e: Event) => {
			export_format.val = (e.target as HTMLSelectElement).value
		},
	}) as HTMLSelectElement
	// Opțiunile se reconstruiesc pe identitate/hartă (MBTiles doar pentru personal).
	van.derive(() => {
		const personal = is_personal_for_map()
		const list = EXPORT_FORMATS_UI.filter((f) => !f.personal || personal)
		sel_format.replaceChildren(...list.map((f) => t.option({ value: f.value }, f.label)))
		if (!list.some((f) => f.value === export_format.val)) {
			export_format.val = "gpkg"
		}
		sel_format.value = export_format.val
		// Bifa „cu atașamente" e ascunsă pentru ne-personal — starea (de MODUL) trebuie să cadă cu
		// ea, altfel următoarea descărcare pleca cu `attachments=1` și primea 403.
		if (!personal) {
			export_attachments.val = false
		}
	})
	const sel_srid = t.select(
		{
			class: INPUT_CLS + " max-w-[220px]",
			onchange: (e: Event) => {
				export_srid.val = (e.target as HTMLSelectElement).value
			},
		},
		...EXPORT_SRIDS.map(([v, label]) => t.option({ value: v }, label)),
	) as HTMLSelectElement
	sel_srid.value = export_srid.val
	const inp_srid = t.input({
		type: "number",
		min: "1",
		class: () => INPUT_CLS + " max-w-[110px]" + (export_srid.val === "custom" ? "" : " hidden"),
		placeholder: "ex. 31700",
		// Legate la starea de MODUL: bara se reconstruiește la schimbarea tab-ului/hărții, iar un cod
		// EPSG tastat anterior pleca în URL cu câmpul afișat gol (la fel bifa de atașamente).
		value: () => export_srid_custom.val,
		oninput: (e: Event) => {
			export_srid_custom.val = (e.target as HTMLInputElement).value
		},
	})
	const chk_att = t.input({
		type: "checkbox",
		class: "accent-cyan-700",
		checked: () => export_attachments.val,
		onchange: (e: Event) => {
			export_attachments.val = (e.target as HTMLInputElement).checked
		},
	}) as HTMLInputElement

	async function run() {
		if (busy.val) {
			return
		}
		if (export_srid.val === "custom" && !/^\d+$/.test(export_srid_custom.val.trim())) {
			show_toast("Scrie un cod EPSG numeric (ex. 31700) sau alege un sistem din listă.", "err")
			return
		}
		busy.val = true
		try {
			warnings.val = await download_export(export_url(get_map_slug()))
		} finally {
			busy.val = false
		}
	}

	return t.div(
		{ class: "flex flex-col gap-2 px-1" },
		t.div(
			{ class: "flex items-center gap-3 flex-wrap" },
			t.span(
				{
					class: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide",
				},
				"Export hartă completă:",
			),
			sel_format,
			// SRID-ul e irelevant la KML (mereu WGS84) și MBTiles (mereu 3857) → ascuns.
			t.div(
				{
					class: () =>
						"flex items-center gap-2 " +
						(export_format.val === "kml" || export_format.val === "mbtiles" ? "hidden" : ""),
				},
				t.span({ class: "text-xs text-slate-400 dark:text-slate-500" }, "coordonate"),
				sel_srid,
				inp_srid,
			),
			t.label(
				{
					class: () =>
						"flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none " +
						(is_personal_for_map() ? "" : "hidden"),
					title:
						"Arhivă zip cu fișierul de export + documentele hărții + pozele de teren + manifest.json (doar personalul primăriei).",
				},
				chk_att,
				"cu atașamente (documente + poze de teren)",
			),
			t.button(
				{
					type: "button",
					class:
						"inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-50 dark:bg-cyan-950 border border-cyan-200 " +
						"text-cyan-700 dark:text-cyan-300 text-xs font-semibold hover:bg-cyan-100 transition disabled:opacity-50",
					disabled: () => busy.val,
					onclick: run,
				},
				() => (busy.val ? "Se generează…" : "Descarcă"),
			),
		),
		t.p(
			{ class: "text-[11px] text-slate-400 dark:text-slate-500" },
			"Formatul și sistemul de coordonate alese aici se aplică și butoanelor „Export” de pe fiecare strat. " +
				"Exporturile mari pot dura până la câteva minute; cel mult două exporturi rulează în același timp.",
		),
		t.div(
			{
				class: () =>
					"rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-900 dark:text-amber-200 flex flex-col gap-0.5 " +
					(warnings.val.length > 0 ? "" : "hidden"),
			},
			t.div({ class: "font-semibold" }, "Fișierul s-a descărcat, cu avertismente:"),
			() => t.div(...warnings.val.map((w) => t.div(`⚠ ${w}`))),
		),
	)
}

function LayersTab(get_map_slug: () => string) {
	return t.div(
		{ class: "flex flex-col gap-4" },

		// export whole map — formate multiple (3.2), atașamente (3.3), MBTiles (3.5). Lotul 2: ascuns
		// când politica primăriei oprește acțiunea `export` pentru rolul contului (refuzul real: 403).
		t.div(
			{
				style: () =>
					session_org_action(store.map_detail?.org_id ?? "", "export") ? "" : "display:none",
			},
			ExportBar(get_map_slug),
		),

		// (re)asignare la organizație + publicare — DOAR pentru cine poate edita (editor+/super).
		t.div(
			{ style: () => (can_edit_map() ? "" : "display:none"), class: "space-y-2" },
			MapOrgRow(get_map_slug),
			MapBrandingRow(get_map_slug),
			MapIssuerSection(get_map_slug),
		),
		// Gating pe contract: fără `portal_extern` pe org-ul hărții, secțiunea de publicare nu apare
		// (refuzul real e pe server — 403 la POST publish) — CU O EXCEPȚIE (O-C-10): harta DEJA
		// publicată. Atunci linkul e mort, iar secțiunea rămâne ca să o spună și să ofere depublicarea
		// (DELETE e permis și fără modul — minimizare, vezi gate_publish). Lotul 2: și acțiunea
		// `partajare` a rolului (publicare + trimitere pe email + embed-uri) — aceeași doctrină.
		t.div(
			{
				style: () =>
					can_edit_map() &&
					session_org_action(store.map_detail?.org_id ?? "", "partajare") &&
					(session_org_feature(store.map_detail?.org_id ?? "", "portal_extern") ||
						publish_dead_link.val)
						? ""
						: "display:none",
			},
			PublishSection(get_map_slug),
		),
		// Catalogul de embed-uri (F7-T3): cere și `interop_ogc` (dreptul contractual al catalogului);
		// refuzul real e pe server (gate_embeds — POST/PATCH 403, revocarea rămâne liberă).
		t.div(
			{
				style: () =>
					can_edit_map() &&
					session_org_action(store.map_detail?.org_id ?? "", "partajare") &&
					session_org_feature(store.map_detail?.org_id ?? "", "portal_extern") &&
					session_org_feature(store.map_detail?.org_id ?? "", "interop_ogc")
						? ""
						: "display:none",
				class: "mt-2",
			},
			EmbedsSection(get_map_slug),
		),
		// „API-uri definibile" (F7-T4) — aceeași grupă contractuală (interop_ogc).
		t.div(
			{
				style: () =>
					can_edit_map() && session_org_feature(store.map_detail?.org_id ?? "", "interop_ogc")
						? ""
						: "display:none",
				class: "mt-2",
			},
			ApiLinksSection(get_map_slug),
		),
		// Fundaluri configurabile (decizia 1.3) — apar și în portalul public (paritate intern ↔ /embed).
		t.div(
			{ style: () => (can_edit_map() ? "" : "display:none"), class: "mt-2" },
			BasemapsSection(get_map_slug),
		),
		t.div(
			{ style: () => (can_edit_map() ? "" : "display:none"), class: "mt-2" },
			ConformitySection(get_map_slug),
		),
		// Verificare topologică pe strat (decizia 4.5) — aceeași poartă de contract ca validatorul 904.
		t.div(
			{ style: () => (can_edit_map() ? "" : "display:none"), class: "mt-2" },
			TopologySection(get_map_slug),
		),

		// loading / error / table
		() => {
			if (store.map_detail_loading) {
				return t.div(
					{
						class:
							"flex items-center gap-2 py-10 justify-center text-slate-400 dark:text-slate-500 text-sm",
					},
					t.span({ class: "spinner" }),
					"Se încarcă...",
				)
			}
			if (store.map_detail_error) {
				return t.div(
					{
						class:
							"rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300",
					},
					`Eroare: ${store.map_detail_error}`,
				)
			}
			const detail = store.map_detail
			if (!detail) {
				return t.div(
					{ class: "py-10 text-center text-slate-400 dark:text-slate-500 text-sm" },
					"Nu s-au încărcat datele.",
				)
			}
			const layers = detail.layers
			if (layers.length === 0) {
				return t.div(
					{ class: "py-10 text-center text-slate-400 dark:text-slate-500 text-sm" },
					"Niciun strat în această hartă.",
				)
			}

			return t.div(
				{ class: "space-y-1.5" },
				OrderHintRow("sus", get_map_slug),
				t.div(
					// overflow-x-auto (nu hidden): cele 6 coloane nu încap pe telefon — tabelul derulează
					// ORIZONTAL în cardul lui, în loc să fie tăiat sau să lățească pagina.
					{ class: "rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto" },
					t.table(
						{ class: "w-full text-sm border-collapse" },
						t.thead(
							t.tr(
								{
									class:
										"bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-700",
								},
								t.th(
									{
										class:
											"text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400",
									},
									"Slug",
								),
								t.th(
									{
										class:
											"text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400",
									},
									"Nume",
								),
								t.th(
									{
										class:
											"text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400",
									},
									"Geometrie",
								),
								t.th(
									{
										class:
											"text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400",
									},
									"Features",
								),
								t.th(
									{
										class:
											"text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400",
									},
									"SRID",
								),
								t.th(
									{
										class:
											"text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400",
									},
									"Acțiuni",
								),
							),
						),
						t.tbody(
							// ⚠️ FĂRĂ wrapper de <div> în <tbody> (HTML invalid): browserul scoate div-urile din
							// tabel și layoutul explodează (celule rupte, butoane tăiate) — rândul de editare
							// inline stă ca AL DOILEA <tr>, deci flatMap.
							...layers.flatMap((layer) => [
								t.tr(
									{
										class:
											"border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition",
									},
									t.td(
										{ class: "px-4 py-3 align-middle" },
										t.code(
											{
												class:
													"font-mono text-xs text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded break-all",
											},
											layer.slug,
										),
									),
									t.td(
										{
											class:
												"px-4 py-3 align-middle text-slate-800 dark:text-slate-200 font-medium",
										},
										layer.name,
									),
									t.td(
										{ class: "px-4 py-3 align-middle" },
										GeomBadge(() => layer.geometry_type),
									),
									t.td(
										{ class: "px-4 py-3 align-middle text-slate-600 dark:text-slate-300" },
										String(layer.feature_count ?? "—"),
									),
									t.td(
										{ class: "px-4 py-3 align-middle text-slate-600 dark:text-slate-300" },
										String(layer.source_srid ?? "3844"),
									),
									t.td(
										{ class: "px-4 py-3 align-middle" },
										t.div(
											{ class: "flex items-center gap-2" },
											// Export per strat în formatul/SRID-ul din bara de sus (fetch + blob → mesaje vizibile).
											t.button(
												{
													type: "button",
													class:
														"inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-cyan-50 dark:bg-cyan-950 border border-cyan-200 " +
														"text-cyan-700 dark:text-cyan-300 text-xs font-semibold hover:bg-cyan-100 transition whitespace-nowrap",
													title:
														"Descarcă stratul în formatul ales în bara „Export hartă completă”",
													onclick: async (e: Event) => {
														const b = e.currentTarget as HTMLButtonElement
														b.disabled = true
														let w: string[] = []
														try {
															w = await download_export(export_url(get_map_slug(), layer.slug))
														} finally {
															b.disabled = false
														}
														if (w.length > 0) {
															show_toast(`Export cu avertismente: ${w.join(" · ")}`, "info", 8000)
														}
													},
												},
												() => `Export ${export_format_label(export_format.val)}`,
											),
											// ORDONARE (18.09): „în față" = deasupra PE HARTĂ = mai jos în listă (panoul de
											// straturi desenează de la primul rând în sus). Săgeata urmează LISTA, eticheta
											// urmează HARTA, iar capetele listei spun care e care.
											() =>
												can_edit_map()
													? t.span(
															{ class: "inline-flex items-center gap-1" },
															t.button(
																{
																	type: "button",
																	class: BTN_SM_SECONDARY + " !px-2",
																	title: "Trimite în spate — se desenează SUB celelalte straturi",
																	onclick: async (e: Event) => {
																		const b = e.currentTarget as HTMLButtonElement
																		b.disabled = true
																		await move_layer_in_order(get_map_slug(), layer.slug, -1)
																		b.disabled = false
																	},
																},
																"↑ În spate",
															),
															t.button(
																{
																	type: "button",
																	class: BTN_SM_SECONDARY + " !px-2",
																	title: "Adu în față — se desenează PESTE celelalte straturi",
																	onclick: async (e: Event) => {
																		const b = e.currentTarget as HTMLButtonElement
																		b.disabled = true
																		await move_layer_in_order(get_map_slug(), layer.slug, 1)
																		b.disabled = false
																	},
																},
																"↓ În față",
															),
														)
													: t.span({ style: "display:none" }),
											() =>
												can_edit_map()
													? t.button(
															{
																type: "button",
																class: () =>
																	store.edit_layer_slug === layer.slug
																		? BTN_SM_SECONDARY + " !bg-slate-200 dark:bg-slate-700"
																		: BTN_SM_SECONDARY,
																onclick: () => {
																	store.edit_layer_slug =
																		store.edit_layer_slug === layer.slug ? null : layer.slug
																},
															},
															() => (store.edit_layer_slug === layer.slug ? "Închide" : "Editare"),
														)
													: t.span({ style: "display:none" }),
										),
									),
								),
								// Inline edit row
								t.tr(
									{
										style: () =>
											`display:${store.edit_layer_slug === layer.slug ? "table-row" : "none"}`,
									},
									t.td(
										{ colspan: "6", class: "p-0" },
										EditLayerPanel(() => {
											const d = store.map_detail
											const found = d?.layers.find((l) => l.slug === layer.slug)
											return found ?? layer
										}, get_map_slug),
									),
								),
							]),
						),
					),
				),
				OrderHintRow("jos", get_map_slug),
			)
		},
	)
}

/**
 * Etichetele de capăt ale listei de straturi (18.09). Panoul NU se inversează — manualele cu capturi
 * sunt deja distribuite — deci primul rând rămâne stratul cel mai de JOS pe hartă, iar butonul
 * „adu în față" mută rândul în JOS. Cele două rânduri de text sunt tot ce face diferența între
 * „coerent" și „contraintuitiv".
 */
function OrderHintRow(where: "sus" | "jos", get_map_slug: () => string) {
	const cls = "flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400"
	if (where === "sus") {
		return t.div({ class: cls }, t.span("↑ primul rând se desenează DEDESUBT pe hartă"))
	}
	return t.div({ class: cls }, t.span("↓ ultimul rând se desenează DEASUPRA pe hartă"), () => {
		const lo = store.map_detail?.attributes?.layer_order
		const has_order = Array.isArray(lo) && lo.length > 0
		if (!has_order || !can_edit_map()) {
			return t.span({ style: "display:none" })
		}
		return t.button(
			{
				type: "button",
				class: "underline hover:text-slate-700 dark:hover:text-slate-200",
				title: "Șterge ordinea configurată — straturile revin la ordinea în care au fost importate",
				onclick: async (e: Event) => {
					const b = e.currentTarget as HTMLButtonElement
					b.disabled = true
					await reset_layer_order(get_map_slug())
					b.disabled = false
				},
			},
			"revino la ordinea implicită",
		)
	})
}

// ─────────────────────────────────────────────
// New Layer Form
// ─────────────────────────────────────────────

function NewLayerForm(get_map_slug: () => string) {
	const schema_err = van.state("")
	const style_err = van.state("")
	const slug_err = van.state("")
	const name_err = van.state("")
	const submitting = van.state(false)

	const inp_slug = t.input({ type: "text", class: INPUT_CLS, placeholder: "ex: zone_functionale" })
	const inp_name = t.input({ type: "text", class: INPUT_CLS, placeholder: "ex: Zone funcționale" })
	const inp_srid = t.input({ type: "text", class: INPUT_CLS, value: "3844", placeholder: "3844" })
	const sel_geom = t.select(
		{ class: SELECT_CLS },
		t.option({ value: "Point" }, "Point"),
		t.option({ value: "LineString" }, "LineString"),
		t.option({ value: "Polygon" }, "Polygon"),
		t.option({ value: "Geometry" }, "Geometry"),
	)
	const inp_desc = t.input({ type: "text", class: INPUT_CLS, placeholder: "Descriere opțională" })
	const ta_schema = t.textarea({
		class: TEXTAREA_CLS,
		placeholder: '{"cod":{"type":"string","required":true},"suprafata":{"type":"number"}}',
		rows: "5",
	})
	const ta_style = t.textarea({
		class: TEXTAREA_CLS,
		placeholder: '{"fill-color":"#3a5bff","fill-opacity":0.5}',
		rows: "4",
	})

	async function on_submit(e: Event) {
		e.preventDefault()
		slug_err.val = name_err.val = schema_err.val = style_err.val = ""

		const slug = inp_slug.value.trim()
		const name = inp_name.value.trim()
		const srid_raw = inp_srid.value.trim()
		const geom_type = sel_geom.value
		const desc = inp_desc.value.trim()

		if (!slug) {
			slug_err.val = "Slug-ul este obligatoriu"
			return
		}
		if (!name) {
			name_err.val = "Numele este obligatoriu"
			return
		}

		const parsed_schema = parse_json_field(ta_schema.value)
		if (parsed_schema.error) {
			schema_err.val = parsed_schema.error
			return
		}

		const parsed_style = parse_json_field(ta_style.value)
		if (parsed_style.error) {
			style_err.val = parsed_style.error
			return
		}

		const source_srid = parseInt(srid_raw, 10) || 3844

		const payload: Record<string, unknown> = { slug, name, geometry_type: geom_type, source_srid }
		if (desc) {
			payload.description = desc
		}
		if (parsed_schema.value !== undefined) {
			payload.attribute_schema = parsed_schema.value
		}
		if (parsed_style.value !== undefined) {
			payload.style = parsed_style.value
		}

		submitting.val = true
		const ok = await create_layer(get_map_slug(), payload as Parameters<typeof create_layer>[1])
		submitting.val = false
		if (ok) {
			inp_slug.value = inp_name.value = inp_desc.value = ""
			ta_schema.value = ta_style.value = ""
			store.active_tab = "layers"
		}
	}

	return t.form(
		{ onsubmit: on_submit, class: "p-5 flex flex-col gap-4" },
		t.div(
			{ class: "grid sm:grid-cols-2 gap-4" },
			Field("Slug", inp_slug, slug_err),
			Field("Nume", inp_name, name_err),
			Field("Tip geometrie", sel_geom),
			Field("SRID sursă", inp_srid),
			t.div({ class: "col-span-2" }, Field("Descriere", inp_desc)),
			t.div({ class: "col-span-2" }, Field("Attribute Schema (JSON)", ta_schema, schema_err)),
			t.div({ class: "col-span-2" }, Field("Stil (JSON)", ta_style, style_err)),
		),
		t.button({ type: "submit", class: BTN_PRIMARY, disabled: () => submitting.val }, () =>
			submitting.val ? "Se creează..." : "Creare strat",
		),
	)
}

// ─────────────────────────────────────────────
// Import Tab
// ─────────────────────────────────────────────

function ImportTab() {
	const file_input = t.input({
		type: "file",
		class:
			INPUT_CLS +
			" file:mr-3 file:px-3 file:py-1 file:rounded-md file:border-0 file:text-xs " +
			"file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100 cursor-pointer",
		// 3.2: pe lângă GPKG/SHP/GeoJSON — CSV, Excel, GML, KML/KMZ și arhive zip (shapefile arhivat).
		accept: ".gpkg,.shp,.zip,.geojson,.json,.csv,.xlsx,.xlsm,.gml,.kml,.kmz",
	})
	// Harta destinație = LISTĂ (harta curentă preselectată, celelalte hărți vizibile, „Hartă nouă"),
	// nu un slug scris de mână (testare 08.09: utilizatorul nu știa ce să scrie și importul crea
	// tăcut o hartă nouă). Opțiunile se re-populează din store.maps / store.selected_map; alegerea
	// explicită a utilizatorului se păstrează cât timp harta mai există.
	const NEW_MAP = "__new__"
	const sel_target = t.select({ class: INPUT_CLS }) as HTMLSelectElement
	let target_touched = false
	sel_target.addEventListener("change", () => {
		target_touched = true
		new_map_chosen.val = sel_target.value === NEW_MAP
	})
	const new_map_chosen = van.state(false)
	van.derive(() => {
		const current = store.selected_map ?? ""
		const maps = store.maps
		const prev = target_touched ? sel_target.value : ""
		// Ordinea: „Hartă nouă" prima (cerere 08.09), apoi harta curentă (PRESELECTATĂ — cazul
		// obișnuit e completarea hărții deschise), apoi celelalte hărți grupate pe primărie
		// (<optgroup>; un super vede toate hărțile — negrupate, lista devenea de necitit).
		const opts: HTMLElement[] = [t.option({ value: NEW_MAP }, "Hartă nouă (cu numele de mai jos)")]
		if (current) {
			const cur = maps.find((m) => m.slug === current)
			opts.push(t.option({ value: current }, `Harta curentă: ${cur?.name ?? current} (${current})`))
		}
		const groups = new Map<string, MapSummary[]>()
		for (const m of maps) {
			if (m.slug !== current) {
				const g = m.org_name || "Fără primărie"
				groups.set(g, [...(groups.get(g) ?? []), m])
			}
		}
		for (const [label, list] of [...groups].sort((a, b) => a[0].localeCompare(b[0], "ro"))) {
			opts.push(
				t.optgroup(
					{ label },
					...list.map((m) => t.option({ value: m.slug }, `${m.name} (${m.slug})`)),
				),
			)
		}
		sel_target.replaceChildren(...opts)
		const values = [...sel_target.options].map((o) => o.value)
		const keep = prev && values.includes(prev)
		sel_target.value = keep ? prev : current || NEW_MAP
		new_map_chosen.val = sel_target.value === NEW_MAP
	})
	// „Hartă nouă": numele hărții (precompletat din numele fișierului, editabil) → identificatorul
	// derivat LIVE (slugify, ca la straturile de colectare). Dacă identificatorul există deja printre
	// hărțile vizibile, importul e blocat cu îndrumare (adaugă la harta existentă din listă) — și
	// serverul refuză (create_only → 409), inclusiv pentru hărțile pe care userul nu le vede.
	const inp_new_name = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "ex. PUG Voiteg",
		maxlength: 120,
	}) as HTMLInputElement
	let new_name_touched = false
	const new_slug = van.state("")
	inp_new_name.addEventListener("input", () => {
		new_name_touched = true
		new_slug.val = slugify(inp_new_name.value)
	})
	file_input.addEventListener("change", () => {
		const f = file_input.files?.[0]
		if (f && !new_name_touched) {
			inp_new_name.value = f.name.replace(/\.[^.]+$/, "")
			new_slug.val = slugify(inp_new_name.value)
		}
	})
	const slug_collision = () => store.maps.find((m) => m.slug === new_slug.val) ?? null
	const new_map_blocked = () =>
		new_map_chosen.val && (new_slug.val === "" || slug_collision() !== null)
	const new_map_fields = t.div(
		{ class: () => (new_map_chosen.val ? "flex flex-col gap-3" : "hidden") },
		t.div(
			{ class: "flex flex-col gap-1" },
			Field("Numele hărții noi", inp_new_name),
			t.span(
				{ class: "text-xs text-slate-400 dark:text-slate-500" },
				"Identificator: ",
				t.code({ class: "text-slate-600 dark:text-slate-300" }, () => new_slug.val || "—"),
			),
			t.span(
				{
					class: () =>
						new_map_chosen.val && slug_collision()
							? "text-xs text-red-600 dark:text-red-400"
							: "hidden",
				},
				() => {
					const c = slug_collision()
					return c
						? `Există deja harta „${c.name}” (${c.slug}). Ca să adaugi straturi la ea, alege-o din listă; altfel schimbă numele.`
						: ""
				},
			),
		),
	)
	const target_field = t.div(
		{ class: "flex flex-col gap-1" },
		Field("Hartă destinație", sel_target),
		t.span(
			{ class: "text-xs text-slate-400 dark:text-slate-500" },
			"Straturile din fișier se adaugă la harta aleasă (un strat cu același nume e completat, vezi „Potrivire la re-import”). " +
				"„Hartă nouă” creează o hartă cu numele pe care îl scrii mai jos (precompletat din numele fișierului), în organizația aleasă.",
		),
	)
	// Sistemul de coordonate al fișierului (3.2, `source_srid`): la GPKG/SHP/GML/KML e detectat
	// per strat (aici doar îl suprascrii); la CSV/Excel cu coloane x/y sau WKT e OBLIGATORIU —
	// fără el serverul sare stratul, cu motivul în rezultat. Pentru coloane lng/lat alege WGS84.
	const srid_mode = van.state<"" | "3844" | "4326" | "3857" | "custom">("")
	const sel_srid = t.select(
		{
			class: INPUT_CLS,
			onchange: (e: Event) => {
				srid_mode.val = (e.target as HTMLSelectElement).value as typeof srid_mode.val
			},
		},
		t.option({ value: "" }, "detectat din fișier (GPKG / SHP / GML / KML)"),
		t.option({ value: "3844" }, "Stereo 70 (EPSG:3844) — coloane x/y sau WKT"),
		t.option({ value: "4326" }, "WGS84 (EPSG:4326) — coloane lng/lat"),
		t.option({ value: "3857" }, "Web Mercator (EPSG:3857)"),
		t.option({ value: "custom" }, "alt cod EPSG…"),
	)
	const inp_srid_custom = t.input({
		type: "number",
		min: "1",
		class: () => INPUT_CLS + " max-w-[160px]" + (srid_mode.val === "custom" ? "" : " hidden"),
		placeholder: "ex. 31700",
	}) as HTMLInputElement
	const srid_field = t.div(
		{ class: "flex flex-col" },
		t.label({ class: LABEL_CLS }, "Sistemul de coordonate al fișierului"),
		t.div({ class: "flex items-center gap-2 flex-wrap" }, sel_srid, inp_srid_custom),
		t.span(
			{ class: "text-xs text-slate-400 dark:text-slate-500 mt-1" },
			"Obligatoriu pentru CSV/Excel cu coloane x/y sau WKT; pentru coloane lng/lat alege WGS84. " +
				"Fiecare foaie Excel devine un strat; CSV: separator detectat, zecimale cu punct.",
		),
	)
	function source_srid_value(): string | undefined {
		if (srid_mode.val === "custom") {
			return inp_srid_custom.value.trim() || undefined
		}
		return srid_mode.val || undefined
	}
	// La RE-IMPORT: cum potrivim features-urile noi cu cele existente, ca să DETECTĂM schimbările
	// (modifică/șterge/adaugă) în loc să dublăm. „fid" = ID-ul intern al fișierului (stabil dacă
	// editezi același fișier); „atribut" = un câmp care identifică unic (ex. cod, nr. cadastral).
	const match_mode = van.state<"none" | "fid" | "attr">("none")
	const match_select = t.select(
		{
			class: INPUT_CLS,
			onchange: (e: Event) => {
				match_mode.val = (e.target as HTMLSelectElement).value as "none" | "fid" | "attr"
			},
		},
		t.option({ value: "none" }, "Fără — adaugă (implicit)"),
		t.option({ value: "fid" }, "După ID-ul intern al fișierului (fid)"),
		t.option({ value: "attr" }, "După un atribut…"),
	)
	const inp_key = t.input({
		type: "text",
		class: INPUT_CLS,
		placeholder: "ex. cod, nr_cadastral",
	})
	// Câmpul de atribut apare doar la modul „după atribut".
	const attr_field = t.div(
		{ style: () => (match_mode.val === "attr" ? "" : "display:none") },
		Field("Atribut-cheie", inp_key),
	)
	// UNIFICARE SCHEMĂ (F5-T8, REQ-URB-002): la re-import cu potrivire, un fișier cu schemă
	// PARȚIAL diferită COMPLETEAZĂ stratul — coloanele absente se păstrează (implicit s-ar șterge).
	const merge_cb = t.input({ type: "checkbox", class: "cursor-pointer" }) as HTMLInputElement
	const merge_field = t.div(
		{ style: () => (match_mode.val !== "none" ? "" : "display:none") },
		t.label(
			{
				class: "flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer",
			},
			merge_cb,
			t.span(
				"Unificare schemă — păstrează coloanele care lipsesc din fișier",
				t.span(
					{ class: "block text-xs text-slate-400 dark:text-slate-500" },
					"pentru fișiere cu alte coloane decât stratul existent; fără bifă, coloanele lipsă se șterg de pe features-urile potrivite",
				),
			),
		),
	)
	// Organizația căreia îi aparține harta NOUĂ (ignorată dacă imporți într-o hartă existentă).
	const orgs = van.state<Array<{ id: string; name: string }>>([])
	const org_select = t.select({ class: INPUT_CLS })
	van.derive(() => {
		org_select.replaceChildren(
			t.option({ value: "" }, "— alege organizația —"),
			...orgs.val.map((o) => t.option({ value: o.id }, o.name)),
		)
	})
	// Reactiv la sesiune: la refresh, `writable_orgs` poate rula ÎNAINTE ca sesiunea să se încarce
	// (super are 0 membershipuri → ar cădea pe ramura goală). Re-încarcă când sesiunea se schimbă.
	van.derive(() => {
		void session.val
		writable_orgs().then((list) => {
			orgs.val = list
		})
	})
	const submitting = van.state(false)

	// dry_run=true → previzualizare (nu scrie); false → import real.
	async function run(dry_run: boolean) {
		const files = file_input.files
		if (!files || files.length === 0) {
			show_toast("Selectați un fișier pentru import", "err")
			return
		}
		const file = files[0]
		const is_new = sel_target.value === NEW_MAP
		const target = is_new ? new_slug.val || undefined : sel_target.value || undefined
		const org_id = org_select.value || undefined
		if (is_new) {
			if (!new_slug.val) {
				show_toast("Scrie numele hărții noi.", "err")
				return
			}
			const c = slug_collision()
			if (c) {
				show_toast(
					`Există deja harta „${c.name}” (${c.slug}) — alege-o din listă sau schimbă numele.`,
					"err",
					7000,
				)
				return
			}
			if (!org_id && session.val.user?.platform_role !== "super") {
				show_toast("Alege organizația pentru harta nouă.", "err")
				return
			}
		}
		const mode = match_mode.val
		const match_fid = mode === "fid"
		const natural_key = mode === "attr" ? inp_key.value.trim() || undefined : undefined
		// Modul „după atribut" fără câmp completat ar degrada tăcut la append → blocăm.
		if (mode === "attr" && !natural_key) {
			show_toast("Completează atributul-cheie sau alege alt mod de potrivire", "err")
			return
		}
		const source_srid = source_srid_value()
		if (srid_mode.val === "custom" && !(source_srid && /^\d+$/.test(source_srid))) {
			show_toast("Scrie un cod EPSG numeric (ex. 31700) sau alege un sistem din listă.", "err")
			return
		}
		// Un tabel (CSV/Excel) fără sistem declarat: serverul îl acceptă doar cu coloane lng/lat —
		// avertizăm din timp, fără să blocăm (fișierul poate avea chiar lng/lat).
		if (!source_srid && /\.(csv|xlsx|xlsm)$/i.test(file.name)) {
			show_toast(
				"Nu ai ales sistemul de coordonate. Dacă fișierul are coloane lng/lat e în regulă; pentru coloane x/y sau WKT alege sistemul, altfel stratul va fi sărit.",
				"info",
				6000,
			)
		}
		submitting.val = true
		await do_import(
			file,
			target,
			org_id,
			natural_key,
			match_fid,
			dry_run,
			mode !== "none" && merge_cb.checked,
			source_srid,
			is_new ? { name: inp_new_name.value.trim() || new_slug.val } : undefined,
		)
		submitting.val = false
		// La preview păstrăm fișierul selectat ca să poată confirma cu „Importă".
		if (!dry_run) {
			file_input.value = ""
		}
	}

	async function on_submit(e: Event) {
		e.preventDefault()
		await run(false)
	}

	const BTN_NEUTRAL =
		"inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm " +
		"font-semibold hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition"

	return t.div(
		{ class: "p-5 flex flex-col gap-4" },
		t.form(
			{ onsubmit: on_submit, class: "flex flex-col gap-4" },
			Field("Fișier (GeoPackage, Shapefile/zip, GeoJSON, CSV, Excel, GML, KML)", file_input),
			srid_field,
			target_field,
			new_map_fields,
			t.div(
				{ class: () => (new_map_chosen.val ? "" : "hidden") },
				Field("Organizația hărții noi", org_select),
			),
			Field("Potrivire la re-import (detecția schimbărilor)", match_select),
			attr_field,
			merge_field,
			t.div(
				{ class: "flex gap-2" },
				t.button(
					{
						type: "button",
						class: BTN_NEUTRAL,
						disabled: () => submitting.val || new_map_blocked(),
						title: "Calculează ce s-ar schimba, fără a scrie nimic",
						onclick: () => run(true),
					},
					() => (submitting.val ? "…" : "Previzualizează"),
				),
				t.button(
					{
						type: "submit",
						class: BTN_PRIMARY,
						disabled: () => submitting.val || new_map_blocked(),
					},
					() => (submitting.val ? "Se importă..." : "Importă"),
				),
			),
		),
		// Rezultat: rezumat lizibil (per layer + sărite) + JSON brut în „Detalii".
		t.div(
			{ style: () => `display:${store.import_result ? "block" : "none"}` },
			() => {
				const r = store.import_result
				const layers = r?.layers ?? []
				const skipped = r?.skipped ?? []
				const is_preview = store.import_is_preview
				return t.div(
					{ class: "flex flex-col gap-1" },
					is_preview
						? t.div(
								{
									class:
										"rounded-lg bg-cyan-50 dark:bg-cyan-950 border border-cyan-200 px-3 py-2 text-xs font-semibold text-cyan-800 dark:text-cyan-300",
								},
								"PREVIZUALIZARE — nimic nu s-a scris. Verifică planul, apoi apasă Importă.",
							)
						: t.span({ style: "display:none" }),
					t.div(
						{
							class:
								"rounded-xl bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 px-4 py-3 text-sm " +
								"text-emerald-900 flex flex-col gap-1",
							style: layers.length === 0 && skipped.length > 0 ? "display:none" : "",
						},
						...layers.map((l) => t.div(import_layer_line(l))),
					),
					...skipped.map((s) =>
						t.div(
							{
								class:
									"rounded-xl bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-900",
							},
							`⚠ ${s.layer}: ${s.reason}`,
						),
					),
				)
			},
			t.details(
				{ class: "mt-2" },
				t.summary(
					{ class: "text-xs text-slate-500 dark:text-slate-400 cursor-pointer select-none" },
					"Detalii (JSON)",
				),
				t.pre(
					{
						class:
							"mt-1 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs font-mono " +
							"text-slate-700 dark:text-slate-200 whitespace-pre-wrap overflow-x-auto",
					},
					() => (store.import_result ? JSON.stringify(store.import_result, null, 2) : ""),
				),
			),
		),
	)
}

/** O linie lizibilă per layer importat: breakdown la re-import cu diff, altfel simplu. */
function import_layer_line(l: ImportLayerSummary): string {
	const diff = (l.updated ?? 0) + (l.deleted ?? 0) + (l.unchanged ?? 0)
	if (diff > 0) {
		return `✓ ${l.slug}: ${l.added ?? 0} noi · ${l.updated ?? 0} modificate · ${l.unchanged ?? 0} neschimbate · ${l.deleted ?? 0} șterse`
	}
	return `✓ ${l.slug}: ${l.features} features importate`
}

// ─────────────────────────────────────────────
// Main Panel
// ─────────────────────────────────────────────

function MainPanel() {
	function tab_btn(tab_id: "layers" | "new_layer" | "import" | "regim_utr", label: string) {
		return t.button(
			{
				type: "button",
				class: () => {
					const base = "shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition "
					return store.active_tab === tab_id
						? base + "bg-cyan-700 text-white shadow-sm"
						: base +
								"text-slate-600 dark:text-slate-300 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800"
				},
				onclick: () => {
					store.active_tab = tab_id
				},
			},
			label,
		)
	}

	return t.main(
		// min-w-0: fără el, flex-1 nu poate coborî sub lățimea min-content a tabelelor →
		// pagina întreagă făcea scroll orizontal pe telefon.
		{ class: "flex-1 min-w-0 p-3 sm:p-6 overflow-y-auto min-h-[calc(100dvh-3rem)]" },

		// no map selected
		t.div(
			{
				class:
					"flex items-center justify-center rounded-xl border border-dashed border-slate-300 dark:border-slate-600 " +
					"bg-white dark:bg-slate-900 min-h-64",
				style: () => `display:${store.selected_map ? "none" : "flex"}`,
			},
			t.div(
				{ class: "text-center" },
				t.div({ class: "text-4xl mb-3" }, "🗺️"),
				t.p(
					{ class: "text-slate-500 dark:text-slate-400 font-medium" },
					"Selectați o hartă din panoul stâng",
				),
				t.p(
					{ class: "text-slate-400 dark:text-slate-500 text-sm mt-1" },
					"sau creați una nouă mai jos",
				),
			),
		),

		// map selected panel
		t.div(
			{
				class: "flex flex-col gap-5",
				style: () => `display:${store.selected_map ? "flex" : "none"}`,
			},

			// map header card
			t.div(
				{
					class:
						"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden",
				},

				// title bar
				t.div(
					{
						class:
							"flex items-start justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800",
					},
					t.div(
						t.h2({ class: "text-lg font-bold text-slate-900 dark:text-slate-100" }, () => {
							const d = store.map_detail
							const slug = store.selected_map ?? ""
							return d ? d.name : slug
						}),
						t.div({ class: "text-sm text-slate-500 dark:text-slate-400 mt-0.5" }, () => {
							const d = store.map_detail
							const desc = d?.config?.description ?? d?.description ?? ""
							return desc
								? `Slug: ${d?.slug ?? ""} — ${desc}`
								: `Slug: ${d?.slug ?? store.selected_map ?? ""}`
						}),
					),
				),

				// tab pills — „Strat nou" + „Import" sunt scriere → doar pentru editor+/super.
				t.div(
					{
						class:
							// overflow-x-auto: cele 4 pastile (~400px) derulează pe telefon în loc să lățească pagina
							"flex items-center gap-2 px-3 sm:px-5 py-3 overflow-x-auto bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-700",
					},
					tab_btn("layers", "Straturi"),
					() =>
						can_edit_map() ? tab_btn("new_layer", "Strat nou") : t.span({ style: "display:none" }),
					// Lotul 2: tabul de import dispare când politica primăriei oprește acțiunea `import` rolului.
					() =>
						can_edit_map() && session_org_action(store.map_detail?.org_id ?? "", "import")
							? tab_btn("import", "Import")
							: t.span({ style: "display:none" }),
					() =>
						can_edit_map() ? tab_btn("regim_utr", "Regim UTR") : t.span({ style: "display:none" }),
					() =>
						can_edit_map()
							? t.span({ style: "display:none" })
							: t.span(
									{
										class:
											"text-xs text-slate-400 dark:text-slate-500 ml-1 inline-flex items-center gap-1",
									},
									"· doar vizualizare",
								),
				),

				// tab content — always rendered, toggled via display
				t.div(
					{
						class: "p-5",
						style: () => `display:${store.active_tab === "layers" ? "block" : "none"}`,
					},
					LayersTab(() => store.selected_map ?? ""),
				),
				t.div(
					{
						style: () => `display:${store.active_tab === "new_layer" ? "block" : "none"}`,
					},
					NewLayerForm(() => store.selected_map ?? ""),
				),
				t.div(
					{
						style: () =>
							`display:${
								store.active_tab === "import" &&
								session_org_action(store.map_detail?.org_id ?? "", "import")
									? "block"
									: "none"
							}`,
					},
					ImportTab(),
				),
				t.div(
					{
						style: () => `display:${store.active_tab === "regim_utr" ? "block" : "none"}`,
					},
					// Tot regulamentul într-un loc: indicatori + fișe RLU, apoi nomenclatorul legislativ
					// (REQ-URB-004 — „legile care modifică regimurile aprobate prin RLU").
					t.div(
						{ class: "flex flex-col gap-5" },
						RegimUtrTab(() => store.selected_map ?? ""),
						ExtravilanSection(() => store.selected_map ?? ""),
						LegislatieSection(() => store.selected_map ?? ""),
					),
				),
			),
		),
	)
}

// ─────────────────────────────────────────────
// App Shell
// ─────────────────────────────────────────────

export function AdminPage() {
	const el = t.div(
		{
			class:
				"min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200 antialiased",
		},
		t.div(
			{ class: "flex flex-col lg:flex-row max-w-screen-xl mx-auto" },
			MapsSidebar(),
			MainPanel(),
		),
	)
	// Încarcă datele după ce shell-ul atașează elementul în DOM.
	queueMicrotask(() => load_maps())
	return el
}
